import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { apiUrl } from '../config/api';
import { formatDateDMY } from '../utils/dateUtils';
import { getCompanyConfigCached } from '../utils/invoiceUtils';
import LoanLink from '../components/LoanLink';
import RepaymentLink from '../components/RepaymentLink';
import { toCurrency } from '../utils/adminMetrics';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import Pager from '../components/UI/Pager';
import { cardClass } from '../components/UI/dashboardCard';

const monthOptions = [
  { value: '', label: 'Select Month' },
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const loanRef = (row) => row?.loan_code || '';

// A date shown as dd/mm/yyyy, or the placeholder when there is none.
const dmy = (v, empty = '—') => (v ? formatDateDMY(v) : empty);
// A 'YYYY-MM-DD' (or ISO date-time) value in a CSV cell, as dd/mm/yyyy.
const csvCell = (v) => (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}(?:[T ][\d:.]+(?:Z|[+-]\d{2}:?\d{2})?)?$/.test(v) ? formatDateDMY(v) : v);

const paginateArray = (items, page, pageSize) => {
  const safeItems = Array.isArray(items) ? items : [];
  const safePageSize = Math.max(1, Number(pageSize) || 25);
  const totalItems = safeItems.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / safePageSize));
  const safePage = clamp(Number(page) || 1, 1, totalPages);
  const start = (safePage - 1) * safePageSize;
  const end = start + safePageSize;

  return {
    page: safePage,
    pageSize: safePageSize,
    totalItems,
    totalPages,
    items: safeItems.slice(start, end),
  };
};

const EMPTY_REPAYMENTS = {
  filters: { category: null, month: null, year: null, startDate: null, endDate: null },
  totals: { count: 0, total_paid: 0 },
  byMonth: [],
  items: [],
};

const EMPTY_REPAYMENT_METHODS = {
  debit: EMPTY_REPAYMENTS,
  cash: EMPTY_REPAYMENTS,
  other: EMPTY_REPAYMENTS,
};

// Shape of a month's derived figures (sent by the API) before any arrive.
const EMPTY_MM_DERIVED = {
  provisions: { specific: 0, general: 0, total_impairment: 0 },
  loan_book: { gross: 0, net: 0 },
  new_loans: { secured: { count: 0, amount: 0 }, unsecured: { count: 0, amount: 0 } },
  income: {
    interest_income: 0, fees_income: 0, default_interest: 0, bad_debts_recovered: 0, other_income: 0,
    operating_expenses: 0, impairment_charge: 0,
    total_income: 0, total_expenses: 0, net_profit: 0,
  },
  capital: { paid_up: 0, retained: 0, total_equity: 0, required_minimum: 0, surplus_shortfall: 0 },
};

// A gender x range pivot from report rows. Genders are case-normalised
// (null/empty -> 'unknown'); rows of the same gender and range are added
// together; a range label not in the API's list goes to an 'Other' column,
// which is shown only when such rows exist.
const OTHER_RANGE_LABEL = 'Other';
const normaliseGender = (g) => {
  const s = String(g ?? '').trim().toLowerCase();
  return s || 'unknown';
};
const buildGenderRangeGrid = (report, rangeField, valueField) => {
  const ranges = Array.isArray(report?.ranges) ? report.ranges : [];
  const rows = Array.isArray(report?.rows) ? report.rows : [];
  const known = ranges.map((r) => String(r.label));
  const rangeOf = (r) => {
    const label = String(r?.[rangeField] ?? '');
    return known.includes(label) ? label : OTHER_RANGE_LABEL;
  };
  const needsOther = !known.includes(OTHER_RANGE_LABEL) && rows.some((r) => rangeOf(r) === OTHER_RANGE_LABEL);
  const columns = needsOther ? [...known, OTHER_RANGE_LABEL] : known;

  const preferredOrder = ['male', 'female', 'other', 'unknown'];
  const seen = Array.from(new Set(rows.map((r) => normaliseGender(r.gender))));
  const genders = preferredOrder
    .filter((g) => seen.includes(g))
    .concat(seen.filter((g) => !preferredOrder.includes(g)).sort());

  const byGender = genders.reduce((acc, g) => {
    acc[g] = {
      gender: g,
      cells: columns.reduce((cAcc, col) => ({ ...cAcc, [col]: { count: 0, [valueField]: 0 } }), {}),
    };
    return acc;
  }, {});

  for (const r of rows) {
    const cell = byGender[normaliseGender(r.gender)].cells[rangeOf(r)];
    cell.count += Number(r.count || 0);
    cell[valueField] += Number(r[valueField] || 0);
  }

  return { columns, tableRows: genders.map((g) => byGender[g]) };
};

const TableScroll = ({ children }) => (
  <div
    className="w-full max-w-full min-w-0 overflow-visible"
  >
    {children}
  </div>
);

// Monthly Management Report house style (screen). Kept at module level so the
// inputs inside sections are never remounted (and never lose focus) on render.
const MM_TH = 'px-3 py-2 text-xs font-semibold uppercase tracking-wide text-white bg-blue-900 dark:bg-blue-950';
const MM_TR = 'border-b border-gray-100 dark:border-slate-700 odd:bg-white even:bg-slate-50 dark:odd:bg-slate-800 dark:even:bg-slate-900/40';
const MM_TR_TOTAL = 'border-t-2 border-blue-800 dark:border-blue-400 bg-blue-50 dark:bg-blue-950/40 font-semibold';
const MM_TD = 'px-3 py-2 text-gray-700 dark:text-slate-200';
const MM_TD_NUM = 'px-3 py-2 text-right tabular-nums whitespace-nowrap text-gray-800 dark:text-slate-100';

// "1. Executive summary — July 2026" with a navy accent bar.
const MmSectionHeading = ({ n, title, period, right }) => (
  <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-md border-l-4 border-blue-800 dark:border-blue-400 bg-indigo-50/70 dark:bg-slate-900/50 px-3 py-2">
    <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-200">
      {n ? <span className="mr-1 tabular-nums">{n}.</span> : null}
      {title}
      {period ? <span className="font-normal text-blue-900/70 dark:text-blue-200/70"> — {period}</span> : null}
    </h4>
    {right || null}
  </div>
);

// Navy band that opens each month / the quarter summary, with its period.
const MmPeriodBanner = ({ title, start, end }) => (
  <div className="rounded-lg bg-gradient-to-r from-blue-900 to-blue-700 dark:from-blue-950 dark:to-blue-800 px-4 py-3 text-white shadow-sm flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
    <h3 className="text-lg font-bold tracking-tight">{title}</h3>
    {(start || end) ? <div className="text-xs text-blue-100">Period: {start} – {end}</div> : null}
  </div>
);

// Label / value table in the report style. rows: [{ label, value, total }]
const MmKvTable = ({ rows, valueHeader = 'Value' }) => (
  <TableScroll>
    <table className="w-full text-sm border-collapse overflow-hidden rounded-md">
      <thead>
        <tr>
          <th className={`${MM_TH} text-left`}>Item</th>
          <th className={`${MM_TH} text-right`}>{valueHeader}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} className={r.total ? MM_TR_TOTAL : MM_TR}>
            <td className={MM_TD}><span className="inline-flex items-center">{r.label}</span></td>
            <td className={MM_TD_NUM}>{r.value}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </TableScroll>
);

const MicrolenderMonthlyManagement = () => {
  const HelpTooltip = ({ text }) => {
    if (!text) return null;
    return (
      <span className="relative inline-flex align-middle ml-1 group">
        <button
          type="button"
          aria-label="Help"
          className="inline-flex items-center justify-center w-4 h-4 rounded-full border border-gray-300 dark:border-slate-600 text-gray-600 dark:text-slate-400 text-[10px] leading-none bg-white dark:bg-slate-700 hover:bg-gray-50 dark:hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-200"
        >
          ?
        </button>
        <div
          role="tooltip"
          className="pointer-events-none absolute left-1/2 top-full mt-2 w-64 -translate-x-1/2 rounded-md border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-2 py-2 text-xs text-gray-700 dark:text-slate-300 shadow-md opacity-0 transition-opacity duration-150 group-hover:opacity-100 group-focus-within:opacity-100 z-50"
        >
          {text}
        </div>
      </span>
    );
  };

  const SourceTag = ({ source }) => {
    if (!source) return null;
    return (
      <span className="ml-2 inline-flex items-center rounded border border-gray-200 dark:border-slate-600 bg-gray-50 dark:bg-slate-700 px-1.5 py-0.5 text-[10px] font-semibold text-gray-700 dark:text-slate-300">
        {source}
      </span>
    );
  };

  const MM_TOOLTIPS = useMemo(() => ({
    majorRiskIssues: 'Summarize the key credit, liquidity, operational, and compliance risks observed during the month, plus any actions taken or planned.',
    securedLoans: 'Enter the secured portion of NEW loans issued for this month (both amount and count). Unsecured is calculated as: New loans issued minus Secured loans.',
    otherMovements: 'Balancing line so both columns tie out: Opening + New − Loans settled − Written off − this = Closing (applied to amount and to loan count). Write-offs are now their own line. The amount captures partial repayments on still-open loans, interest, expenses and penalty added to balances (New counts principal only) and capture adjustments; the count is normally 0. A positive value is a net reduction.',
    writtenOff: 'Loans written off in the month, each at the gross balance it had on the day it was written off. A written-off loan leaves the loan book (money received afterwards is Bad debts recovered).',
    interestIncome: "Interest part of each repayment received in the month (split in the loan's proportion of interest to everything repayable, penalty included); payments after a write-off are Bad debts recovered.",
    feesIncome: 'Fees charged in the month on paid-out loans, excluding the NAMFISA levy and stamp duty, which are paid over.',
    defaultInterest: 'Penalty interest charged in the month (each penalty month by its charge date, GN 263/2018).',
    badDebtsRecovered: 'Money received in the month on loans already written off (it carries no interest and is not a loan-book repayment).',
    otherIncome: 'Any other operating income for the month (N$) not included in interest or fees.',
    operatingExpenses: 'Total operating expenses for the month (N$), excluding bad debt expense (auto-calculated separately).',
    impairmentCharge: 'Closing impairment − opening impairment + loans written off in the month. Impairment = loans 90+ days past due plus the general provision.',

    cashAndBankBalances: 'Month-end cash on hand and bank balances (N$). Required for every month, and cannot be N$0.00: enter a negative amount for an overdraft. The report asks for it before it is generated or downloaded.',
    loanBookNetOfProvisions: 'Calculated as: Closing loan book (system) minus (Specific provisions (AUTO) + General provision (MANUAL)) for the month.',
    otherAssets: 'Month-end other assets balance (N$) not included above.',
    totalLiabilities: 'Month-end total liabilities balance (N$).',
    shareholderEquity: 'Month-end shareholder equity balance (N$).',

    paidUpShareCapital: 'Paid-up share capital at month-end (N$).',
    retainedEarnings: 'Retained earnings at month-end (N$).',
    requiredMinimumCapital: 'Regulatory required minimum capital amount (N$).',

    cashOnHand: 'Cash on hand at month-end (N$).',
    monthlyExpenses: 'Average/total monthly expenses used for liquidity monitoring (N$).',
    shortTermLiabilities: 'Short-term liabilities at month-end (N$).',

    specificProvisions: 'Calculated automatically from month-end 90+ days arrears outstanding (NPL 90+) (N$).',
    generalProvision: 'General provision balance at month-end (N$).',

    complaintsReceived: 'Auto-counted from the Consumer Protection & Compliance register: complaints reported during the month. Log cases under "Compliance Cases".',
    complaintsResolved: 'Auto-counted from the register: complaints resolved/closed during the month (by resolved date).',
    courtCases: 'Auto-counted from the register: court cases reported during the month.',
    regulatoryBreaches: 'Auto-counted from the register: regulatory breaches reported during the month.',
    overchargingIssues: 'Auto-counted from the register: overcharging issues reported during the month.',

    managementCommentary: 'Provide any narrative commentary explaining performance, key events, trends, and management actions for the month.',

    licenceNumber: 'Set your NAMFISA microlender licence / registration number in Company Configuration. This report will pull it automatically.',
    preparedBy: 'Enter the name and role of the person who prepared this report (e.g., Risk Officer / Finance Manager).',
    approvedBy: 'Enter the name and role of the person who approved this report (e.g., CEO / Managing Director).',
    quarterCommentary: 'Provide a quarter-end / QTD narrative summary: key drivers, trends across the 3 months, and notable events.',
    quarterActions: 'List key actions taken/planned during the quarter to address risks, arrears/NPLs, liquidity, compliance issues, and operational concerns.',
  }), []);

  const reportOptions = [
    { value: 'disbursements', label: 'Loan Disbursements' },
    { value: 'total_loans_disbursed', label: 'Total number of loans disbursed during the period' },
    { value: 'loans_per_collection_method', label: 'Number of Loans per collection method' },
    { value: 'loans_by_purpose', label: 'Number of loans by purpose' },
    { value: 'disbursements_gender_ranges', label: 'Disbursed loans by gender (amount ranges)' },
    { value: 'borrower_salaries_by_gender', label: 'Borrower salaries by gender' },
    { value: 'number_of_clients', label: 'Total number of clients in book at last day of reporting period (Active Clients)' },
    { value: 'loans_outstanding_numbers', label: 'Number of loans outstanding' },
    { value: 'disbursements_duration_breakdown', label: 'Loan disbursement breakdown (1–5 months)' },
    { value: 'overdue', label: 'Overdue Loans (Penalty Interest)' },
    { value: 'arrears_breakdown', label: 'Loans in arrears (all buckets)' },
    { value: 'defaultInterest', label: 'Default Interest' },
    { value: 'unpaid', label: 'Unpaid Loans' },
    { value: 'repayment_methods', label: 'Repayment Methods' },
    { value: 'provision_bad_debts_movement', label: 'Movement in Provision for Bad Debts' },
    { value: 'interest_outstanding_quarter', label: 'Interest Charged on loans outstanding (end of quarter)' },
    { value: 'interest', label: 'Interest on Loans and Advances - Micro lenders' },
    { value: 'microlender_monthly_management', label: 'Microlender Monthly Management Report' },
  ];

  const [filters, setFilters] = useState({ month: '', year: '', startDate: '', endDate: '' });
  const [query, setQuery] = useState({ month: '', year: '', startDate: '', endDate: '' });
  const [selectedReport, setSelectedReport] = useState('microlender_monthly_management');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState({ totals: { count: 0, total_amount: 0 }, paidOff: { count: 0, total_amount: 0 }, byGender: [], byReason: [], byMonth: [] });
  const [totalLoansDisbursed, setTotalLoansDisbursed] = useState({ totals: { count: 0 }, filters: { month: null, year: null, quarter: null, startDate: null, endDate: null, dateField: 'start_date' } });
  const [totalLoansDisbursedError, setTotalLoansDisbursedError] = useState('');
  const [totalLoansDisbursedLoans, setTotalLoansDisbursedLoans] = useState([]);
  const [totalLoansDisbursedLoansError, setTotalLoansDisbursedLoansError] = useState('');
  const [loansPerCollectionMethod, setLoansPerCollectionMethod] = useState({ totals: { count: 0 }, rows: [], filters: { month: null, year: null, quarter: null, startDate: null, endDate: null, dateField: 'start_date' } });
  const [loansPerCollectionMethodError, setLoansPerCollectionMethodError] = useState('');
  const [loansPerCollectionMethodLoans, setLoansPerCollectionMethodLoans] = useState([]);
  const [loansPerCollectionMethodLoansError, setLoansPerCollectionMethodLoansError] = useState('');
  const [loansByPurpose, setLoansByPurpose] = useState({ rows: [], filters: { month: null, year: null, quarter: null, startDate: null, endDate: null, dateField: 'start_date' } });
  const [loansByPurposeError, setLoansByPurposeError] = useState('');
  const [loansByPurposeLoans, setLoansByPurposeLoans] = useState([]);
  const [loansByPurposeLoansError, setLoansByPurposeLoansError] = useState('');
  const [disbGenderRanges, setDisbGenderRanges] = useState({ ranges: [], rows: [], filters: { month: null, year: null, startDate: null, endDate: null } });
  const [disbGenderRangesError, setDisbGenderRangesError] = useState('');
  const [disbGenderRangesLoans, setDisbGenderRangesLoans] = useState([]);
  const [borrowerSalariesByGender, setBorrowerSalariesByGender] = useState({ ranges: [], rows: [], filters: { month: null, year: null, startDate: null, endDate: null } });
  const [borrowerSalariesByGenderError, setBorrowerSalariesByGenderError] = useState('');
  const [borrowerSalariesByGenderBorrowers, setBorrowerSalariesByGenderBorrowers] = useState([]);
  const [borrowerSalariesByGenderBorrowersError, setBorrowerSalariesByGenderBorrowersError] = useState('');
  const [numberOfClients, setNumberOfClients] = useState({ totals: { active_clients: 0, loan_count: 0, total_outstanding: 0 }, loans: [], filters: { month: null, year: null, endDate: null } });
  const [numberOfClientsError, setNumberOfClientsError] = useState('');
  const [loansOutstandingNumbers, setLoansOutstandingNumbers] = useState({
    current: { count: 0, total_outstanding: 0 },
    arrears: { count: 0, total_outstanding: 0 },
    currentLoans: [],
    arrearsLoans: [],
    filters: { month: null, year: null, endDate: null },
  });
  const [loansOutstandingNumbersError, setLoansOutstandingNumbersError] = useState('');
  const [overdue, setOverdue] = useState({ totals: { count: 0, total_penalty_interest: 0, total_amount_due: 0 }, loans: [] });
  const [overdueError, setOverdueError] = useState('');
  const [arrearsBreakdown, setArrearsBreakdown] = useState({
    asOfDate: null,
    buckets: {
      '30_60': { totals: { count: 0, total_outstanding: 0 }, loans: [] },
      '60_90': { totals: { count: 0, total_outstanding: 0 }, loans: [] },
      '90_120': { totals: { count: 0, total_outstanding: 0 }, loans: [] },
      'gt_120': { totals: { count: 0, total_outstanding: 0 }, loans: [] },
    }
  });
  const [arrearsBreakdownError, setArrearsBreakdownError] = useState('');
  const [unpaid, setUnpaid] = useState({ totals: { count: 0, total_outstanding: 0 }, loans: [] });
  const [unpaidError, setUnpaidError] = useState('');
  const [interest, setInterest] = useState({
    totals: { count: 0, total_principal: 0, total_interest: 0, total_repayable: 0 },
    byMonth: [],
    loans: [],
    filters: { month: null, year: null, quarter: null, startDate: null, endDate: null },
  });
  const [interestError, setInterestError] = useState('');
  const [defaultInterest, setDefaultInterest] = useState({ totals: { count: 0, total_default_interest: 0, total_amount_due: 0 }, byMonth: [], items: [] });
  const [defaultInterestError, setDefaultInterestError] = useState('');
  const [disbDurationBreakdown, setDisbDurationBreakdown] = useState({ rows: [] });
  const [disbDurationBreakdownError, setDisbDurationBreakdownError] = useState('');
  const [disbDurationBreakdownLoans, setDisbDurationBreakdownLoans] = useState([]);
  const [disbDurationBreakdownLoansError, setDisbDurationBreakdownLoansError] = useState('');
  const [repaymentMethods, setRepaymentMethods] = useState(EMPTY_REPAYMENT_METHODS);
  const [repaymentsError, setRepaymentsError] = useState('');
  const [provisionRatePct, setProvisionRatePct] = useState('0');
  const [provision, setProvision] = useState({ totals: { count: 0, total_outstanding_at_start: 0, total_provision: 0 }, loans: [], filters: { startDate: null, provisionRate: 0 } });
  const [provisionError, setProvisionError] = useState('');
  const [provisionMovement, setProvisionMovement] = useState({
    movement: {
      opening_provision: 0,
      loans_written_off: 0,
      provision_during_period: 0,
      closing_provision: 0,
      opening_outstanding: 0,
      closing_outstanding: 0,
      written_off_count: 0,
    },
    writtenOffLoans: [],
    filters: { startDate: null, endDate: null, provisionRate: 0 },
  });
  const [provisionMovementError, setProvisionMovementError] = useState('');
  const [quarter, setQuarter] = useState('');
  const [quarterYear, setQuarterYear] = useState('');
  const [interestQuarter, setInterestQuarter] = useState({ totals: { count: 0, total_outstanding_at_end: 0, total_interest_charged: 0 }, loans: [], filters: { endDate: null, year: null, quarter: null } });
  const [interestQuarterError, setInterestQuarterError] = useState('');

  const [monthlyManagement, setMonthlyManagement] = useState(null);
  const [monthlyManagementError, setMonthlyManagementError] = useState('');
  const [monthlyManagementLoading, setMonthlyManagementLoading] = useState(false);

  const [mmYear, setMmYear] = useState('');
  const [mmQuarter, setMmQuarter] = useState('');

  const EMPTY_MM_INPUTS = useMemo(() => ({
    majorRiskIssues: '',

    interestIncome: '',
    feesIncome: '',
    otherIncome: '',
    operatingExpenses: '',
    badDebtExpense: '',

    cashAndBankBalances: '',
    loanBookNetOfProvisions: '',
    otherAssets: '',
    totalLiabilities: '',
    shareholderEquity: '',

    paidUpShareCapital: '',
    retainedEarnings: '',
    requiredMinimumCapital: '',

    cashOnHand: '',
    monthlyExpenses: '',
    shortTermLiabilities: '',

    specificProvisions: '',
    generalProvision: '',

    complaintsReceived: '',
    complaintsResolved: '',
    courtCases: '',
    regulatoryBreaches: '',
    overchargingIssues: '',

    managementCommentary: '',

    securedLoansCount: '0',
    securedLoansAmount: '0',
    unsecuredLoansCount: '',
    unsecuredLoansAmount: '',
  }), []);

  const EMPTY_MM_QUARTER_INPUTS = useMemo(() => ({
    preparedBy: '',
    approvedBy: '',
    quarterCommentary: '',
    quarterActions: '',
  }), []);

  const [monthlyManagementInputsByMonth, setMonthlyManagementInputsByMonth] = useState({});
  const [monthlyManagementInputsByQuarter, setMonthlyManagementInputsByQuarter] = useState({});

  // Live mirrors of the input maps so rapid successive edits compose correctly
  // (setState is async) and the debounced saver always reads the latest object.
  const monthlyInputsRef = useRef(monthlyManagementInputsByMonth);
  const quarterlyInputsRef = useRef(monthlyManagementInputsByQuarter);
  useEffect(() => { monthlyInputsRef.current = monthlyManagementInputsByMonth; }, [monthlyManagementInputsByMonth]);
  useEffect(() => { quarterlyInputsRef.current = monthlyManagementInputsByQuarter; }, [monthlyManagementInputsByQuarter]);

  // Debounced persistence of manual inputs to the DB. One timer per period key.
  const mmSaveTimers = useRef({});
  const [mmSaveState, setMmSaveState] = useState('idle'); // idle | saving | saved | error

  const mmKey = (y, m) => {
    const yy = String(Number(y) || '').padStart(4, '0');
    const mm = String(Number(m) || '').padStart(2, '0');
    if (!yy.trim() || !mm.trim()) return '';
    return `${yy}-${mm}`;
  };

  const mmQuarterKey = (y, q) => {
    const yy = String(Number(y) || '').padStart(4, '0');
    const qq = Number(q);
    if (!yy.trim() || !qq || qq < 1 || qq > 4) return '';
    return `${yy}-Q${qq}`;
  };

  const getMmInputs = (key) => {
    if (!key) return EMPTY_MM_INPUTS;
    return monthlyManagementInputsByMonth?.[key] || EMPTY_MM_INPUTS;
  };

  const getMmQuarterInputs = (key) => {
    if (!key) return EMPTY_MM_QUARTER_INPUTS;
    return monthlyManagementInputsByQuarter?.[key] || EMPTY_MM_QUARTER_INPUTS;
  };

  // Debounced upsert of one month's inputs. key = "YYYY-MM".
  const persistMmMonth = useCallback((key, dataObj) => {
    const parts = String(key).split('-');
    const year = Number(parts[0]);
    const month = Number(parts[1]);
    if (!year || !month) return;
    const tk = `m:${key}`;
    if (mmSaveTimers.current[tk]) clearTimeout(mmSaveTimers.current[tk]);
    setMmSaveState('saving');
    mmSaveTimers.current[tk] = setTimeout(async () => {
      try {
        const token = localStorage.getItem('token');
        await axios.put(
          apiUrl('/api/reports/mm-inputs/monthly'),
          { year, month, data: dataObj },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setMmSaveState('saved');
      } catch (e) {
        setMmSaveState('error');
      }
    }, 800);
  }, []);

  // Debounced upsert of one quarter's inputs. key = "YYYY-Qn".
  const persistMmQuarter = useCallback((key, dataObj) => {
    const m = String(key).match(/^(\d+)-Q(\d)$/);
    if (!m) return;
    const year = Number(m[1]);
    const quarter = Number(m[2]);
    if (!year || !quarter) return;
    const tk = `q:${key}`;
    if (mmSaveTimers.current[tk]) clearTimeout(mmSaveTimers.current[tk]);
    setMmSaveState('saving');
    mmSaveTimers.current[tk] = setTimeout(async () => {
      try {
        const token = localStorage.getItem('token');
        await axios.put(
          apiUrl('/api/reports/mm-inputs/quarterly'),
          { year, quarter, data: dataObj },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setMmSaveState('saved');
      } catch (e) {
        setMmSaveState('error');
      }
    }, 800);
  }, []);

  // The derived figures of one month (provisions, net loan book, unsecured
  // loans, net profit, capital) come from the API: from the report itself, then
  // from POST /api/reports/mm-derived as that month's manual inputs are edited.
  const [mmDerivedByMonth, setMmDerivedByMonth] = useState({});
  const [mmDerivedError, setMmDerivedError] = useState('');
  const mmDerivedTimers = useRef({});
  const mmDerivedSeq = useRef({});

  const getMmDerived = (key, mr) => mmDerivedByMonth?.[key] || mr?.derived || EMPTY_MM_DERIVED;

  const refreshMmDerived = (key, inputs) => {
    const months = Array.isArray(monthlyManagement?.months) ? monthlyManagement.months : [];
    const index = months.findIndex((m) => mmKey(m?.period?.year, m?.period?.month) === key);
    const month = months[index];
    if (!month) return;
    // The opening impairment uses the previous month's general provision: the
    // one being typed for a month of this quarter, else the report's own.
    const prev = index > 0 ? months[index - 1] : null;
    const prevInputs = prev ? monthlyInputsRef.current?.[mmKey(prev?.period?.year, prev?.period?.month)] : null;
    const withOpening = prevInputs ? { ...inputs, openingGeneralProvision: prevInputs.generalProvision ?? '' } : inputs;
    if (mmDerivedTimers.current[key]) clearTimeout(mmDerivedTimers.current[key]);
    mmDerivedTimers.current[key] = setTimeout(async () => {
      const seq = (mmDerivedSeq.current[key] || 0) + 1;
      mmDerivedSeq.current[key] = seq;
      try {
        const token = localStorage.getItem('token');
        const res = await axios.post(
          apiUrl('/api/reports/mm-derived'),
          { month, inputs: withOpening },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (mmDerivedSeq.current[key] !== seq) return;
        setMmDerivedByMonth((prev) => ({ ...prev, [key]: res.data }));
        setMmDerivedError('');
      } catch (err) {
        if (mmDerivedSeq.current[key] !== seq) return;
        setMmDerivedError(err?.response?.data?.error || err?.message || 'Could not work out the report figures.');
      }
    }, 400);
  };

  const setMmInput = (key, field, value) => {
    if (!key) return;
    const nextForKey = { ...(monthlyInputsRef.current?.[key] || EMPTY_MM_INPUTS), [field]: value };
    monthlyInputsRef.current = { ...(monthlyInputsRef.current || {}), [key]: nextForKey };
    setMonthlyManagementInputsByMonth((prev) => ({ ...prev, [key]: nextForKey }));
    persistMmMonth(key, nextForKey);
    refreshMmDerived(key, nextForKey);
  };

  const setMmQuarterInput = (key, field, value) => {
    if (!key) return;
    const nextForKey = { ...(quarterlyInputsRef.current?.[key] || EMPTY_MM_QUARTER_INPUTS), [field]: value };
    quarterlyInputsRef.current = { ...(quarterlyInputsRef.current || {}), [key]: nextForKey };
    setMonthlyManagementInputsByQuarter((prev) => ({ ...prev, [key]: nextForKey }));
    persistMmQuarter(key, nextForKey);
  };

  // "Prepared by": what staff entered, else the principal officer from
  // Company Configuration.
  const mmPreparedBy = (qInputs) => String(qInputs?.preparedBy || '').trim()
    || String(monthlyManagement?.company?.principalOfficerName || '').trim();

  // "Quarter 3 · July – September 2026"
  const mmQuarterTitle = (year, quarter) => {
    const q = Number(quarter);
    if (!year || !q || q < 1 || q > 4) return '';
    const first = monthOptions.find((o) => Number(o.value) === (q - 1) * 3 + 1)?.label || '';
    const last = monthOptions.find((o) => Number(o.value) === q * 3)?.label || '';
    return `Quarter ${q} · ${first} – ${last} ${year}`;
  };

  // The company logo as a PNG data URI (jsPDF cannot draw SVG/WEBP), with its
  // size so the PDF cover keeps the aspect ratio.
  const [mmLogo, setMmLogo] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const cfg = await getCompanyConfigCached();
      const src = cfg?.logo_base64;
      if (!src || cancelled) return;
      const img = new Image();
      img.onload = () => {
        if (cancelled) return;
        try {
          const w = img.naturalWidth || img.width || 0;
          const h = img.naturalHeight || img.height || 0;
          if (!w || !h) return;
          const scale = Math.min(1, 600 / Math.max(w, h));
          const canvas = document.createElement('canvas');
          canvas.width = Math.round(w * scale);
          canvas.height = Math.round(h * scale);
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          setMmLogo({ src, png: canvas.toDataURL('image/png'), w: canvas.width, h: canvas.height });
        } catch (_) {
          setMmLogo({ src, png: null, w: 0, h: 0 });
        }
      };
      img.onerror = () => {};
      img.src = src;
    })();
    return () => { cancelled = true; };
  }, []);

  // The report's written sections, drafted from the quarter's figures in the
  // Principal Officer's voice: the quarter commentary and actions /
  // remediation, and each month's management commentary and major risk
  // issues. They are written automatically the first time a quarter loads with
  // any of them empty (filling only the empty ones), saved through the same
  // setters as typing, and staff can edit or regenerate them afterwards.
  //  - mmAiInFlight: one request per quarter at a time (guards double calls).
  //  - mmAutoGenDone: a quarter is auto-checked once per session, so clearing
  //    a section on purpose never brings a draft back by itself.
  //  - mmAiPromises: the running request, so the PDF export can wait for it.
  const [mmAiBusyKey, setMmAiBusyKey] = useState('');
  const [mmAiError, setMmAiError] = useState({ key: '', message: '' });
  const [mmInputsReadyKey, setMmInputsReadyKey] = useState('');
  const [mmPdfWaiting, setMmPdfWaiting] = useState(false);
  const mmAiInFlight = useRef({});
  const mmAutoGenDone = useRef({});
  const mmAiPromises = useRef({});
  const MM_AI_QUARTER_FIELDS = ['quarterCommentary', 'quarterActions'];
  const MM_AI_MONTH_FIELDS = ['managementCommentary', 'majorRiskIssues'];
  const mmQuarterMonthKeys = (year, quarter) => [0, 1, 2].map((i) => mmKey(year, (Number(quarter) - 1) * 3 + 1 + i));
  const mmFilled = (v) => Boolean(String(v || '').trim());
  // Whether any written section of the quarter is filled / empty.
  const mmAiSections = (qk, year, quarter) => {
    const q = quarterlyInputsRef.current?.[qk] || {};
    const values = [
      ...MM_AI_QUARTER_FIELDS.map((f) => q[f]),
      ...mmQuarterMonthKeys(year, quarter).flatMap((k) => MM_AI_MONTH_FIELDS.map((f) => monthlyInputsRef.current?.[k]?.[f])),
    ];
    return { anyFilled: values.some(mmFilled), anyEmpty: values.some((v) => !mmFilled(v)) };
  };
  const generateMmCommentary = async (qk, year, quarter, { auto = false } = {}) => {
    if (!qk || mmAiInFlight.current[qk]) return;
    const sections = mmAiSections(qk, year, quarter);
    if (auto && !sections.anyEmpty) return;
    if (!auto && sections.anyFilled && !window.confirm("Write the commentary, actions / remediation, and each month's management commentary and major risk issues again? This replaces what is there now.")) return;
    mmAiInFlight.current[qk] = true;
    setMmAiBusyKey(qk);
    setMmAiError({ key: '', message: '' });
    const run = (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.post(
          apiUrl('/api/reports/mm-commentary'),
          { year: Number(year), quarter: Number(quarter) },
          { headers: { Authorization: `Bearer ${token}` }, timeout: 120000 },
        );
        const text = String(res.data?.commentary || '').trim();
        if (!text) throw new Error('The commentary could not be written. Please try again.');
        // Automatic drafts fill only what is still empty when they arrive, so
        // nothing typed in the meantime is lost.
        const fill = (current, value, set) => {
          const v = String(value || '').trim();
          if (v && (!auto || !mmFilled(current))) set(v);
        };
        const q = quarterlyInputsRef.current?.[qk] || {};
        fill(q.quarterCommentary, text, (v) => setMmQuarterInput(qk, 'quarterCommentary', v));
        fill(q.quarterActions, res.data?.actions, (v) => setMmQuarterInput(qk, 'quarterActions', v));
        const months = res.data?.months || {};
        mmQuarterMonthKeys(year, quarter).forEach((k) => {
          const m = monthlyInputsRef.current?.[k] || {};
          fill(m.managementCommentary, months[k]?.commentary, (v) => setMmInput(k, 'managementCommentary', v));
          fill(m.majorRiskIssues, months[k]?.major_risks, (v) => setMmInput(k, 'majorRiskIssues', v));
        });
      } catch (err) {
        setMmAiError({ key: qk, message: err?.response?.data?.error || err?.message || 'The commentary could not be written. Please try again.' });
      } finally {
        delete mmAiInFlight.current[qk];
        delete mmAiPromises.current[qk];
        setMmAiBusyKey((prev) => (prev === qk ? '' : prev));
      }
    })();
    mmAiPromises.current[qk] = run;
    await run;
  };

  // Once a quarter's report and its saved inputs have loaded: write the
  // sections automatically when any is empty and this quarter has not been
  // auto-checked in this session yet.
  useEffect(() => {
    const qk = mmInputsReadyKey;
    if (!qk || mmAutoGenDone.current[qk]) return;
    mmAutoGenDone.current[qk] = true;
    const m = qk.match(/^(\d+)-Q(\d)$/);
    if (!m) return;
    generateMmCommentary(qk, m[1], m[2], { auto: true });
  }, [mmInputsReadyKey]);

  // The cash and bank balance of each month must be captured (and not
  // N$0.00) before the report is generated or downloaded. The API decides
  // (GET/PUT /api/reports/mm-cash-balances, and a 422 CASH_BALANCE_REQUIRED
  // from the report); this asks for the missing months and then carries on.
  const [mmCashPrompt, setMmCashPrompt] = useState(null);
  const mmCashThen = useRef(null);
  const generateRef = useRef(() => {});
  const openCashPrompt = (cash, then) => {
    mmCashThen.current = then;
    setMmCashPrompt({
      year: cash.year,
      quarter: cash.quarter,
      months: cash.months || [],
      values: Object.fromEntries((cash.months || []).map((m) => [m.key, m.ok ? String(m.value) : (Number(m.value) === 0 ? '' : String(m.value ?? ''))])),
      errors: Object.fromEntries((cash.months || []).filter((m) => !m.ok && String(m.value ?? '').trim() !== '').map((m) => [m.key, m.message])),
      error: '',
      saving: false,
    });
  };
  // true when every balance is captured; otherwise asks for them, then runs `retry`.
  const ensureCashBalances = async (year, quarter, retry) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(apiUrl('/api/reports/mm-cash-balances'), {
        params: { year, quarter },
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data?.complete) return true;
      openCashPrompt(res.data, retry);
    } catch (err) {
      setMonthlyManagementError(err?.response?.data?.error || 'The cash and bank balances could not be checked. Please try again.');
    }
    return false;
  };
  const saveCashPrompt = async () => {
    const p = mmCashPrompt;
    if (!p) return;
    setMmCashPrompt({ ...p, saving: true, error: '' });
    try {
      const token = localStorage.getItem('token');
      const res = await axios.put(
        apiUrl('/api/reports/mm-cash-balances'),
        { year: p.year, quarter: p.quarter, balances: p.values },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      // Months already on screen show the saved balance (and save it with the
      // rest of their inputs); months not loaded yet are read from the API.
      (res.data?.months || []).forEach((m) => {
        if (m.ok && monthlyInputsRef.current?.[m.key]) setMmInput(m.key, 'cashAndBankBalances', String(m.value));
      });
      if (!res.data?.complete) {
        openCashPrompt(res.data, mmCashThen.current);
        return;
      }
      setMmCashPrompt(null);
      const then = mmCashThen.current;
      mmCashThen.current = null;
      if (then) then();
    } catch (err) {
      setMmCashPrompt({
        ...p,
        saving: false,
        errors: err?.response?.data?.errors || {},
        error: err?.response?.data?.error || 'The balances could not be saved. Please try again.',
      });
    }
  };

  // The PDF waits for a commentary that is still being written.
  const exportPdfRef = useRef(() => {});
  const exportMmPdf = async () => {
    if (monthlyManagement?.year && monthlyManagement?.quarter
      && !(await ensureCashBalances(monthlyManagement.year, monthlyManagement.quarter, () => exportMmPdf()))) return;
    const qk = mmQuarterKey(monthlyManagement?.year, monthlyManagement?.quarter);
    const pending = qk ? mmAiPromises.current[qk] : null;
    if (pending) {
      setMmPdfWaiting(true);
      try { await pending; } catch (_) { /* use what is there */ } finally { setMmPdfWaiting(false); }
    }
    exportPdfRef.current();
  };

  // Auto-refresh reports when any filter changes.
  // Keep a short debounce so date inputs don't spam requests.
  const didMountRef = useRef(false);
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }

    const t = setTimeout(() => {
      setQuery({ ...filters });
    }, 150);

    return () => clearTimeout(t);
  }, [filters]);

  const [pageSize, setPageSize] = useState(10);
  const [pages, setPages] = useState({
    disbGender: 1,
    disbReason: 1,
    disbMonth: 1,
    overdue: 1,
    arrears_30_60: 1,
    arrears_60_90: 1,
    arrears_90_120: 1,
    arrears_gt_120: 1,
    defaultInterestByMonth: 1,
    defaultInterestItems: 1,
    unpaid: 1,
    repaymentsDebitItems: 1,
    repaymentsCashItems: 1,
    repaymentsOtherItems: 1,
    provision: 1,
    provisionMovementWrittenOff: 1,
    interestQuarter: 1,
    interestByMonth: 1,
    interestLoans: 1,
    disbGenderRangesLoans: 1,
    disbGenderRangesPivot: 1,
    totalLoansDisbursedLoans: 1,
    totalLoansDisbursedSummary: 1,
    disbDurationBreakdownLoans1: 1,
    disbDurationBreakdownLoans2: 1,
    disbDurationBreakdownLoans3: 1,
    disbDurationBreakdownLoans4: 1,
    disbDurationBreakdownLoans5: 1,
    numberOfClientsLoans: 1,
    loansOutstandingCurrent: 1,
    loansOutstandingArrears: 1,
    loansPerCollectionMethodLoans: 1,
    loansPerCollectionMethodSummary: 1,
    loansPerCollectionMethodRows: 1,
    loansByPurposeLoans: 1,
    loansByPurposeSummary: 1,
    loansByPurposeRows: 1,
    borrowerSalariesByGenderBorrowers: 1,
    borrowerSalariesByGenderPivot: 1,
  });

  useEffect(() => {
    // Reset pagination whenever report selection or filters change.
    setPages((prev) => Object.keys(prev).reduce((acc, key) => ({ ...acc, [key]: 1 }), {}));
  }, [selectedReport, query]);

  useEffect(() => {
    // If the UI no longer offers these legacy options, force to the new combined report.
    if (['disbursements_1m', 'disbursements_2m', 'disbursements_3m', 'disbursements_4m', 'disbursements_5m'].includes(selectedReport)) {
      setSelectedReport('disbursements_duration_breakdown');
    }
  }, [selectedReport]);

  useEffect(() => {
    // If the UI no longer offers these legacy arrears options, force to the combined arrears report.
    if (['arrears_30_60', 'arrears_60_90', 'arrears_90_120', 'arrears_gt_120'].includes(selectedReport)) {
      setSelectedReport('arrears_breakdown');
    }
  }, [selectedReport]);

  const years = useMemo(() => {
    const current = new Date().getFullYear();
    const arr = [];
    for (let y = current; y >= current - 6; y -= 1) arr.push(y);
    return arr;
  }, []);

  const totalLoansDisbursedSummaryRows = useMemo(() => (
    [
      { metric: 'Start Date', value: dmy(totalLoansDisbursed.filters?.startDate, '—') },
      { metric: 'End Date', value: dmy(totalLoansDisbursed.filters?.endDate, '—') },
      { metric: 'Total Loans Disbursed', value: totalLoansDisbursed.totals?.count ?? 0, bold: true },
    ]
  ), [totalLoansDisbursed.filters?.startDate, totalLoansDisbursed.filters?.endDate, totalLoansDisbursed.totals?.count]);

  const loansPerCollectionMethodSummaryRows = useMemo(() => (
    [
      { metric: 'Start Date', value: dmy(loansPerCollectionMethod.filters?.startDate, '—') },
      { metric: 'End Date', value: dmy(loansPerCollectionMethod.filters?.endDate, '—') },
      { metric: 'Total Loans', value: loansPerCollectionMethod.totals?.count ?? 0, bold: true },
    ]
  ), [loansPerCollectionMethod.filters?.startDate, loansPerCollectionMethod.filters?.endDate, loansPerCollectionMethod.totals?.count]);

  // Row, column and grand totals of a grid, worked out by the API.
  // rows: [{ key, values: { [column]: number } }]
  const fetchGridTotals = useCallback(async (rows, columns) => {
    const token = localStorage.getItem('token');
    const res = await axios.post(
      apiUrl('/api/reports/grid-totals'),
      { rows, columns },
      { headers: { Authorization: `Bearer ${token}` } },
    );
    return res.data;
  }, []);

  const [loansByPurposeTotalLoans, setLoansByPurposeTotalLoans] = useState(0);
  useEffect(() => {
    const rows = Array.isArray(loansByPurpose.rows) ? loansByPurpose.rows : [];
    if (!rows.length) { setLoansByPurposeTotalLoans(0); return undefined; }
    let cancelled = false;
    fetchGridTotals(rows.map((r, i) => ({ key: `${i}`, values: { loan_count: r.loan_count } })), ['loan_count'])
      .then((t) => { if (!cancelled) setLoansByPurposeTotalLoans(Number(t?.grand_total || 0)); })
      .catch((err) => { if (!cancelled) setLoansByPurposeError(err?.response?.data?.error || err?.message || 'Failed to total the loans by purpose'); });
    return () => { cancelled = true; };
  }, [loansByPurpose.rows, fetchGridTotals]);

  const loansByPurposeSummaryRows = useMemo(() => (
    [
      { metric: 'Start Date', value: dmy(loansByPurpose.filters?.startDate, '—') },
      { metric: 'End Date', value: dmy(loansByPurpose.filters?.endDate, '—') },
      { metric: 'Total Loans', value: loansByPurposeTotalLoans, bold: true },
    ]
  ), [loansByPurpose.filters?.startDate, loansByPurpose.filters?.endDate, loansByPurposeTotalLoans]);

  const disbGenderPage = useMemo(() => paginateArray(data.byGender, pages.disbGender, pageSize), [data.byGender, pages.disbGender, pageSize]);
  const disbReasonPage = useMemo(() => paginateArray(data.byReason, pages.disbReason, pageSize), [data.byReason, pages.disbReason, pageSize]);
  const disbMonthPage = useMemo(() => paginateArray(data.byMonth, pages.disbMonth, pageSize), [data.byMonth, pages.disbMonth, pageSize]);

  const overduePage = useMemo(() => paginateArray(overdue.loans, pages.overdue, pageSize), [overdue.loans, pages.overdue, pageSize]);
  const arrearsBreakdownPages = useMemo(() => {
    const buckets = arrearsBreakdown?.buckets || {};
    const make = (bucketKey, pageKey) => paginateArray(buckets?.[bucketKey]?.loans || [], pages?.[pageKey] || 1, pageSize);
    return {
      '30_60': make('30_60', 'arrears_30_60'),
      '60_90': make('60_90', 'arrears_60_90'),
      '90_120': make('90_120', 'arrears_90_120'),
      'gt_120': make('gt_120', 'arrears_gt_120'),
    };
  }, [arrearsBreakdown, pages, pageSize]);
  const defaultInterestByMonthPage = useMemo(() => paginateArray(defaultInterest.byMonth, pages.defaultInterestByMonth, pageSize), [defaultInterest.byMonth, pages.defaultInterestByMonth, pageSize]);
  const defaultInterestItemsPage = useMemo(() => paginateArray(defaultInterest.items, pages.defaultInterestItems, pageSize), [defaultInterest.items, pages.defaultInterestItems, pageSize]);
  const unpaidPage = useMemo(() => paginateArray(unpaid.loans, pages.unpaid, pageSize), [unpaid.loans, pages.unpaid, pageSize]);
  const repaymentsDebitItemsPage = useMemo(() => paginateArray(repaymentMethods.debit.items, pages.repaymentsDebitItems, pageSize), [repaymentMethods.debit.items, pages.repaymentsDebitItems, pageSize]);
  const repaymentsCashItemsPage = useMemo(() => paginateArray(repaymentMethods.cash.items, pages.repaymentsCashItems, pageSize), [repaymentMethods.cash.items, pages.repaymentsCashItems, pageSize]);
  const repaymentsOtherItemsPage = useMemo(() => paginateArray(repaymentMethods.other.items, pages.repaymentsOtherItems, pageSize), [repaymentMethods.other.items, pages.repaymentsOtherItems, pageSize]);
  const provisionPage = useMemo(() => paginateArray(provision.loans, pages.provision, pageSize), [provision.loans, pages.provision, pageSize]);
  const provisionMovementWrittenOffPage = useMemo(
    () => paginateArray(provisionMovement.writtenOffLoans, pages.provisionMovementWrittenOff, pageSize),
    [provisionMovement.writtenOffLoans, pages.provisionMovementWrittenOff, pageSize]
  );
  const interestQuarterPage = useMemo(() => paginateArray(interestQuarter.loans, pages.interestQuarter, pageSize), [interestQuarter.loans, pages.interestQuarter, pageSize]);
  const interestByMonthPage = useMemo(() => paginateArray(interest.byMonth, pages.interestByMonth, pageSize), [interest.byMonth, pages.interestByMonth, pageSize]);
  const interestLoansPage = useMemo(() => paginateArray(interest.loans, pages.interestLoans, pageSize), [interest.loans, pages.interestLoans, pageSize]);
  const disbGenderRangesLoansPage = useMemo(() => paginateArray(disbGenderRangesLoans, pages.disbGenderRangesLoans, pageSize), [disbGenderRangesLoans, pages.disbGenderRangesLoans, pageSize]);

  const totalLoansDisbursedLoansPage = useMemo(() => paginateArray(totalLoansDisbursedLoans, pages.totalLoansDisbursedLoans, pageSize), [totalLoansDisbursedLoans, pages.totalLoansDisbursedLoans, pageSize]);
  const totalLoansDisbursedSummaryPage = useMemo(
    () => paginateArray(totalLoansDisbursedSummaryRows, pages.totalLoansDisbursedSummary, pageSize),
    [totalLoansDisbursedSummaryRows, pages.totalLoansDisbursedSummary, pageSize],
  );

  const loansPerCollectionMethodLoansPage = useMemo(() => paginateArray(loansPerCollectionMethodLoans, pages.loansPerCollectionMethodLoans, pageSize), [loansPerCollectionMethodLoans, pages.loansPerCollectionMethodLoans, pageSize]);
  const loansPerCollectionMethodSummaryPage = useMemo(
    () => paginateArray(loansPerCollectionMethodSummaryRows, pages.loansPerCollectionMethodSummary, pageSize),
    [loansPerCollectionMethodSummaryRows, pages.loansPerCollectionMethodSummary, pageSize],
  );
  const loansPerCollectionMethodRowsPage = useMemo(
    () => paginateArray(Array.isArray(loansPerCollectionMethod.rows) ? loansPerCollectionMethod.rows : [], pages.loansPerCollectionMethodRows, pageSize),
    [loansPerCollectionMethod.rows, pages.loansPerCollectionMethodRows, pageSize],
  );

  const loansByPurposeLoansPage = useMemo(() => paginateArray(loansByPurposeLoans, pages.loansByPurposeLoans, pageSize), [loansByPurposeLoans, pages.loansByPurposeLoans, pageSize]);
  const loansByPurposeSummaryPage = useMemo(
    () => paginateArray(loansByPurposeSummaryRows, pages.loansByPurposeSummary, pageSize),
    [loansByPurposeSummaryRows, pages.loansByPurposeSummary, pageSize],
  );
  const loansByPurposeRowsPage = useMemo(
    () => paginateArray(Array.isArray(loansByPurpose.rows) ? loansByPurpose.rows : [], pages.loansByPurposeRows, pageSize),
    [loansByPurpose.rows, pages.loansByPurposeRows, pageSize],
  );

  const borrowerSalariesByGenderBorrowersPage = useMemo(() => paginateArray(borrowerSalariesByGenderBorrowers, pages.borrowerSalariesByGenderBorrowers, pageSize), [borrowerSalariesByGenderBorrowers, pages.borrowerSalariesByGenderBorrowers, pageSize]);
  const numberOfClientsLoansPage = useMemo(() => paginateArray(numberOfClients.loans, pages.numberOfClientsLoans, pageSize), [numberOfClients.loans, pages.numberOfClientsLoans, pageSize]);
  const loansOutstandingCurrentPage = useMemo(() => paginateArray(loansOutstandingNumbers.currentLoans, pages.loansOutstandingCurrent, pageSize), [loansOutstandingNumbers.currentLoans, pages.loansOutstandingCurrent, pageSize]);
  const loansOutstandingArrearsPage = useMemo(() => paginateArray(loansOutstandingNumbers.arrearsLoans, pages.loansOutstandingArrears, pageSize), [loansOutstandingNumbers.arrearsLoans, pages.loansOutstandingArrears, pageSize]);
  const disbDurationBreakdownLoansByDuration = useMemo(() => {
    const loans = Array.isArray(disbDurationBreakdownLoans) ? disbDurationBreakdownLoans : [];
    const get = (durationMonths) => {
      // 6 = 6 months or longer (the API's duration_bucket).
      const filtered = loans.filter((r) => Number(r?.duration_bucket ?? r?.duration_months) === Number(durationMonths));
      const pageKey = `disbDurationBreakdownLoans${durationMonths}`;
      return paginateArray(filtered, pages?.[pageKey] || 1, pageSize);
    };

    return {
      1: get(1),
      2: get(2),
      3: get(3),
      4: get(4),
      5: get(5),
      6: get(6),
    };
  }, [disbDurationBreakdownLoans, pages, pageSize]);

  const fetchReports = useCallback(async (activeFilters) => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (activeFilters.month) params.month = activeFilters.month;
      if (activeFilters.year) params.year = activeFilters.year;
      if (activeFilters.startDate) params.startDate = activeFilters.startDate;
      if (activeFilters.endDate) params.endDate = activeFilters.endDate;
      const token = localStorage.getItem('token');
      const res = await axios.get(apiUrl('/api/reports/disbursements'), {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      setData({
        totals: res.data?.totals || { count: 0, total_amount: 0 },
        paidOff: res.data?.paidOff || { count: 0, total_amount: 0 },
        byGender: res.data?.byGender || [],
        byReason: res.data?.byReason || [],
        byMonth: res.data?.byMonth || [],
      });
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to load reports';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchTotalLoansDisbursed = useCallback(async (activeFilters) => {
    setTotalLoansDisbursedError('');
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = {};
      if (activeFilters.month) params.month = activeFilters.month;
      if (activeFilters.year) params.year = activeFilters.year;
      if (activeFilters.startDate) params.startDate = activeFilters.startDate;
      if (activeFilters.endDate) params.endDate = activeFilters.endDate;
      params.dateField = 'start_date';

      const res = await axios.get(apiUrl('/api/reports/total-loans-disbursed'), {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });

      setTotalLoansDisbursed({
        totals: res.data?.totals || { count: 0 },
        filters: res.data?.filters || { month: null, year: null, quarter: null, startDate: null, endDate: null, dateField: 'start_date' },
      });
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to load total loans disbursed report';
      setTotalLoansDisbursedError(msg);
      setTotalLoansDisbursed({ totals: { count: 0 }, filters: { month: null, year: null, quarter: null, startDate: null, endDate: null, dateField: 'start_date' } });
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchTotalLoansDisbursedLoans = useCallback(async (activeFilters) => {
    setTotalLoansDisbursedLoansError('');
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = {};
      if (activeFilters.month) params.month = activeFilters.month;
      if (activeFilters.year) params.year = activeFilters.year;
      if (activeFilters.startDate) params.startDate = activeFilters.startDate;
      if (activeFilters.endDate) params.endDate = activeFilters.endDate;
      params.dateField = 'start_date';

      const res = await axios.get(apiUrl('/api/reports/total-loans-disbursed-loans'), {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });

      setTotalLoansDisbursedLoans(Array.isArray(res.data?.loans) ? res.data.loans : []);
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to load total loans disbursed loans';
      setTotalLoansDisbursedLoansError(msg);
      setTotalLoansDisbursedLoans([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchLoansPerCollectionMethod = useCallback(async (activeFilters) => {
    setLoansPerCollectionMethodError('');
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = {};
      if (activeFilters.month) params.month = activeFilters.month;
      if (activeFilters.year) params.year = activeFilters.year;
      if (activeFilters.startDate) params.startDate = activeFilters.startDate;
      if (activeFilters.endDate) params.endDate = activeFilters.endDate;

      const res = await axios.get(apiUrl('/api/reports/loans-per-collection-method'), {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });

      setLoansPerCollectionMethod({
        totals: res.data?.totals || { count: 0 },
        rows: res.data?.rows || [],
        filters: res.data?.filters || { month: null, year: null, quarter: null, startDate: null, endDate: null, dateField: 'start_date' },
      });
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to load loans per collection method report';
      setLoansPerCollectionMethodError(msg);
      setLoansPerCollectionMethod({ totals: { count: 0 }, rows: [], filters: { month: null, year: null, quarter: null, startDate: null, endDate: null, dateField: 'start_date' } });
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchLoansPerCollectionMethodLoans = useCallback(async (activeFilters) => {
    setLoansPerCollectionMethodLoansError('');
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = {};
      if (activeFilters.month) params.month = activeFilters.month;
      if (activeFilters.year) params.year = activeFilters.year;
      if (activeFilters.startDate) params.startDate = activeFilters.startDate;
      if (activeFilters.endDate) params.endDate = activeFilters.endDate;

      const res = await axios.get(apiUrl('/api/reports/loans-per-collection-method-loans'), {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });

      setLoansPerCollectionMethodLoans(Array.isArray(res.data?.loans) ? res.data.loans : []);
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to load loans per collection method loans';
      setLoansPerCollectionMethodLoansError(msg);
      setLoansPerCollectionMethodLoans([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchLoansByPurpose = useCallback(async (activeFilters) => {
    setLoansByPurposeError('');
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = {};
      if (activeFilters.month) params.month = activeFilters.month;
      if (activeFilters.year) params.year = activeFilters.year;
      if (activeFilters.startDate) params.startDate = activeFilters.startDate;
      if (activeFilters.endDate) params.endDate = activeFilters.endDate;
      params.dateField = 'start_date';

      const res = await axios.get(apiUrl('/api/reports/loans-by-purpose'), {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });

      setLoansByPurpose({
        rows: res.data?.rows || [],
        filters: res.data?.filters || { month: null, year: null, quarter: null, startDate: null, endDate: null, dateField: 'start_date' },
      });
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to load loans by purpose report';
      setLoansByPurposeError(msg);
      setLoansByPurpose({ rows: [], filters: { month: null, year: null, quarter: null, startDate: null, endDate: null, dateField: 'start_date' } });
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchLoansByPurposeLoans = useCallback(async (activeFilters) => {
    setLoansByPurposeLoansError('');
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = {};
      if (activeFilters.month) params.month = activeFilters.month;
      if (activeFilters.year) params.year = activeFilters.year;
      if (activeFilters.startDate) params.startDate = activeFilters.startDate;
      if (activeFilters.endDate) params.endDate = activeFilters.endDate;
      params.dateField = 'start_date';

      const res = await axios.get(apiUrl('/api/reports/loans-by-purpose-loans'), {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });

      setLoansByPurposeLoans(Array.isArray(res.data?.loans) ? res.data.loans : []);
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to load loans by purpose loans';
      setLoansByPurposeLoansError(msg);
      setLoansByPurposeLoans([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchDisbursedByGenderRanges = useCallback(async (activeFilters) => {
    setDisbGenderRangesError('');
    setLoading(true);
    try {
      const params = {};
      if (activeFilters.month) params.month = activeFilters.month;
      if (activeFilters.year) params.year = activeFilters.year;
      if (activeFilters.startDate) params.startDate = activeFilters.startDate;
      if (activeFilters.endDate) params.endDate = activeFilters.endDate;
      const token = localStorage.getItem('token');
      const res = await axios.get(apiUrl('/api/reports/disbursements-gender-ranges'), {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      setDisbGenderRanges({
        ranges: res.data?.ranges || [],
        rows: res.data?.rows || [],
        filters: res.data?.filters || { month: null, year: null, startDate: null, endDate: null },
      });
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to load disbursed loans by gender ranges report';
      setDisbGenderRangesError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchBorrowerSalariesByGender = useCallback(async (activeFilters) => {
    setBorrowerSalariesByGenderError('');
    setLoading(true);
    try {
      const params = {};
      if (activeFilters.month) params.month = activeFilters.month;
      if (activeFilters.year) params.year = activeFilters.year;
      if (activeFilters.startDate) params.startDate = activeFilters.startDate;
      if (activeFilters.endDate) params.endDate = activeFilters.endDate;
      const token = localStorage.getItem('token');
      const res = await axios.get(apiUrl('/api/reports/borrower-salaries-by-gender'), {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      setBorrowerSalariesByGender({
        ranges: res.data?.ranges || [],
        rows: res.data?.rows || [],
        filters: res.data?.filters || { month: null, year: null, startDate: null, endDate: null },
      });
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to load borrower salaries by gender report';
      setBorrowerSalariesByGenderError(msg);
      setBorrowerSalariesByGender({ ranges: [], rows: [], filters: { month: null, year: null, startDate: null, endDate: null } });
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchBorrowerSalariesByGenderBorrowers = useCallback(async (activeFilters) => {
    setBorrowerSalariesByGenderBorrowersError('');
    setLoading(true);
    try {
      const params = {};
      if (activeFilters.month) params.month = activeFilters.month;
      if (activeFilters.year) params.year = activeFilters.year;
      if (activeFilters.startDate) params.startDate = activeFilters.startDate;
      if (activeFilters.endDate) params.endDate = activeFilters.endDate;
      const token = localStorage.getItem('token');

      const res = await axios.get(apiUrl('/api/reports/borrower-salaries-by-gender-borrowers'), {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });

      setBorrowerSalariesByGenderBorrowers(Array.isArray(res.data?.borrowers) ? res.data.borrowers : []);
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to load borrower salaries by gender borrowers';
      setBorrowerSalariesByGenderBorrowersError(msg);
      setBorrowerSalariesByGenderBorrowers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchNumberOfClients = useCallback(async (activeFilters) => {
    setNumberOfClientsError('');
    setLoading(true);
    try {
      const params = {};
      if (activeFilters.month) params.month = activeFilters.month;
      if (activeFilters.year) params.year = activeFilters.year;
      if (activeFilters.startDate) params.startDate = activeFilters.startDate;
      if (activeFilters.endDate) params.endDate = activeFilters.endDate;
      const token = localStorage.getItem('token');
      const res = await axios.get(apiUrl('/api/reports/clients-active-in-book'), {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      setNumberOfClients({
        totals: res.data?.totals || { active_clients: 0, loan_count: 0, total_outstanding: 0 },
        loans: Array.isArray(res.data?.loans) ? res.data.loans : [],
        filters: res.data?.filters || { month: null, year: null, endDate: null },
      });
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to load number of clients report';
      setNumberOfClientsError(msg);
      setNumberOfClients({ totals: { active_clients: 0, loan_count: 0, total_outstanding: 0 }, loans: [], filters: { month: null, year: null, endDate: null } });
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchLoansOutstandingNumbers = useCallback(async (activeFilters) => {
    setLoansOutstandingNumbersError('');
    setLoading(true);
    try {
      const params = {};
      if (activeFilters.month) params.month = activeFilters.month;
      if (activeFilters.year) params.year = activeFilters.year;
      if (activeFilters.startDate) params.startDate = activeFilters.startDate;
      if (activeFilters.endDate) params.endDate = activeFilters.endDate;
      const token = localStorage.getItem('token');
      const res = await axios.get(apiUrl('/api/reports/loans-outstanding-summary'), {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      setLoansOutstandingNumbers({
        current: res.data?.current || { count: 0, total_outstanding: 0 },
        arrears: res.data?.arrears || { count: 0, total_outstanding: 0 },
        currentLoans: Array.isArray(res.data?.currentLoans) ? res.data.currentLoans : [],
        arrearsLoans: Array.isArray(res.data?.arrearsLoans) ? res.data.arrearsLoans : [],
        filters: res.data?.filters || { month: null, year: null, endDate: null },
      });
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to load loans outstanding report';
      setLoansOutstandingNumbersError(msg);
      setLoansOutstandingNumbers({ current: { count: 0, total_outstanding: 0 }, arrears: { count: 0, total_outstanding: 0 }, currentLoans: [], arrearsLoans: [], filters: { month: null, year: null, endDate: null } });
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchDisbursedGenderRangesLoans = useCallback(async (activeFilters) => {
    setDisbGenderRangesError('');
    setLoading(true);
    try {
      const params = {};
      if (activeFilters.month) params.month = activeFilters.month;
      if (activeFilters.year) params.year = activeFilters.year;
      if (activeFilters.startDate) params.startDate = activeFilters.startDate;
      if (activeFilters.endDate) params.endDate = activeFilters.endDate;
      const token = localStorage.getItem('token');
      const res = await axios.get(apiUrl('/api/reports/disbursements-gender-ranges-loans'), {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      setDisbGenderRangesLoans(Array.isArray(res.data?.loans) ? res.data.loans : []);
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to load loans for gender ranges report';
      setDisbGenderRangesError(msg);
      setDisbGenderRangesLoans([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchDisbursementsDurationBreakdown = useCallback(async (activeFilters) => {
    setDisbDurationBreakdownError('');
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = {};
      if (activeFilters.month) params.month = activeFilters.month;
      if (activeFilters.year) params.year = activeFilters.year;
      if (activeFilters.startDate) params.startDate = activeFilters.startDate;
      if (activeFilters.endDate) params.endDate = activeFilters.endDate;
      const res = await axios.get(apiUrl('/api/reports/disbursements-duration-breakdown'), {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      setDisbDurationBreakdown({ rows: Array.isArray(res.data?.rows) ? res.data.rows : [] });
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to load disbursement duration breakdown';
      setDisbDurationBreakdownError(msg);
      setDisbDurationBreakdown({ rows: [] });
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchDisbursementsDurationBreakdownLoans = useCallback(async (activeFilters) => {
    setDisbDurationBreakdownLoansError('');
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = {};
      if (activeFilters.month) params.month = activeFilters.month;
      if (activeFilters.year) params.year = activeFilters.year;
      if (activeFilters.startDate) params.startDate = activeFilters.startDate;
      if (activeFilters.endDate) params.endDate = activeFilters.endDate;
      const res = await axios.get(apiUrl('/api/reports/disbursements-duration-breakdown-loans'), {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      setDisbDurationBreakdownLoans(Array.isArray(res.data?.loans) ? res.data.loans : []);
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to load loans for disbursement duration breakdown';
      setDisbDurationBreakdownLoansError(msg);
      setDisbDurationBreakdownLoans([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchArrearsBreakdown = useCallback(async (activeFilters) => {
    setArrearsBreakdownError('');
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const baseParams = {};
      if (activeFilters.month) baseParams.month = activeFilters.month;
      if (activeFilters.year) baseParams.year = activeFilters.year;
      if (activeFilters.startDate) baseParams.startDate = activeFilters.startDate;
      if (activeFilters.endDate) baseParams.endDate = activeFilters.endDate;

      const bucketKeys = ['30_60', '60_90', '90_120', 'gt_120'];
      const requests = bucketKeys.map((bucket) => axios.get(apiUrl('/api/reports/arrears'), {
        params: { ...baseParams, bucket },
        headers: { Authorization: `Bearer ${token}` },
      }));

      const results = await Promise.all(requests);
      const buckets = {};
      let asOfDate = null;
      for (const res of results) {
        const bucket = String(res.data?.bucket || '').trim();
        if (bucket) {
          buckets[bucket] = {
            totals: res.data?.totals || { count: 0, total_outstanding: 0 },
            loans: Array.isArray(res.data?.loans) ? res.data.loans : [],
          };
        }
        if (!asOfDate && res.data?.asOfDate) asOfDate = res.data.asOfDate;
      }

      setArrearsBreakdown((prev) => ({
        ...prev,
        asOfDate,
        buckets: {
          ...prev.buckets,
          ...buckets,
        },
      }));
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to load arrears breakdown';
      setArrearsBreakdownError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchRepaymentMethods = useCallback(async (activeFilters) => {
    setRepaymentsError('');
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const baseParams = {};
      if (activeFilters.month) baseParams.month = activeFilters.month;
      if (activeFilters.year) baseParams.year = activeFilters.year;
      if (activeFilters.startDate) baseParams.startDate = activeFilters.startDate;
      if (activeFilters.endDate) baseParams.endDate = activeFilters.endDate;
      const categories = ['debit', 'cash', 'other'];
      const results = await Promise.all(categories.map((category) => axios.get(apiUrl('/api/reports/repayments'), {
        params: { ...baseParams, category },
        headers: { Authorization: `Bearer ${token}` },
      })));

      const byCategory = {};
      for (let i = 0; i < categories.length; i += 1) {
        const cat = categories[i];
        const res = results[i];
        byCategory[cat] = {
          filters: res.data?.filters || { category: cat, month: null, year: null, startDate: null, endDate: null },
          totals: res.data?.totals || { count: 0, total_paid: 0 },
          byMonth: res.data?.byMonth || [],
          items: res.data?.items || [],
        };
      }

      setRepaymentMethods({
        debit: byCategory.debit || EMPTY_REPAYMENTS,
        cash: byCategory.cash || EMPTY_REPAYMENTS,
        other: byCategory.other || EMPTY_REPAYMENTS,
      });
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to load repayments report';
      setRepaymentsError(msg);
      setRepaymentMethods(EMPTY_REPAYMENT_METHODS);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchProvisionBadDebts = useCallback(async (activeFilters) => {
    setProvisionError('');
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = {};
      if (activeFilters.month) params.month = activeFilters.month;
      if (activeFilters.year) params.year = activeFilters.year;
      if (activeFilters.startDate) params.startDate = activeFilters.startDate;
      const rate = Number(provisionRatePct);
      if (Number.isFinite(rate) && rate >= 0) params.provisionRate = rate / 100;
      const res = await axios.get(apiUrl('/api/reports/provision-bad-debts'), {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      setProvision({
        totals: res.data?.totals || { count: 0, total_outstanding_at_start: 0, total_provision: 0 },
        loans: res.data?.loans || [],
        filters: res.data?.filters || { startDate: null, provisionRate: (rate / 100) || 0 },
      });
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to load bad debts provision report';
      setProvisionError(msg);
    } finally {
      setLoading(false);
    }
  }, [provisionRatePct]);

  const fetchProvisionBadDebtsMovement = useCallback(async (activeFilters) => {
    setProvisionMovementError('');
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = {};
      if (activeFilters.month) params.month = activeFilters.month;
      if (activeFilters.year) params.year = activeFilters.year;
      if (activeFilters.startDate) params.startDate = activeFilters.startDate;
      if (activeFilters.endDate) params.endDate = activeFilters.endDate;
      const rate = Number(provisionRatePct);
      if (Number.isFinite(rate) && rate >= 0) params.provisionRate = rate / 100;

      const res = await axios.get(apiUrl('/api/reports/provision-bad-debts-movement'), {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });

      setProvisionMovement({
        movement: res.data?.movement || {
          opening_provision: 0,
          loans_written_off: 0,
          provision_during_period: 0,
          closing_provision: 0,
          opening_outstanding: 0,
          closing_outstanding: 0,
          written_off_count: 0,
        },
        writtenOffLoans: res.data?.writtenOffLoans || [],
        filters: res.data?.filters || { startDate: null, endDate: null, provisionRate: (rate / 100) || 0 },
      });
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to load bad debts provision movement report';
      setProvisionMovementError(msg);
    } finally {
      setLoading(false);
    }
  }, [provisionRatePct]);

  const fetchInterestOutstandingQuarter = useCallback(async () => {
    setInterestQuarterError('');
    if (!quarterYear || !quarter) {
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = { year: Number(quarterYear), quarter: Number(quarter) };
      const res = await axios.get(apiUrl('/api/reports/interest-outstanding-quarter'), {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      setInterestQuarter({
        totals: res.data?.totals || { count: 0, total_outstanding_at_end: 0, total_interest_charged: 0 },
        loans: res.data?.loans || [],
        filters: res.data?.filters || { endDate: null, year: Number(quarterYear), quarter: Number(quarter) },
      });
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to load interest outstanding report';
      setInterestQuarterError(msg);
    } finally {
      setLoading(false);
    }
  }, [quarter, quarterYear]);

  const formatDate = (val) => (val ? formatDateDMY(val) : '');

  const fetchOverdue = useCallback(async (activeFilters) => {
    setOverdueError('');
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = {};
      if (activeFilters.month) params.month = activeFilters.month;
      if (activeFilters.year) params.year = activeFilters.year;
      if (activeFilters.startDate) params.startDate = activeFilters.startDate;
      if (activeFilters.endDate) params.endDate = activeFilters.endDate;
      const res = await axios.get(apiUrl('/api/reports/overdue'), {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      setOverdue({
        totals: res.data?.totals || { count: 0, total_penalty_interest: 0, total_amount_due: 0 },
        loans: res.data?.loans || [],
      });
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to load overdue loans';
      setOverdueError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchUnpaid = useCallback(async (activeFilters) => {
    setUnpaidError('');
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = {};
      if (activeFilters.month) params.month = activeFilters.month;
      if (activeFilters.year) params.year = activeFilters.year;
      if (activeFilters.startDate) params.startDate = activeFilters.startDate;
      if (activeFilters.endDate) params.endDate = activeFilters.endDate;
      const res = await axios.get(apiUrl('/api/reports/unpaid'), {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      setUnpaid({
        totals: res.data?.totals || { count: 0, total_outstanding: 0 },
        loans: res.data?.loans || [],
      });
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to load unpaid loans';
      setUnpaidError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchInterest = useCallback(async (activeFilters) => {
    setInterestError('');
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = {};
      if (activeFilters.month) params.month = activeFilters.month;
      if (activeFilters.year) params.year = activeFilters.year;
      if (activeFilters.startDate) params.startDate = activeFilters.startDate;
      if (activeFilters.endDate) params.endDate = activeFilters.endDate;
      const res = await axios.get(apiUrl('/api/reports/interest'), {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      setInterest({
        totals: res.data?.totals || { count: 0, total_principal: 0, total_interest: 0, total_repayable: 0 },
        byMonth: res.data?.byMonth || [],
        loans: res.data?.loans || [],
        filters: res.data?.filters || { month: null, year: null, quarter: null, startDate: null, endDate: null },
      });
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to load interest report';
      setInterestError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchInterestQuarterly = useCallback(async () => {
    setInterestError('');
    if (!quarterYear || !quarter) {
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = { year: Number(quarterYear), quarter: Number(quarter) };
      const res = await axios.get(apiUrl('/api/reports/interest'), {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });

      setInterest({
        totals: res.data?.totals || { count: 0, total_principal: 0, total_interest: 0, total_repayable: 0 },
        byMonth: res.data?.byMonth || [],
        loans: res.data?.loans || [],
        filters: res.data?.filters || { month: null, year: Number(quarterYear), quarter: Number(quarter), startDate: null, endDate: null },
      });
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to load interest report';
      setInterestError(msg);
    } finally {
      setLoading(false);
    }
  }, [quarter, quarterYear]);

  const fetchDefaultInterest = useCallback(async (activeFilters) => {
    setDefaultInterestError('');
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = {};
      if (activeFilters.month) params.month = activeFilters.month;
      if (activeFilters.year) params.year = activeFilters.year;
      if (activeFilters.startDate) params.startDate = activeFilters.startDate;
      if (activeFilters.endDate) params.endDate = activeFilters.endDate;
      const res = await axios.get(apiUrl('/api/reports/default-interest'), {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      setDefaultInterest({
        totals: res.data?.totals || { count: 0, total_default_interest: 0, total_amount_due: 0 },
        byMonth: res.data?.byMonth || [],
        items: res.data?.items || [],
      });
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to load default interest report';
      setDefaultInterestError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedReport === 'disbursements') fetchReports(query);
    if (selectedReport === 'total_loans_disbursed') fetchTotalLoansDisbursed(query);
    if (selectedReport === 'total_loans_disbursed') fetchTotalLoansDisbursedLoans(query);
    if (selectedReport === 'loans_per_collection_method') fetchLoansPerCollectionMethod(query);
    if (selectedReport === 'loans_per_collection_method') fetchLoansPerCollectionMethodLoans(query);
    if (selectedReport === 'loans_by_purpose') fetchLoansByPurpose(query);
    if (selectedReport === 'loans_by_purpose') fetchLoansByPurposeLoans(query);
    if (selectedReport === 'disbursements_gender_ranges') fetchDisbursedByGenderRanges(query);
    if (selectedReport === 'disbursements_gender_ranges') fetchDisbursedGenderRangesLoans(query);
    if (selectedReport === 'borrower_salaries_by_gender') fetchBorrowerSalariesByGender(query);
    if (selectedReport === 'borrower_salaries_by_gender') fetchBorrowerSalariesByGenderBorrowers(query);
    if (selectedReport === 'number_of_clients') fetchNumberOfClients(query);
    if (selectedReport === 'loans_outstanding_numbers') fetchLoansOutstandingNumbers(query);
    if (selectedReport === 'disbursements_duration_breakdown') fetchDisbursementsDurationBreakdown(query);
    if (selectedReport === 'disbursements_duration_breakdown') fetchDisbursementsDurationBreakdownLoans(query);
    if (selectedReport === 'overdue') fetchOverdue(query);
    if (selectedReport === 'arrears_breakdown') fetchArrearsBreakdown(query);
    if (selectedReport === 'defaultInterest') fetchDefaultInterest(query);
    if (selectedReport === 'unpaid') fetchUnpaid(query);
    if (selectedReport === 'repayment_methods') fetchRepaymentMethods(query);
    if (selectedReport === 'provision_bad_debts_movement') fetchProvisionBadDebtsMovement(query);
    if (selectedReport === 'interest_outstanding_quarter') fetchInterestOutstandingQuarter();
    if (selectedReport === 'interest') {
      const hasCustomFilters = Boolean(query?.month || query?.startDate || query?.endDate);
      if (hasCustomFilters) fetchInterest(query);
      else fetchInterestQuarterly();
    }
  }, [
    fetchReports,
    fetchTotalLoansDisbursed,
    fetchTotalLoansDisbursedLoans,
    fetchLoansPerCollectionMethod,
    fetchLoansPerCollectionMethodLoans,
    fetchLoansByPurpose,
    fetchLoansByPurposeLoans,
    fetchDisbursedByGenderRanges,
    fetchDisbursedGenderRangesLoans,
    fetchBorrowerSalariesByGender,
    fetchBorrowerSalariesByGenderBorrowers,
    fetchNumberOfClients,
    fetchLoansOutstandingNumbers,
    fetchDisbursementsDurationBreakdown,
    fetchDisbursementsDurationBreakdownLoans,
    fetchOverdue,
    fetchArrearsBreakdown,
    fetchDefaultInterest,
    fetchUnpaid,
    fetchRepaymentMethods,
    fetchProvisionBadDebtsMovement,
    fetchInterestOutstandingQuarter,
    fetchInterest,
    fetchInterestQuarterly,
    query,
    selectedReport,
  ]);

  // A pivot's cells as grid rows for POST /api/reports/grid-totals.
  const pivotGridRows = (tableRows, columns, field) => tableRows.map((tr) => ({
    key: tr.gender,
    values: columns.reduce((acc, col) => ({ ...acc, [col]: tr.cells[col]?.[field] || 0 }), {}),
  }));

  const disbGenderRangesGrid = useMemo(
    () => buildGenderRangeGrid(disbGenderRanges, 'amount_range', 'total_amount'),
    [disbGenderRanges],
  );

  // Row, column and grand totals of the pivot (count and amount) from the API.
  const [disbGenderRangesTotals, setDisbGenderRangesTotals] = useState(null);
  useEffect(() => {
    const { columns, tableRows } = disbGenderRangesGrid;
    if (!tableRows.length) { setDisbGenderRangesTotals(null); return undefined; }
    let cancelled = false;
    Promise.all([
      fetchGridTotals(pivotGridRows(tableRows, columns, 'count'), columns),
      fetchGridTotals(pivotGridRows(tableRows, columns, 'total_amount'), columns),
    ])
      .then(([count, amount]) => { if (!cancelled) setDisbGenderRangesTotals({ count, amount }); })
      .catch((err) => { if (!cancelled) setDisbGenderRangesError(err?.response?.data?.error || err?.message || 'Failed to total the disbursements by gender report'); });
    return () => { cancelled = true; };
  }, [disbGenderRangesGrid, fetchGridTotals]);

  const disbGenderRangesPivot = useMemo(() => {
    const { columns, tableRows } = disbGenderRangesGrid;
    const t = disbGenderRangesTotals;
    return {
      columns,
      tableRows: tableRows.map((tr) => ({
        ...tr,
        totalCount: Number(t?.count?.row_totals?.[tr.gender] || 0),
        totalAmount: Number(t?.amount?.row_totals?.[tr.gender] || 0),
      })),
      columnTotals: columns.reduce((acc, col) => ({
        ...acc,
        [col]: { count: Number(t?.count?.column_totals?.[col] || 0), total_amount: Number(t?.amount?.column_totals?.[col] || 0) },
      }), {}),
      grand: { totalCount: Number(t?.count?.grand_total || 0), totalAmount: Number(t?.amount?.grand_total || 0) },
    };
  }, [disbGenderRangesGrid, disbGenderRangesTotals]);

  const disbGenderRangesPivotPage = useMemo(
    () => paginateArray(Array.isArray(disbGenderRangesPivot.tableRows) ? disbGenderRangesPivot.tableRows : [], pages.disbGenderRangesPivot, pageSize),
    [disbGenderRangesPivot.tableRows, pages.disbGenderRangesPivot, pageSize],
  );

  const borrowerSalariesByGenderGrid = useMemo(
    () => buildGenderRangeGrid(borrowerSalariesByGender, 'salary_range', 'total_salary'),
    [borrowerSalariesByGender],
  );

  // Row, column and grand totals of the pivot (count and salary) from the API.
  const [borrowerSalariesByGenderTotals, setBorrowerSalariesByGenderTotals] = useState(null);
  useEffect(() => {
    const { columns, tableRows } = borrowerSalariesByGenderGrid;
    if (!tableRows.length) { setBorrowerSalariesByGenderTotals(null); return undefined; }
    let cancelled = false;
    Promise.all([
      fetchGridTotals(pivotGridRows(tableRows, columns, 'count'), columns),
      fetchGridTotals(pivotGridRows(tableRows, columns, 'total_salary'), columns),
    ])
      .then(([count, salary]) => { if (!cancelled) setBorrowerSalariesByGenderTotals({ count, salary }); })
      .catch((err) => { if (!cancelled) setBorrowerSalariesByGenderError(err?.response?.data?.error || err?.message || 'Failed to total the borrower salaries by gender report'); });
    return () => { cancelled = true; };
  }, [borrowerSalariesByGenderGrid, fetchGridTotals]);

  const borrowerSalariesByGenderPivot = useMemo(() => {
    const { columns, tableRows } = borrowerSalariesByGenderGrid;
    const t = borrowerSalariesByGenderTotals;
    return {
      columns,
      tableRows: tableRows.map((tr) => ({
        ...tr,
        totalCount: Number(t?.count?.row_totals?.[tr.gender] || 0),
        totalSalary: Number(t?.salary?.row_totals?.[tr.gender] || 0),
      })),
      columnTotals: columns.reduce((acc, col) => ({
        ...acc,
        [col]: { count: Number(t?.count?.column_totals?.[col] || 0), total_salary: Number(t?.salary?.column_totals?.[col] || 0) },
      }), {}),
      grand: { totalCount: Number(t?.count?.grand_total || 0), totalSalary: Number(t?.salary?.grand_total || 0) },
    };
  }, [borrowerSalariesByGenderGrid, borrowerSalariesByGenderTotals]);

  const borrowerSalariesByGenderPivotPage = useMemo(
    () => paginateArray(Array.isArray(borrowerSalariesByGenderPivot.tableRows) ? borrowerSalariesByGenderPivot.tableRows : [], pages.borrowerSalariesByGenderPivot, pageSize),
    [borrowerSalariesByGenderPivot.tableRows, pages.borrowerSalariesByGenderPivot, pageSize],
  );

  const arrearsBucketMeta = useMemo(() => ({
    '30_60': { label: 'Loans in arrears 30 - 60 days', pageKey: 'arrears_30_60' },
    '60_90': { label: 'Loans in arrears 60 - 90 days', pageKey: 'arrears_60_90' },
    '90_120': { label: 'Loans in arrears 90 - 120 days', pageKey: 'arrears_90_120' },
    'gt_120': { label: 'Loans in arrears for more than 120 days', pageKey: 'arrears_gt_120' },
  }), []);


  const mmNumber = (val) => {
    const n = Number(String(val ?? '').replace(/,/g, '').trim());
    return Number.isFinite(n) ? n : 0;
  };

  const mmPercent = (ratio) => {
    const r = Number(ratio);
    if (!Number.isFinite(r)) return '0.00%';
    return `${(r * 100).toFixed(2)}%`;
  };

  const generateMonthlyManagement = useCallback(async () => {
    setMonthlyManagementError('');
    setMonthlyManagement(null);
    setMmInputsReadyKey('');
    setMmDerivedByMonth({});
    setMmDerivedError('');

    const y = Number(mmYear);
    const q = Number(mmQuarter);
    if (!y || !q || q < 1 || q > 4) {
      setMonthlyManagementError('Please select Year and Quarter for the Monthly Management Report.');
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      setMonthlyManagementError('Not authenticated. Please log in again.');
      return;
    }

    setMonthlyManagementLoading(true);
    try {
      const res = await axios.get(apiUrl('/api/reports/microlender-monthly-management'), {
        params: { year: y, quarter: q },
        headers: { Authorization: `Bearer ${token}` },
      });

      const payload = res.data || null;
      setMonthlyManagement(payload);

      // Load any persisted manual inputs for this quarter so the user sees
      // values they entered before. Non-fatal: report still renders without them.
      let savedMonthly = {};
      let savedQuarterly = {};
      let savedLoaded = false;
      try {
        const savedRes = await axios.get(apiUrl('/api/reports/mm-inputs'), {
          params: { year: y, quarter: q },
          headers: { Authorization: `Bearer ${token}` },
        });
        savedMonthly = savedRes.data?.monthly || {};
        savedQuarterly = savedRes.data?.quarterly || {};
        savedLoaded = true;
      } catch (e) {
        console.warn('Could not load saved monthly management inputs:', e?.message || e);
      }

      // Quarter-level inputs: defaults < existing edits < saved DB values.
      if (payload?.year && payload?.quarter) {
        const qk = mmQuarterKey(payload.year, payload.quarter);
        if (qk) {
          setMonthlyManagementInputsByQuarter((prev) => {
            const merged = { ...EMPTY_MM_QUARTER_INPUTS, ...(prev?.[qk] || {}), ...(savedQuarterly[qk] || {}) };
            // "Prepared by" defaults to the principal officer (Company
            // Configuration) until staff enter someone else.
            if (!String(merged.preparedBy || '').trim() && payload?.company?.principalOfficerName) {
              merged.preparedBy = payload.company.principalOfficerName;
            }
            const next = { ...(prev || {}), [qk]: merged };
            quarterlyInputsRef.current = next;
            return next;
          });
          // Only once the saved inputs really loaded, so a load failure can
          // never let a fresh draft overwrite commentary already saved.
          if (savedLoaded) setMmInputsReadyKey(qk);
        }
      }

      // Month-level inputs: defaults < existing edits < saved DB values, then
      // ensure secured/unsecured defaults (secured=0 editable; unsecured derived).
      const monthReports = Array.isArray(payload?.months) ? payload.months : [];
      setMonthlyManagementInputsByMonth((prev) => {
        const next = { ...(prev || {}) };
        monthReports.forEach((mr) => {
          const k = mmKey(mr?.period?.year, mr?.period?.month);
          if (!k) return;
          const merged = { ...EMPTY_MM_INPUTS, ...(next[k] || {}), ...(savedMonthly[k] || {}) };
          const securedLoansCount = String(merged.securedLoansCount ?? '').trim() ? merged.securedLoansCount : '0';
          const securedLoansAmount = String(merged.securedLoansAmount ?? '').trim() ? merged.securedLoansAmount : '0';
          next[k] = { ...merged, securedLoansCount, securedLoansAmount };
        });
        monthlyInputsRef.current = next;
        return next;
      });
      setMmSaveState('idle');
    } catch (err) {
      if (err?.response?.status === 422 && err?.response?.data?.code === 'CASH_BALANCE_REQUIRED') {
        openCashPrompt(err.response.data, () => generateRef.current());
        return;
      }
      const msg = err?.response?.data?.error || err?.message || 'Failed to generate monthly management report';
      setMonthlyManagementError(msg);
    } finally {
      setMonthlyManagementLoading(false);
    }
  }, [mmYear, mmQuarter, EMPTY_MM_INPUTS, EMPTY_MM_QUARTER_INPUTS]);
  generateRef.current = generateMonthlyManagement;

  const downloadCsv = (rows, headers, filename) => {
    const csv = [headers.join(',')]
      .concat(rows.map(r => headers.map(h => (r[h] !== undefined && r[h] !== null ? String(csvCell(r[h])).replace(/,/g, ' ') : '')).join(',')))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
  };

  const exportExcel = async () => {
    if (selectedReport === 'disbursements') {
      downloadCsv(
        [
          { metric: 'Total Loans', value: data.totals.count },
          { metric: 'Total Amount', value: data.totals.total_amount },
          { metric: 'Loans Paid Off', value: data.paidOff.count },
          { metric: 'Paid Off Amount', value: data.paidOff.total_amount },
        ],
        ['metric', 'value'],
        'disbursements_summary.csv'
      );
      downloadCsv(data.byGender, ['gender', 'count', 'total_amount'], 'disbursements_by_gender.csv');
      downloadCsv(data.byReason, ['reason', 'count', 'total_amount'], 'disbursements_by_reason.csv');
      downloadCsv(data.byMonth, ['month', 'count', 'total_amount'], 'disbursements_by_month.csv');
      return;
    }

    if (selectedReport === 'total_loans_disbursed') {
      downloadCsv(
        [
          { metric: 'Start Date', value: dmy(totalLoansDisbursed.filters?.startDate, '') },
          { metric: 'End Date', value: dmy(totalLoansDisbursed.filters?.endDate, '') },
          { metric: 'Total Loans Disbursed', value: totalLoansDisbursed.totals?.count ?? 0 },
        ],
        ['metric', 'value'],
        'total_loans_disbursed_during_period.csv'
      );
      return;
    }

    if (selectedReport === 'loans_per_collection_method') {
      downloadCsv(
        (Array.isArray(loansPerCollectionMethod.rows) ? loansPerCollectionMethod.rows : []).map(r => ({
          collection_method: r.collection_method,
          loan_count: r.loan_count,
        })),
        ['collection_method', 'loan_count'],
        'loans_per_collection_method.csv'
      );
      return;
    }

    if (selectedReport === 'loans_by_purpose') {
      downloadCsv(
        (Array.isArray(loansByPurpose.rows) ? loansByPurpose.rows : []).map(r => ({
          purpose: r.purpose,
          loan_count: r.loan_count,
        })),
        ['purpose', 'loan_count'],
        'loans_by_purpose.csv'
      );
      return;
    }

    if (selectedReport === 'disbursements_gender_ranges') {
      const columns = disbGenderRangesPivot.columns;
      const headerForColumn = (label) => String(label || '').replace(/,/g, '');
      const headers = [
        'gender',
        ...columns.flatMap((c) => {
          const col = headerForColumn(c);
          return [`${col}__count`, `${col}__total_amount`];
        }),
        'total_count',
        'total_amount',
      ];

      const rows = disbGenderRangesPivot.tableRows.map((r) => {
        const base = { gender: r.gender };
        for (const col of columns) {
          const cell = r.cells[col] || { count: 0, total_amount: 0 };
          const safeCol = headerForColumn(col);
          base[`${safeCol}__count`] = cell.count;
          base[`${safeCol}__total_amount`] = cell.total_amount;
        }
        base.total_count = r.totalCount;
        base.total_amount = r.totalAmount;
        return base;
      });

      downloadCsv(rows, headers, 'disbursed_loans_by_gender_amount_ranges.csv');

      downloadCsv(
        [
          { metric: 'Total Loans', value: disbGenderRangesPivot.grand.totalCount },
          { metric: 'Total Amount', value: disbGenderRangesPivot.grand.totalAmount },
        ],
        ['metric', 'value'],
        'disbursed_loans_by_gender_amount_ranges_summary.csv'
      );

      downloadCsv(
        (Array.isArray(disbGenderRangesLoans) ? disbGenderRangesLoans : []).map((r) => ({
          amount_range: r.amount_range,
          loan_code: loanRef(r),
          client_name: r.client_name,
          gender: r.gender,
          principal_amount: r.principal_amount,
          created_at: r.created_at ? formatDate(r.created_at) : '',
          due_date: r.due_date ? formatDate(r.due_date) : '',
          days_overdue: r.days_overdue ?? '',
          loan_status: r.loan_status,
        })),
        ['amount_range', 'loan_code', 'client_name', 'gender', 'principal_amount', 'created_at', 'due_date', 'days_overdue', 'loan_status'],
        'disbursed_loans_by_gender_amount_ranges_loans.csv'
      );
      return;
    }

    if (selectedReport === 'borrower_salaries_by_gender') {
      const columns = borrowerSalariesByGenderPivot.columns;
      const headerForColumn = (label) => String(label || '').replace(/,/g, '');
      const headers = [
        'gender',
        ...columns.flatMap((c) => {
          const col = headerForColumn(c);
          return [`${col}__count`, `${col}__total_salary`];
        }),
        'total_count',
        'total_salary',
      ];

      const rows = borrowerSalariesByGenderPivot.tableRows.map((r) => {
        const base = { gender: r.gender };
        for (const col of columns) {
          const cell = r.cells[col] || { count: 0, total_salary: 0 };
          const safeCol = headerForColumn(col);
          base[`${safeCol}__count`] = cell.count;
          base[`${safeCol}__total_salary`] = cell.total_salary;
        }
        base.total_count = r.totalCount;
        base.total_salary = r.totalSalary;
        return base;
      });

      downloadCsv(rows, headers, 'borrower_salaries_by_gender.csv');

      downloadCsv(
        [
          { metric: 'Total Borrowers', value: borrowerSalariesByGenderPivot.grand.totalCount },
          { metric: 'Total Salary', value: borrowerSalariesByGenderPivot.grand.totalSalary },
        ],
        ['metric', 'value'],
        'borrower_salaries_by_gender_summary.csv'
      );
      return;
    }

    if (selectedReport === 'number_of_clients') {
      downloadCsv(
        [
          { metric: 'As Of', value: dmy(numberOfClients.filters?.endDate, '') },
          { metric: 'Active Clients', value: numberOfClients.totals.active_clients },
          { metric: 'Outstanding Loans', value: numberOfClients.totals.loan_count },
          { metric: 'Total Outstanding', value: numberOfClients.totals.total_outstanding },
        ],
        ['metric', 'value'],
        'total_number_of_clients_in_book_active_clients_summary.csv'
      );

      downloadCsv(
        (Array.isArray(numberOfClients.loans) ? numberOfClients.loans : []).map((r) => ({
          loan_code: loanRef(r),
          client_name: r.client_name,
          loan_status: r.loan_status,
          created_at: r.created_at ? formatDate(r.created_at) : '',
          due_date: r.due_date ? formatDate(r.due_date) : '',
          days_overdue: r.days_overdue ?? '',
          principal_amount: r.principal_amount,
          total_due: r.total_due,
          total_paid: r.total_paid,
          outstanding: r.outstanding,
        })),
        ['loan_code', 'client_name', 'loan_status', 'created_at', 'due_date', 'days_overdue', 'principal_amount', 'total_due', 'total_paid', 'outstanding'],
        'total_number_of_clients_in_book_active_clients_loans.csv'
      );
      return;
    }

    if (selectedReport === 'loans_outstanding_numbers') {
      downloadCsv(
        [
          { metric: 'As Of', value: dmy(loansOutstandingNumbers.filters?.endDate, '') },
          { metric: 'Current Loans Count', value: loansOutstandingNumbers.current.count },
          { metric: 'Current Total Outstanding', value: loansOutstandingNumbers.current.total_outstanding },
          { metric: 'Arrears Loans Count', value: loansOutstandingNumbers.arrears.count },
          { metric: 'Arrears Total Outstanding', value: loansOutstandingNumbers.arrears.total_outstanding },
        ],
        ['metric', 'value'],
        'loans_outstanding_summary.csv'
      );

      downloadCsv(
        (Array.isArray(loansOutstandingNumbers.currentLoans) ? loansOutstandingNumbers.currentLoans : []).map((r) => ({
          loan_code: loanRef(r),
          client_name: r.client_name,
          loan_status: r.loan_status,
          created_at: r.created_at ? formatDate(r.created_at) : '',
          due_date: r.due_date ? formatDate(r.due_date) : '',
          days_overdue: r.days_overdue ?? '',
          total_due: r.total_due,
          total_paid: r.total_paid,
          outstanding: r.outstanding,
        })),
        ['loan_code', 'client_name', 'loan_status', 'created_at', 'due_date', 'days_overdue', 'total_due', 'total_paid', 'outstanding'],
        'loans_outstanding_current_loans.csv'
      );

      downloadCsv(
        (Array.isArray(loansOutstandingNumbers.arrearsLoans) ? loansOutstandingNumbers.arrearsLoans : []).map((r) => ({
          loan_code: loanRef(r),
          client_name: r.client_name,
          loan_status: r.loan_status,
          created_at: r.created_at ? formatDate(r.created_at) : '',
          due_date: r.due_date ? formatDate(r.due_date) : '',
          days_overdue: r.days_overdue ?? '',
          total_due: r.total_due,
          total_paid: r.total_paid,
          outstanding: r.outstanding,
        })),
        ['loan_code', 'client_name', 'loan_status', 'created_at', 'due_date', 'days_overdue', 'total_due', 'total_paid', 'outstanding'],
        'loans_outstanding_arrears_loans.csv'
      );
      return;
    }

    if (selectedReport === 'disbursements_duration_breakdown') {
      downloadCsv(
        (Array.isArray(disbDurationBreakdown.rows) ? disbDurationBreakdown.rows : []).map((r) => ({
          duration_months: r.duration_months,
          loan_count: r.loan_count,
          total_amount: r.total_amount,
        })),
        ['duration_months', 'loan_count', 'total_amount'],
        'disbursements_duration_breakdown_totals.csv'
      );
      downloadCsv(
        (Array.isArray(disbDurationBreakdownLoans) ? disbDurationBreakdownLoans : []).map((r) => ({
          duration_months: r.duration_months,
          loan_code: loanRef(r),
          client_name: r.client_name,
          gender: r.gender,
          principal_amount: r.principal_amount,
          disbursement_date: r.disbursement_date ? formatDate(r.disbursement_date) : '',
          loan_status: r.loan_status,
        })),
        ['duration_months', 'loan_code', 'client_name', 'gender', 'principal_amount', 'disbursement_date', 'loan_status'],
        'disbursements_duration_breakdown_loans.csv'
      );
      return;
    }

    if (selectedReport === 'overdue') {
      downloadCsv(
        overdue.loans.map(r => ({
          loan_code: loanRef(r),
          client_name: r.client_name,
          created_at: r.created_at ? formatDate(r.created_at) : '',
          due_date: formatDate(r.due_date),
          days_overdue: r.days_overdue,
          amount_due: r.amount_due,
          overdue_interest: r.overdue_interest,
          total_due: r.total_due ?? ((Number(r.amount_due) || 0) + (Number(r.overdue_interest) || 0)),
          loan_status: r.loan_status,
        })),
        ['loan_code', 'client_name', 'created_at', 'due_date', 'days_overdue', 'amount_due', 'overdue_interest', 'total_due', 'loan_status'],
        'overdue_loans.csv'
      );
      return;
    }

    if (selectedReport === 'arrears_breakdown') {
      const safeName = 'arrears_breakdown';
      const buckets = arrearsBreakdown?.buckets || {};

      for (const bucketKey of ['30_60', '60_90', '90_120', 'gt_120']) {
        const meta = arrearsBucketMeta?.[bucketKey];
        const label = meta?.label || `Arrears ${bucketKey}`;
        const bucket = buckets?.[bucketKey] || { totals: { count: 0, total_outstanding: 0 }, loans: [] };

        downloadCsv(
          (bucket.loans || []).map(r => ({
            bucket: bucketKey,
            loan_code: loanRef(r),
            client_name: r.client_name,
            loan_status: r.loan_status,
            created_at: r.created_at ? formatDate(r.created_at) : '',
            due_date: r.due_date ? formatDate(r.due_date) : '',
            days_overdue: r.days_overdue ?? '',
            total_due: r.total_due,
            total_paid: r.total_paid,
            outstanding: r.outstanding,
          })),
          ['bucket', 'loan_code', 'client_name', 'loan_status', 'created_at', 'due_date', 'days_overdue', 'total_due', 'total_paid', 'outstanding'],
          `${safeName}_${bucketKey}.csv`
        );

        downloadCsv(
          [
            { metric: 'Report', value: label },
            { metric: 'Loans Count', value: bucket.totals?.count || 0 },
            { metric: 'Total Outstanding', value: bucket.totals?.total_outstanding || 0 },
          ],
          ['metric', 'value'],
          `${safeName}_${bucketKey}_summary.csv`
        );
      }

      downloadCsv(
        [
          { metric: 'Report', value: 'Loans in arrears (all buckets)' },
          { metric: 'As Of Date', value: arrearsBreakdown?.asOfDate ? formatDate(arrearsBreakdown.asOfDate) : '' },
        ],
        ['metric', 'value'],
        `${safeName}_meta.csv`
      );

      return;
    }

    if (selectedReport === 'defaultInterest') {
      downloadCsv(
        defaultInterest.items.map(r => ({
          loan_code: loanRef(r),
          client_name: r.client_name,
          created_at: r.created_at ? formatDate(r.created_at) : '',
          due_date: formatDate(r.due_date),
          days_overdue: r.days_overdue,
          default_interest: r.default_interest,
          amount_due: r.amount_due,
          total_due: r.total_due,
          loan_status: r.loan_status,
        })),
        ['loan_code', 'client_name', 'created_at', 'due_date', 'days_overdue', 'default_interest', 'amount_due', 'total_due', 'loan_status'],
        'default_interest.csv'
      );
      downloadCsv(defaultInterest.byMonth, ['month', 'count', 'total_default_interest'], 'default_interest_by_month.csv');
      return;
    }

    if (selectedReport === 'unpaid') {
      downloadCsv(
        unpaid.loans.map(r => ({
          loan_code: loanRef(r),
          client_name: r.client_name,
          loan_status: r.loan_status,
          created_at: formatDate(r.created_at),
          due_date: r.due_date ? formatDate(r.due_date) : '',
          days_overdue: r.days_overdue ?? '',
          total_due: r.total_due,
          total_paid: r.total_paid,
          outstanding: r.outstanding,
        })),
        ['loan_code', 'client_name', 'loan_status', 'created_at', 'due_date', 'days_overdue', 'total_due', 'total_paid', 'outstanding'],
        'unpaid_loans.csv'
      );
      return;
    }

    if (selectedReport === 'repayment_methods') {
      const exportCategory = (category, label) => {
        const rep = repaymentMethods?.[category] || EMPTY_REPAYMENTS;
        downloadCsv(
          (Array.isArray(rep.items) ? rep.items : []).map(r => ({
            repayment_id: r.repayment_id,
            loan_code: loanRef(r),
            client_name: r.client_name,
            created_at: r.created_at ? formatDate(r.created_at) : '',
            due_date: r.due_date ? formatDate(r.due_date) : '',
            days_overdue: r.days_overdue ?? '',
            payment_date: r.payment_date ? formatDate(r.payment_date) : '',
            payment_method: r.payment_method,
            payment_reference: r.payment_reference,
            amount_paid: r.amount_paid,
          })),
          ['repayment_id', 'loan_code', 'client_name', 'created_at', 'due_date', 'days_overdue', 'payment_date', 'payment_method', 'payment_reference', 'amount_paid'],
          `repayment_methods_${category}.csv`
        );

        downloadCsv(
          (Array.isArray(rep.byMonth) ? rep.byMonth : []).map(r => ({ month: r.month, count: r.count, total_paid: r.total_paid })),
          ['month', 'count', 'total_paid'],
          `repayment_methods_${category}_by_month.csv`
        );

        downloadCsv(
          [
            { metric: 'Method', value: label },
            { metric: 'Payments Count', value: rep.totals?.count || 0 },
            { metric: 'Total Paid', value: rep.totals?.total_paid || 0 },
          ],
          ['metric', 'value'],
          `repayment_methods_${category}_summary.csv`
        );
      };

      exportCategory('debit', 'Debit Order');
      exportCategory('cash', 'Cash');
      exportCategory('other', 'Other');
      return;
    }

    if (selectedReport === 'provision_bad_debts_movement') {
      const m = provisionMovement?.movement || {};

      downloadCsv(
        [
          { metric: 'Start Date', value: dmy(provisionMovement.filters?.startDate, '') },
          { metric: 'End Date', value: dmy(provisionMovement.filters?.endDate, '') },
          { metric: 'Provision Rate', value: `${Number(provisionMovement.filters?.provisionRate || 0) * 100}%` },
          { metric: 'Provision for bad debts beginning of period', value: m.opening_provision ?? 0 },
          { metric: 'Loans written-off during the period', value: m.loans_written_off ?? 0 },
          { metric: 'Provision for bad debts during the period', value: m.provision_during_period ?? 0 },
          { metric: 'Written-off count', value: m.written_off_count ?? 0 },
        ],
        ['metric', 'value'],
        'provision_bad_debts_movement_summary.csv'
      );

      downloadCsv(
        (provisionMovement.writtenOffLoans || []).map(r => ({
          loan_code: loanRef(r),
          client_name: r.client_name,
          loan_status: r.loan_status,
          created_at: r.created_at ? formatDate(r.created_at) : '',
          due_date: r.due_date ? formatDate(r.due_date) : '',
          writeoff_date: r.writeoff_date ? formatDate(r.writeoff_date) : '',
          principal_amount: r.principal_amount,
        })),
        ['loan_code', 'client_name', 'loan_status', 'created_at', 'due_date', 'writeoff_date', 'principal_amount'],
        'provision_bad_debts_movement_written_off_loans.csv'
      );
      return;
    }

    if (selectedReport === 'interest_outstanding_quarter') {
      downloadCsv(
        interestQuarter.loans.map(r => ({
          loan_code: loanRef(r),
          client_name: r.client_name,
          loan_status: r.loan_status,
          created_at: formatDate(r.created_at),
          due_date: r.due_date ? formatDate(r.due_date) : '',
          days_overdue: r.days_overdue ?? '',
          principal_amount: r.principal_amount,
          total_repayable: r.total_repayable,
          interest_charged: r.interest_charged,
          total_paid_to_end: r.total_paid_to_end,
          outstanding_at_end: r.outstanding_at_end,
        })),
        ['loan_code', 'client_name', 'loan_status', 'created_at', 'due_date', 'days_overdue', 'principal_amount', 'total_repayable', 'interest_charged', 'total_paid_to_end', 'outstanding_at_end'],
        'interest_outstanding_quarter.csv'
      );
      return;
    }

    if (selectedReport === 'interest') {
      downloadCsv(
        interest.loans.map(r => ({
          loan_code: loanRef(r),
          client_name: r.client_name,
          loan_status: r.loan_status,
          created_at: formatDate(r.created_at),
          due_date: r.due_date ? formatDate(r.due_date) : '',
          days_overdue: r.days_overdue ?? '',
          interest_rate: r.interest_rate,
          principal_amount: r.principal_amount,
          interest_amount: r.interest_amount,
          total_repayable: r.total_repayable,
        })),
        ['loan_code', 'client_name', 'loan_status', 'created_at', 'due_date', 'days_overdue', 'interest_rate', 'principal_amount', 'interest_amount', 'total_repayable'],
        'interest_on_loans.csv'
      );
      downloadCsv(interest.byMonth, ['month', 'count', 'total_interest'], 'interest_by_month.csv');
    }

    if (selectedReport === 'microlender_monthly_management') {
      if (!monthlyManagement) {
        setMonthlyManagementError('Generate the Monthly Management Report first.');
        return;
      }
      if (monthlyManagement?.year && monthlyManagement?.quarter
        && !(await ensureCashBalances(monthlyManagement.year, monthlyManagement.quarter, () => exportExcel()))) return;

      const monthReports = Array.isArray(monthlyManagement?.months)
        ? monthlyManagement.months
        : (monthlyManagement?.period?.month && monthlyManagement?.period?.year)
          ? [monthlyManagement]
          : [];

      if (monthReports.length === 0) {
        setMonthlyManagementError('Generate the Monthly Management Report first.');
        return;
      }

      const monthLabel = (mr) => {
        const reportMonth = mr?.period?.month;
        const reportYear = mr?.period?.year;
        const label = monthOptions.find(o => Number(o.value) === Number(reportMonth))?.label || reportMonth;
        return `${label} ${reportYear}`.trim();
      };

      const fileSuffix = (monthlyManagement?.quarter && monthlyManagement?.year)
        ? `Q${monthlyManagement.quarter}_${monthlyManagement.year}`
        : 'custom';

      const qKey = (monthlyManagement?.year && monthlyManagement?.quarter)
        ? mmQuarterKey(monthlyManagement.year, monthlyManagement.quarter)
        : '';
      const qInputs = getMmQuarterInputs(qKey);
      const company = monthlyManagement?.company || {};

      downloadCsv(
        [
          { field: 'Company name', value: company?.name || '' },
          { field: 'NAMFISA licence / reg no', value: company?.namfisaRegNo || '' },
          { field: 'Address', value: company?.address || '' },
          { field: 'Phone', value: company?.phoneNumber || '' },
          { field: 'Email', value: company?.email || '' },
          { field: 'Year', value: monthlyManagement?.year ?? '' },
          { field: 'Quarter', value: monthlyManagement?.quarter ?? '' },
          { field: 'Quarter start', value: monthlyManagement?.quarterStartDate || '' },
          { field: 'Quarter end', value: monthlyManagement?.quarterEndDate || '' },
          { field: 'Compiled by', value: mmPreparedBy(qInputs) },
          { field: 'Approved by', value: qInputs.approvedBy || '' },
        ],
        ['field', 'value'],
        `microlender_monthly_management_cover_${fileSuffix}.csv`
      );

      const execRows = [];
      const portfolioRows = [];
      const arrearsRows = [];
      const incomeRows = [];

      monthReports.forEach((mr) => {
        const reportMonth = mr?.period?.month;
        const reportYear = mr?.period?.year;
        const key = mmKey(reportYear, reportMonth);
        const inputs = getMmInputs(key);
        const label = monthLabel(mr);

        execRows.push(
          { month: label, metric: 'Period Start', value: mr?.period?.startDate || '' },
          { month: label, metric: 'Period End', value: mr?.period?.endDate || '' },
          { month: label, metric: 'Total loan book value (closing)', value: mr?.loanBook?.closing?.total_outstanding ?? 0 },
          { month: label, metric: 'Active loans (closing)', value: mr?.loanBook?.closing?.count ?? 0 },
          { month: label, metric: 'Arrears ratio (by amount)', value: mmPercent(mr?.arrears?.arrearsRatioByAmount || 0) },
          { month: label, metric: 'Major risk issues', value: inputs.majorRiskIssues || '-' },
        );

        const derived = getMmDerived(key, mr);
        const unsecuredCount = derived.new_loans.unsecured.count;
        const unsecuredAmt = derived.new_loans.unsecured.amount;

        incomeRows.push(
          { month: label, item: 'Interest income', amount: derived.income.interest_income },
          { month: label, item: 'Fees income', amount: derived.income.fees_income },
          { month: label, item: 'Default interest', amount: derived.income.default_interest },
          { month: label, item: 'Bad debts recovered', amount: derived.income.bad_debts_recovered },
          { month: label, item: 'Other income', amount: derived.income.other_income },
          { month: label, item: 'Total income', amount: derived.income.total_income },
          { month: label, item: 'Operating expenses', amount: derived.income.operating_expenses },
          { month: label, item: 'Impairment charge', amount: derived.income.impairment_charge },
          { month: label, item: 'Total expenses', amount: derived.income.total_expenses },
          { month: label, item: 'Net profit / loss', amount: derived.income.net_profit },
        );

        portfolioRows.push(
          { month: label, item: 'Opening loan book', amount: mr?.loanBook?.opening?.total_outstanding ?? 0, loans: mr?.loanBook?.opening?.count ?? 0 },
          { month: label, item: 'New loans issued', amount: mr?.loanBook?.newLoans?.total_principal ?? 0, loans: mr?.loanBook?.newLoans?.count ?? 0 },
          { month: label, item: 'Loans settled', amount: mr?.loanBook?.settled?.total_settled ?? 0, loans: mr?.loanBook?.settled?.count ?? 0 },
          { month: label, item: 'Written off', amount: mr?.loanBook?.writtenOff?.total ?? 0, loans: mr?.loanBook?.writtenOff?.count ?? 0 },
          { month: label, item: 'Repayments & other movements', amount: mr?.loanBook?.otherMovements?.total ?? 0, loans: mr?.loanBook?.otherMovements?.count ?? 0 },
          { month: label, item: 'Closing loan book', amount: mr?.loanBook?.closing?.total_outstanding ?? 0, loans: mr?.loanBook?.closing?.count ?? 0 },
          { month: label, item: 'Unsecured loans', amount: unsecuredAmt, loans: unsecuredCount },
          { month: label, item: 'Secured loans', amount: mmNumber(inputs.securedLoansAmount), loans: mmNumber(inputs.securedLoansCount) },
        );

        arrearsRows.push(
          { month: label, bucket: '30–60 days', number_of_loans: mr?.arrears?.buckets?.['30_60']?.count ?? 0, outstanding: mr?.arrears?.buckets?.['30_60']?.outstanding ?? 0 },
          { month: label, bucket: '60–90 days', number_of_loans: mr?.arrears?.buckets?.['60_90']?.count ?? 0, outstanding: mr?.arrears?.buckets?.['60_90']?.outstanding ?? 0 },
          { month: label, bucket: '90–120 days', number_of_loans: mr?.arrears?.buckets?.['90_120']?.count ?? 0, outstanding: mr?.arrears?.buckets?.['90_120']?.outstanding ?? 0 },
          { month: label, bucket: 'Over 120 days', number_of_loans: mr?.arrears?.buckets?.['gt_120']?.count ?? 0, outstanding: mr?.arrears?.buckets?.['gt_120']?.outstanding ?? 0 },
          { month: label, bucket: 'Total in arrears', number_of_loans: mr?.arrears?.totals?.count ?? 0, outstanding: mr?.arrears?.totals?.outstanding ?? 0 },
          { month: label, bucket: 'Arrears Ratio', number_of_loans: '', outstanding: mmPercent(mr?.arrears?.arrearsRatioByAmount || 0) },
        );
      });

      downloadCsv(execRows, ['month', 'metric', 'value'], `microlender_monthly_management_exec_summary_${fileSuffix}.csv`);
      downloadCsv(portfolioRows, ['month', 'item', 'amount', 'loans'], `microlender_monthly_management_portfolio_${fileSuffix}.csv`);
      downloadCsv(incomeRows, ['month', 'item', 'amount'], `microlender_monthly_management_income_${fileSuffix}.csv`);
      downloadCsv(arrearsRows, ['month', 'bucket', 'number_of_loans', 'outstanding'], `microlender_monthly_management_arrears_${fileSuffix}.csv`);

      downloadCsv(
        [
          { role: 'Compiled by', name: mmPreparedBy(qInputs), signature: '', date: '' },
          { role: 'Approved by', name: qInputs.approvedBy || '', signature: '', date: '' },
        ],
        ['role', 'name', 'signature', 'date'],
        `microlender_monthly_management_signatures_${fileSuffix}.csv`
      );
    }
  };

  const exportPdf = () => {
    const doc = (selectedReport === 'disbursements_gender_ranges' || selectedReport === 'borrower_salaries_by_gender')
      ? new jsPDF({ orientation: 'landscape' })
      : new jsPDF();

    if (selectedReport === 'disbursements') {
      doc.text('Loan Disbursement Reports', 14, 12);

      autoTable(doc, {
        startY: 18,
        head: [['Metric', 'Value']],
        body: [
          ['Total Loans', data.totals.count],
          ['Total Amount', data.totals.total_amount],
          ['Loans Paid Off', data.paidOff.count],
          ['Paid Off Amount', data.paidOff.total_amount],
        ],
      });

      autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 8,
        head: [['Gender', 'Count', 'Total Amount']],
        body: data.byGender.map(r => [r.gender, r.count, r.total_amount]),
      });

      autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 8,
        head: [['Reason', 'Count', 'Total Amount']],
        body: data.byReason.map(r => [r.reason, r.count, r.total_amount]),
      });

      autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 8,
        head: [['Month', 'Count', 'Total Amount']],
        body: data.byMonth.map(r => [r.month, r.count, r.total_amount]),
      });

      doc.save('disbursements_report.pdf');
      return;
    }

    if (selectedReport === 'total_loans_disbursed') {
      doc.text('Total number of loans disbursed during the period', 14, 12);

      autoTable(doc, {
        startY: 18,
        head: [['Metric', 'Value']],
        body: [
          ['Start Date', dmy(totalLoansDisbursed.filters?.startDate, '')],
          ['End Date', dmy(totalLoansDisbursed.filters?.endDate, '')],
          ['Total Loans Disbursed', totalLoansDisbursed.totals?.count ?? 0],
        ],
      });

      doc.save('total_loans_disbursed_during_period.pdf');
      return;
    }

    if (selectedReport === 'loans_per_collection_method') {
      doc.text('Number of Loans per collection method', 14, 12);

      autoTable(doc, {
        startY: 18,
        head: [['Metric', 'Value']],
        body: [
          ['Start Date', dmy(loansPerCollectionMethod.filters?.startDate, '')],
          ['End Date', dmy(loansPerCollectionMethod.filters?.endDate, '')],
          ['Total Loans', loansPerCollectionMethod.totals?.count ?? 0],
        ],
      });

      autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 8,
        head: [['Collection Method', 'Number of Loans']],
        body: (() => {
          const rows = (Array.isArray(loansPerCollectionMethod.rows) ? loansPerCollectionMethod.rows : []).map(r => [r.collection_method, r.loan_count]);
          const total = loansPerCollectionMethod.totals?.count ?? 0;
          if (rows.length > 0) rows.push(['Total', total]);
          return rows;
        })(),
      });

      doc.save('loans_per_collection_method.pdf');
      return;
    }

    if (selectedReport === 'loans_by_purpose') {
      doc.text('Number of loans by purpose', 14, 12);

      const purposeTotal = loansByPurposeTotalLoans;

      autoTable(doc, {
        startY: 18,
        head: [['Metric', 'Value']],
        body: [
          ['Start Date', dmy(loansByPurpose.filters?.startDate, '')],
          ['End Date', dmy(loansByPurpose.filters?.endDate, '')],
          ['Total Loans', purposeTotal],
        ],
      });

      autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 8,
        head: [['Purpose', 'Number of Loans']],
        body: (() => {
          const rows = (Array.isArray(loansByPurpose.rows) ? loansByPurpose.rows : []).map(r => [r.purpose, r.loan_count]);
          if (rows.length > 0) rows.push(['Total', purposeTotal]);
          return rows;
        })(),
      });

      doc.save('loans_by_purpose.pdf');
      return;
    }

    if (selectedReport === 'disbursements_gender_ranges') {
      doc.text('Disbursed loans by gender (amount ranges)', 14, 12);

      autoTable(doc, {
        startY: 18,
        head: [['Metric', 'Value']],
        body: [
          ['Total Loans', disbGenderRangesPivot.grand.totalCount],
          ['Total Amount', disbGenderRangesPivot.grand.totalAmount],
        ],
      });

      const columns = disbGenderRangesPivot.columns;
      const head = [['Gender', ...columns, 'Total']];
      const body = disbGenderRangesPivot.tableRows.map((r) => {
        const cells = columns.map((col) => {
          const cell = r.cells[col] || { count: 0, total_amount: 0 };
          return `${cell.count} | ${toCurrency(cell.total_amount)}`;
        });
        return [r.gender, ...cells, `${r.totalCount} | ${toCurrency(r.totalAmount)}`];
      });

      if (disbGenderRangesPivot.tableRows.length > 0) {
        const totalRow = [
          'Total',
          ...columns.map((col) => {
            const t = disbGenderRangesPivot.columnTotals[col] || { count: 0, total_amount: 0 };
            return `${t.count} | ${toCurrency(t.total_amount)}`;
          }),
          `${disbGenderRangesPivot.grand.totalCount} | ${toCurrency(disbGenderRangesPivot.grand.totalAmount)}`,
        ];
        body.push(totalRow);
      }

      autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 8,
        head,
        body,
        styles: { fontSize: 8, cellPadding: 2 },
      });

      if (Array.isArray(disbGenderRangesLoans) && disbGenderRangesLoans.length > 0) {
        autoTable(doc, {
          startY: doc.lastAutoTable.finalY + 8,
          head: [['Range', 'Loan Code', 'Client', 'Gender', 'Principal', 'Created', 'Due', 'Days Overdue', 'Status']],
          body: disbGenderRangesLoans.map((r) => [
            r.amount_range,
            loanRef(r) || r.loan_id,
            r.client_name,
            r.gender,
            r.principal_amount,
            r.created_at ? formatDate(r.created_at) : '-',
            r.due_date ? formatDate(r.due_date) : '-',
            r.days_overdue ?? '-',
            r.loan_status,
          ]),
          styles: { fontSize: 7, cellPadding: 1.5 },
        });
      }

      doc.save('disbursed_loans_by_gender_amount_ranges.pdf');
      return;
    }

    if (selectedReport === 'borrower_salaries_by_gender') {
      doc.text('Borrower salaries by gender', 14, 12);

      autoTable(doc, {
        startY: 18,
        head: [['Metric', 'Value']],
        body: [
          ['Total Borrowers', borrowerSalariesByGenderPivot.grand.totalCount],
          ['Total Salary', toCurrency(borrowerSalariesByGenderPivot.grand.totalSalary)],
        ],
      });

      const columns = borrowerSalariesByGenderPivot.columns;
      const head = [['Gender', ...columns, 'Total']];
      const body = borrowerSalariesByGenderPivot.tableRows.map((r) => {
        const cells = columns.map((col) => {
          const cell = r.cells[col] || { count: 0, total_salary: 0 };
          return `${cell.count} | ${toCurrency(cell.total_salary)}`;
        });
        return [r.gender, ...cells, `${r.totalCount} | ${toCurrency(r.totalSalary)}`];
      });

      if (borrowerSalariesByGenderPivot.tableRows.length > 0) {
        const totalRow = [
          'Total',
          ...columns.map((col) => {
            const t = borrowerSalariesByGenderPivot.columnTotals[col] || { count: 0, total_salary: 0 };
            return `${t.count} | ${toCurrency(t.total_salary)}`;
          }),
          `${borrowerSalariesByGenderPivot.grand.totalCount} | ${toCurrency(borrowerSalariesByGenderPivot.grand.totalSalary)}`,
        ];
        body.push(totalRow);
      }

      autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 8,
        head,
        body,
        styles: { fontSize: 8, cellPadding: 2 },
      });

      doc.save('borrower_salaries_by_gender.pdf');
      return;
    }

    if (selectedReport === 'number_of_clients') {
      doc.text('Total number of clients in book at last day of reporting period (Active Clients)', 14, 12);

      autoTable(doc, {
        startY: 18,
        head: [['Metric', 'Value']],
        body: [
          ['As Of', dmy(numberOfClients.filters?.endDate, '')],
          ['Active Clients', numberOfClients.totals.active_clients],
          ['Outstanding Loans', numberOfClients.totals.loan_count],
          ['Total Outstanding', numberOfClients.totals.total_outstanding],
        ],
      });

      autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 8,
        head: [['Loan Code', 'Client', 'Status', 'Created', 'Due Date', 'Days Overdue', 'Principal', 'Total Due', 'Paid', 'Outstanding']],
        body: (Array.isArray(numberOfClients.loans) ? numberOfClients.loans : []).map((r) => [
          loanRef(r) || r.loan_id,
          r.client_name,
          r.loan_status,
          r.created_at ? formatDate(r.created_at) : '-',
          r.due_date ? formatDate(r.due_date) : '-',
          r.days_overdue ?? '-',
          r.principal_amount,
          r.total_due,
          r.total_paid,
          r.outstanding,
        ]),
        styles: { fontSize: 7, cellPadding: 1.5 },
      });

      doc.save('total_number_of_clients_in_book_active_clients.pdf');
      return;
    }

    if (selectedReport === 'loans_outstanding_numbers') {
      doc.text('Number of loans outstanding', 14, 12);

      autoTable(doc, {
        startY: 18,
        head: [['Metric', 'Value']],
        body: [
          ['As Of', dmy(loansOutstandingNumbers.filters?.endDate, '')],
          ['Current Loans', loansOutstandingNumbers.current.count],
          ['Current Total Outstanding', loansOutstandingNumbers.current.total_outstanding],
          ['Loans in arrears', loansOutstandingNumbers.arrears.count],
          ['Arrears Total Outstanding', loansOutstandingNumbers.arrears.total_outstanding],
        ],
      });

      autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 8,
        head: [['Current loans (detail)', '', '', '', '', '', '', '']],
        body: [],
      });

      autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 4,
        head: [['Loan Code', 'Client', 'Status', 'Created', 'Due Date', 'Days Overdue', 'Paid', 'Outstanding']],
        body: (Array.isArray(loansOutstandingNumbers.currentLoans) ? loansOutstandingNumbers.currentLoans : []).map((r) => [
          loanRef(r) || r.loan_id,
          r.client_name,
          r.loan_status,
          r.created_at ? formatDate(r.created_at) : '-',
          r.due_date ? formatDate(r.due_date) : '-',
          r.days_overdue ?? '-',
          r.total_paid,
          r.outstanding,
        ]),
        styles: { fontSize: 7, cellPadding: 1.5 },
      });

      autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 8,
        head: [['Loans in arrears (detail)', '', '', '', '', '', '', '']],
        body: [],
      });

      autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 4,
        head: [['Loan Code', 'Client', 'Status', 'Created', 'Due Date', 'Days Overdue', 'Paid', 'Outstanding']],
        body: (Array.isArray(loansOutstandingNumbers.arrearsLoans) ? loansOutstandingNumbers.arrearsLoans : []).map((r) => [
          loanRef(r) || r.loan_id,
          r.client_name,
          r.loan_status,
          r.created_at ? formatDate(r.created_at) : '-',
          r.due_date ? formatDate(r.due_date) : '-',
          r.days_overdue ?? '-',
          r.total_paid,
          r.outstanding,
        ]),
        styles: { fontSize: 7, cellPadding: 1.5 },
      });

      doc.save('loans_outstanding_numbers.pdf');
      return;
    }

    if (selectedReport === 'disbursements_duration_breakdown') {
      doc.text('Loan disbursement breakdown (1–5 months)', 14, 12);

      autoTable(doc, {
        startY: 18,
        head: [['Duration (months)', 'Loan Count', 'Total Amount']],
        body: (Array.isArray(disbDurationBreakdown.rows) ? disbDurationBreakdown.rows : []).map((r) => [
          r.duration_months,
          r.loan_count,
          r.total_amount,
        ]),
      });

      if (Array.isArray(disbDurationBreakdownLoans) && disbDurationBreakdownLoans.length > 0) {
        autoTable(doc, {
          startY: doc.lastAutoTable.finalY + 8,
          head: [['Duration', 'Loan Code', 'Client', 'Gender', 'Principal', 'Disbursed', 'Status']],
          body: disbDurationBreakdownLoans.map((r) => [
            `${r.duration_months}m`,
            loanRef(r) || r.loan_id,
            r.client_name,
            r.gender,
            r.principal_amount,
            r.disbursement_date ? formatDate(r.disbursement_date) : '-',
            r.loan_status,
          ]),
          styles: { fontSize: 7, cellPadding: 1.5 },
        });
      }

      doc.save('disbursements_duration_breakdown.pdf');
      return;
    }

    if (selectedReport === 'overdue') {
      doc.text('Overdue Loans (Penalty Interest)', 14, 12);

      autoTable(doc, {
        startY: 18,
        head: [['Metric', 'Value']],
        body: [
          ['Loans Count', overdue.totals.count],
          ['Total Penalty Interest', overdue.totals.total_penalty_interest],
          ['Total Amount Due', overdue.totals.total_amount_due],
        ],
      });

      autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 8,
        head: [['Loan Code', 'Client', 'Created', 'Due Date', 'Days Overdue', 'Amount Due', 'Penalty Interest', 'Total Due', 'Status']],
        body: overdue.loans.map(r => [
          loanRef(r) || r.loan_id,
          r.client_name,
          r.created_at ? formatDate(r.created_at) : '-',
          formatDate(r.due_date),
          r.days_overdue,
          r.amount_due,
          r.overdue_interest,
          r.total_due ?? ((Number(r.amount_due) || 0) + (Number(r.overdue_interest) || 0)),
          r.loan_status,
        ]),
      });

      doc.save('overdue_loans_report.pdf');
      return;
    }

    if (selectedReport === 'arrears_breakdown') {
      doc.text('Loans in arrears (all buckets)', 14, 12);
      const asOfText = arrearsBreakdown?.asOfDate ? `As of: ${formatDate(arrearsBreakdown.asOfDate)}` : '';
      if (asOfText) doc.text(asOfText, 14, 18);

      const buckets = arrearsBreakdown?.buckets || {};
      let startY = asOfText ? 24 : 18;

      for (const bucketKey of ['30_60', '60_90', '90_120', 'gt_120']) {
        const label = arrearsBucketMeta?.[bucketKey]?.label || `Arrears ${bucketKey}`;
        const bucket = buckets?.[bucketKey] || { totals: { count: 0, total_outstanding: 0 }, loans: [] };

        doc.text(label, 14, startY);

        autoTable(doc, {
          startY: startY + 4,
          head: [['Metric', 'Value']],
          body: [
            ['Loans Count', bucket.totals?.count || 0],
            ['Total Outstanding', bucket.totals?.total_outstanding || 0],
          ],
        });

        autoTable(doc, {
          startY: doc.lastAutoTable.finalY + 6,
          head: [['Loan Code', 'Client', 'Status', 'Created', 'Due Date', 'Days Overdue', 'Total Due', 'Paid', 'Outstanding']],
          body: (bucket.loans || []).map(r => [
            loanRef(r) || r.loan_id,
            r.client_name,
            r.loan_status,
            r.created_at ? formatDate(r.created_at) : '-',
            r.due_date ? formatDate(r.due_date) : '-',
            r.days_overdue ?? '-',
            r.total_due,
            r.total_paid,
            r.outstanding,
          ]),
          styles: { fontSize: 7, cellPadding: 1.5 },
        });

        startY = doc.lastAutoTable.finalY + 12;
        if (startY > 260) {
          doc.addPage();
          startY = 18;
        }
      }

      doc.save('arrears_breakdown.pdf');
      return;
    }

    if (selectedReport === 'defaultInterest') {
      doc.text('Default Interest', 14, 12);

      autoTable(doc, {
        startY: 18,
        head: [['Metric', 'Value']],
        body: [
          ['Items Count', defaultInterest.totals.count],
          ['Total Default Interest', defaultInterest.totals.total_default_interest],
          ['Total Amount Due', defaultInterest.totals.total_amount_due],
        ],
      });

      autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 8,
        head: [['Month', 'Count', 'Total Default Interest']],
        body: defaultInterest.byMonth.map(r => [r.month, r.count, r.total_default_interest]),
      });

      autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 8,
        head: [['Loan Code', 'Client', 'Created', 'Due Date', 'Days Overdue', 'Default Interest', 'Amount Due', 'Total Due', 'Loan Status']],
        body: defaultInterest.items.map(r => [
          loanRef(r) || r.loan_id,
          r.client_name,
          r.created_at ? formatDate(r.created_at) : '-',
          formatDate(r.due_date),
          r.days_overdue,
          r.default_interest,
          r.amount_due,
          r.total_due,
          r.loan_status,
        ]),
      });

      doc.save('default_interest_report.pdf');
      return;
    }

    if (selectedReport === 'unpaid') {
      doc.text('Unpaid Loans', 14, 12);

      autoTable(doc, {
        startY: 18,
        head: [['Metric', 'Value']],
        body: [
          ['Loans Count', unpaid.totals.count],
          ['Total Outstanding', unpaid.totals.total_outstanding],
        ],
      });

      autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 8,
        head: [['Loan Code', 'Client', 'Status', 'Created', 'Due Date', 'Days Overdue', 'Total Due', 'Paid', 'Outstanding']],
        body: unpaid.loans.map(r => [
          loanRef(r) || r.loan_id,
          r.client_name,
          r.loan_status,
          formatDate(r.created_at),
          r.due_date ? formatDate(r.due_date) : '-',
          r.days_overdue ?? '-',
          r.total_due,
          r.total_paid,
          r.outstanding,
        ]),
      });

      doc.save('unpaid_loans_report.pdf');
      return;
    }

    if (selectedReport === 'repayment_methods') {
      doc.text('Repayment Methods', 14, 12);

      const renderCategory = (category, title) => {
        const rep = repaymentMethods?.[category] || EMPTY_REPAYMENTS;

        autoTable(doc, {
          startY: doc.lastAutoTable ? (doc.lastAutoTable.finalY + 8) : 18,
          head: [[title, '']],
          body: [
            ['Payments Count', rep.totals?.count ?? 0],
            ['Total Paid', rep.totals?.total_paid ?? 0],
          ],
          styles: { fontSize: 9 },
          headStyles: { fillColor: [240, 240, 240], textColor: 20 },
        });

        if (Array.isArray(rep.byMonth) && rep.byMonth.length > 0) {
          autoTable(doc, {
            startY: doc.lastAutoTable.finalY + 6,
            head: [['Month', 'Count', 'Total Paid']],
            body: rep.byMonth.map(r => [r.month, r.count, r.total_paid]),
            styles: { fontSize: 8 },
          });
        }

        autoTable(doc, {
          startY: doc.lastAutoTable.finalY + 6,
          head: [['Repayment ID', 'Loan Code', 'Client', 'Loan Created', 'Due Date', 'Days Overdue', 'Payment Date', 'Method', 'Reference', 'Amount Paid']],
          body: (Array.isArray(rep.items) ? rep.items : []).map(r => [
            r.repayment_id,
            loanRef(r) || r.loan_id,
            r.client_name,
            r.created_at ? formatDate(r.created_at) : '-',
            r.due_date ? formatDate(r.due_date) : '-',
            r.days_overdue ?? '-',
            r.payment_date ? formatDate(r.payment_date) : '-',
            r.payment_method,
            r.payment_reference || '',
            r.amount_paid,
          ]),
          styles: { fontSize: 7, cellPadding: 1.5 },
        });
      };

      renderCategory('debit', 'Debit Order');
      renderCategory('cash', 'Cash');
      renderCategory('other', 'Other');

      doc.save('repayment_methods.pdf');
      return;
    }

    if (selectedReport === 'provision_bad_debts_movement') {
      doc.text('Movement in Provision for Bad Debts', 14, 12);

      const m = provisionMovement?.movement || {};

      autoTable(doc, {
        startY: 18,
        head: [['Metric', 'Value']],
        body: [
          ['Start Date', dmy(provisionMovement.filters?.startDate, '')],
          ['End Date', dmy(provisionMovement.filters?.endDate, '')],
          ['Provision Rate', `${Number(provisionMovement.filters?.provisionRate || 0) * 100}%`],
          ['Provision for bad debts beginning of period', m.opening_provision ?? 0],
          ['Loans written-off during the period', m.loans_written_off ?? 0],
          ['Provision for bad debts during the period', m.provision_during_period ?? 0],
          ['Written-off count', m.written_off_count ?? 0],
        ],
      });

      autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 8,
        head: [['Loan Code', 'Client', 'Status', 'Created', 'Due Date', 'Write-off Date', 'Principal Written-off']],
        body: (provisionMovement.writtenOffLoans || []).map(r => [
          loanRef(r) || r.loan_id,
          r.client_name,
          r.loan_status,
          r.created_at ? formatDate(r.created_at) : '-',
          r.due_date ? formatDate(r.due_date) : '-',
          r.writeoff_date ? formatDate(r.writeoff_date) : '-',
          r.principal_amount,
        ]),
      });

      doc.save('provision_bad_debts_movement.pdf');
      return;
    }

    if (selectedReport === 'interest_outstanding_quarter') {
      doc.text('Interest Charged on loans outstanding (end of quarter)', 14, 12);

      autoTable(doc, {
        startY: 18,
        head: [['Metric', 'Value']],
        body: [
          ['Year', quarterYear],
          ['Quarter', quarter],
          ['Loans Count', interestQuarter.totals.count],
          ['Total Outstanding (End)', interestQuarter.totals.total_outstanding_at_end],
          ['Total Interest Charged', interestQuarter.totals.total_interest_charged],
        ],
      });

      autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 8,
        head: [['Loan Code', 'Client', 'Status', 'Created', 'Due Date', 'Days Overdue', 'Outstanding (End)', 'Interest Charged']],
        body: interestQuarter.loans.map(r => [
          loanRef(r) || r.loan_id,
          r.client_name,
          r.loan_status,
          r.created_at ? formatDate(r.created_at) : '-',
          r.due_date ? formatDate(r.due_date) : '-',
          r.days_overdue ?? '-',
          r.outstanding_at_end,
          r.interest_charged,
        ]),
      });

      doc.save('interest_outstanding_quarter.pdf');
      return;
    }

    if (selectedReport === 'interest') {
      doc.text('Interest on Loans and Advances - Micro lenders', 14, 12);

      autoTable(doc, {
        startY: 18,
        head: [['Metric', 'Value']],
        body: [
          ['Loans Count', interest.totals.count],
          ['Total Principal', interest.totals.total_principal],
          ['Total Interest', interest.totals.total_interest],
          ['Total Repayable', interest.totals.total_repayable],
        ],
      });

      autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 8,
        head: [['Month', 'Count', 'Total Interest']],
        body: interest.byMonth.map(r => [r.month, r.count, r.total_interest]),
      });

      autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 8,
        head: [['Loan Code', 'Client', 'Status', 'Created', 'Due Date', 'Days Overdue', 'Interest Rate', 'Principal', 'Interest', 'Total Repayable']],
        body: interest.loans.map(r => [
          loanRef(r) || r.loan_id,
          r.client_name,
          r.loan_status,
          formatDate(r.created_at),
          r.due_date ? formatDate(r.due_date) : '-',
          r.days_overdue ?? '-',
          `${r.interest_rate}%`,
          r.principal_amount,
          r.interest_amount,
          r.total_repayable,
        ]),
      });

      doc.save('interest_on_loans_report.pdf');
    }

    if (selectedReport === 'microlender_monthly_management') {
      if (!monthlyManagement) {
        setMonthlyManagementError('Generate the Monthly Management Report first.');
        return;
      }

      const monthReports = Array.isArray(monthlyManagement?.months)
        ? monthlyManagement.months
        : (monthlyManagement?.period?.month && monthlyManagement?.period?.year)
          ? [monthlyManagement]
          : [];

      if (monthReports.length === 0) {
        setMonthlyManagementError('Generate the Monthly Management Report first.');
        return;
      }

      const fileSuffix = (monthlyManagement?.quarter && monthlyManagement?.year)
        ? `Q${monthlyManagement.quarter}_${monthlyManagement.year}`
        : 'custom';

      const qKey = (monthlyManagement?.year && monthlyManagement?.quarter)
        ? mmQuarterKey(monthlyManagement.year, monthlyManagement.quarter)
        : '';
      // Read the live inputs so a commentary written moments ago is included.
      const qInputs = { ...EMPTY_MM_QUARTER_INPUTS, ...(quarterlyInputsRef.current?.[qKey] || getMmQuarterInputs(qKey)) };
      const company = monthlyManagement?.company || {};
      const namfisaRegNo = company?.namfisaRegNo || '';
      const companyName = String(company?.name || '').trim();
      const quarterTag = (monthlyManagement?.quarter && monthlyManagement?.year)
        ? `Q${monthlyManagement.quarter} ${monthlyManagement.year}`
        : '';

      // House style shared by every page after the cover.
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const centerX = pageWidth / 2;
      const NAVY = [30, 58, 138];
      const BLUE = [37, 99, 235];
      const SKY = [14, 165, 233];
      const INK = [31, 41, 55];
      const MUTED = [107, 114, 128];
      const TINT = [238, 242, 255];
      const ZEBRA = [246, 248, 252];
      const RULE = [219, 228, 245];
      const TOTAL_FILL = [226, 232, 246];
      const MARGIN_X = 14;
      const CONTENT_W = pageWidth - MARGIN_X * 2;
      const TOP_Y = 26; // below the running header
      const BOTTOM_GAP = 20; // above the running footer

      const resetInk = () => {
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...INK);
        doc.setDrawColor(0, 0, 0);
        doc.setLineWidth(0.2);
      };

      // Start a new page when `needed` mm will not fit below y.
      const ensureSpace = (y, needed) => {
        if (y + needed > pageHeight - BOTTOM_GAP) {
          doc.addPage();
          return TOP_Y;
        }
        return y;
      };

      // "3. Arrears / aging analysis - July 2026": tinted band with a navy
      // accent bar. Keeps room for the table header and a couple of rows so a
      // heading never sits alone at the bottom of a page.
      const sectionHeading = (title, y, { keepWith = 26 } = {}) => {
        const top = ensureSpace(y, 10 + keepWith);
        doc.setFillColor(...TINT);
        doc.rect(MARGIN_X, top, CONTENT_W, 8.5, 'F');
        doc.setFillColor(...NAVY);
        doc.rect(MARGIN_X, top, 1.6, 8.5, 'F');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(10.5);
        doc.setTextColor(...NAVY);
        doc.text(String(title), MARGIN_X + 5, top + 5.8);
        resetInk();
        return top + 11;
      };

      // Navy band that opens each month / the quarter summary, with its period.
      const periodDivider = (title, start, end, y) => {
        doc.setFillColor(...NAVY);
        doc.rect(MARGIN_X, y, CONTENT_W, 15, 'F');
        doc.setFillColor(...SKY);
        doc.rect(MARGIN_X, y + 15, CONTENT_W, 1.2, 'F');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(15);
        doc.setTextColor(255, 255, 255);
        doc.text(String(title), MARGIN_X + 5, y + 9.8);
        if (start || end) {
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(9);
          doc.text(`Period: ${dmy(start, '')} - ${dmy(end, '')}`, pageWidth - MARGIN_X - 5, y + 9.8, { align: 'right' });
        }
        resetInk();
        return y + 23;
      };

      // One table style for the whole report: navy header with white bold
      // text, zebra rows, right-aligned figures, bold totals with a top rule.
      //   numeric: column indexes holding figures (right-aligned)
      //   totals: body row indexes that are totals
      //   textRows: body row indexes whose value is prose (left-aligned)
      const mmTable = ({ startY, head, body, widths, numeric = [], totals = [], textRows = [] }) => {
        const columnStyles = {};
        (widths || []).forEach((w, i) => {
          columnStyles[i] = { cellWidth: w, halign: numeric.includes(i) ? 'right' : 'left' };
        });
        autoTable(doc, {
          startY,
          head: head ? [head] : undefined,
          body,
          theme: 'plain',
          margin: { left: MARGIN_X, right: MARGIN_X, top: TOP_Y, bottom: BOTTOM_GAP },
          tableWidth: CONTENT_W,
          showHead: 'everyPage',
          rowPageBreak: 'avoid',
          styles: {
            font: 'helvetica',
            fontSize: 9,
            textColor: INK,
            cellPadding: { top: 2.4, bottom: 2.4, left: 3, right: 3 },
            lineColor: RULE,
            lineWidth: { bottom: 0.1 },
            valign: 'middle',
            overflow: 'linebreak',
          },
          headStyles: { fillColor: NAVY, textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 9, lineWidth: 0 },
          alternateRowStyles: { fillColor: ZEBRA },
          columnStyles,
          didParseCell: (data) => {
            if (data.section === 'head' && numeric.includes(data.column.index)) data.cell.styles.halign = 'right';
            if (data.section !== 'body') return;
            if (textRows.includes(data.row.index) && data.column.index > 0) data.cell.styles.halign = 'left';
            if (totals.includes(data.row.index)) {
              data.cell.styles.fontStyle = 'bold';
              data.cell.styles.fillColor = TOTAL_FILL;
            }
          },
          didDrawCell: (data) => {
            if (data.section === 'body' && totals.includes(data.row.index)) {
              doc.setDrawColor(...NAVY);
              doc.setLineWidth(0.4);
              doc.line(data.cell.x, data.cell.y, data.cell.x + data.cell.width, data.cell.y);
            }
          },
        });
        resetInk();
        return doc.lastAutoTable.finalY;
      };

      // A paragraph of narrative in a light panel (breaks across pages).
      const proseBlock = (text, startY) => {
        autoTable(doc, {
          startY,
          body: [[String(text || '').trim() || '-']],
          theme: 'plain',
          margin: { left: MARGIN_X, right: MARGIN_X, top: TOP_Y, bottom: BOTTOM_GAP },
          tableWidth: CONTENT_W,
          styles: {
            font: 'helvetica',
            fontSize: 9.5,
            textColor: INK,
            cellPadding: { top: 4, bottom: 4, left: 5, right: 5 },
            fillColor: [250, 251, 254],
            lineColor: RULE,
            lineWidth: 0.2,
            overflow: 'linebreak',
          },
        });
        resetInk();
        return doc.lastAutoTable.finalY;
      };

      // Major risk issues as a small register: each "- Credit: ..." line becomes
      // a row with its category (colour-marked) beside the issue and the
      // action. Text that is not in that shape is shown as a paragraph.
      const RISK_COLORS = {
        credit: [220, 38, 38],
        liquidity: [217, 119, 6],
        operational: [37, 99, 235],
        compliance: [124, 58, 237],
      };
      const riskBlock = (text, startY) => {
        let y = ensureSpace(startY + 4, 22);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9.5);
        doc.setTextColor(...NAVY);
        doc.text('Major risk issues', MARGIN_X, y + 3);
        resetInk();
        y += 5;
        const lines = String(text || '').split(/\r?\n/).map((l) => l.replace(/^\s*(?:[-•*]|\d+[.)])\s*/, '').trim()).filter(Boolean);
        const rows = lines.map((l) => {
          const m = l.match(/^([A-Za-z][A-Za-z &/-]{1,28}):\s*(.+)$/);
          return m ? [m[1].trim(), m[2].trim()] : ['', l];
        });
        if (!rows.length || rows.every((r) => !r[0])) return proseBlock(text, y);
        autoTable(doc, {
          startY: y,
          body: rows,
          theme: 'plain',
          margin: { left: MARGIN_X, right: MARGIN_X, top: TOP_Y, bottom: BOTTOM_GAP },
          tableWidth: CONTENT_W,
          rowPageBreak: 'avoid',
          styles: {
            font: 'helvetica',
            fontSize: 9,
            textColor: INK,
            cellPadding: { top: 3, bottom: 3, left: 4, right: 4 },
            lineColor: RULE,
            lineWidth: { bottom: 0.1 },
            valign: 'top',
            overflow: 'linebreak',
          },
          alternateRowStyles: { fillColor: ZEBRA },
          columnStyles: {
            0: { cellWidth: 32, fontStyle: 'bold', cellPadding: { top: 3, bottom: 3, left: 5, right: 3 } },
            1: { cellWidth: CONTENT_W - 32 },
          },
          didParseCell: (data) => {
            if (data.section !== 'body' || data.column.index !== 0) return;
            data.cell.styles.textColor = RISK_COLORS[String(data.cell.raw || '').toLowerCase()] || NAVY;
          },
          didDrawCell: (data) => {
            if (data.section !== 'body' || data.column.index !== 0) return;
            doc.setFillColor(...(RISK_COLORS[String(data.cell.raw || '').toLowerCase()] || NAVY));
            doc.rect(data.cell.x, data.cell.y, 1.4, data.cell.height, 'F');
          },
        });
        resetInk();
        return doc.lastAutoTable.finalY;
      };

      const W2 = [CONTENT_W - 62, 62]; // label | value
      const W3 = [CONTENT_W - 100, 50, 50]; // label | figure | figure

      // Cover page: accent bands, logo, company, title, quarter and a details panel.
      try {
        const centered = (text, y, { size = 10, style = 'normal', color = INK, gap } = {}) => {
          if (!text) return y;
          doc.setFont('helvetica', style);
          doc.setFontSize(size);
          doc.setTextColor(...color);
          const lines = doc.splitTextToSize(String(text), pageWidth - 40);
          doc.text(lines, centerX, y, { align: 'center' });
          return y + lines.length * (gap || size * 0.45);
        };

        // Top accent band
        doc.setFillColor(...NAVY);
        doc.rect(0, 0, pageWidth, 16, 'F');
        doc.setFillColor(...SKY);
        doc.rect(0, 16, pageWidth, 2, 'F');

        let y = 34;
        // Logo, at most 70 x 32 mm, aspect ratio kept
        if (mmLogo?.png && mmLogo.w && mmLogo.h) {
          try {
            const maxW = 70;
            const maxH = 32;
            const scale = Math.min(maxW / mmLogo.w, maxH / mmLogo.h);
            const w = mmLogo.w * scale;
            const h = mmLogo.h * scale;
            doc.addImage(mmLogo.png, 'PNG', centerX - w / 2, y, w, h);
            y += h + 10;
          } catch (_) { /* no logo */ }
        }

        y = centered(String(company?.name || '-').toUpperCase(), y + 4, { size: 16, style: 'bold', color: INK, gap: 7 });
        if (company?.address) y = centered(company.address, y + 1, { size: 9, color: MUTED });
        const contact = [company?.phoneNumber, company?.email].filter(Boolean).join('   ·   ');
        if (contact) y = centered(contact, y + 1, { size: 9, color: MUTED });

        // Short divider
        y += 10;
        doc.setDrawColor(...BLUE);
        doc.setLineWidth(0.8);
        doc.line(centerX - 12, y, centerX + 12, y);

        y = centered('Monthly Management Report', y + 18, { size: 26, style: 'bold', color: NAVY, gap: 11 });
        const quarterLine = mmQuarterTitle(monthlyManagement?.year, monthlyManagement?.quarter).replace('–', '-');
        if (quarterLine) y = centered(quarterLine, y + 2, { size: 14, color: INK, gap: 7 });

        // Details panel
        const qStart = monthlyManagement?.quarterStartDate || monthlyManagement?.qtd?.period?.startDate || '';
        const qEnd = monthlyManagement?.quarterEndDate || monthlyManagement?.qtd?.period?.endDate || '';
        const preparedBy = mmPreparedBy(qInputs);
        const details = [
          ['NAMFISA licence no.', namfisaRegNo || '-'],
          ['Reporting period', (qStart || qEnd) ? `${formatDate(qStart)} to ${formatDate(qEnd)}` : '-'],
          ['Prepared by', preparedBy ? (preparedBy === String(company?.principalOfficerName || '').trim() ? `${preparedBy} (Principal Officer)` : preparedBy) : '-'],
          ['Approved by', String(qInputs.approvedBy || '').trim() || '-'],
          ['Date prepared', formatDate(new Date())],
        ];
        const panelW = 130;
        const panelX = centerX - panelW / 2;
        const rowH = 9;
        const panelY = Math.max(y + 16, 150);
        const panelH = details.length * rowH + 10;
        doc.setFillColor(243, 246, 252);
        doc.setDrawColor(219, 228, 245);
        doc.setLineWidth(0.3);
        doc.roundedRect(panelX, panelY, panelW, panelH, 3, 3, 'FD');
        doc.setFillColor(...BLUE);
        doc.rect(panelX, panelY + 3, 1.2, panelH - 6, 'F');
        details.forEach(([label, value], i) => {
          const ry = panelY + 10 + i * rowH;
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(8);
          doc.setTextColor(...MUTED);
          doc.text(label.toUpperCase(), panelX + 8, ry);
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(10);
          doc.setTextColor(...INK);
          const v = doc.splitTextToSize(String(value), panelW - 60)[0] || '';
          doc.text(v, panelX + 52, ry);
        });

        // Bottom accent band
        doc.setFillColor(...SKY);
        doc.rect(0, pageHeight - 14, pageWidth, 1.5, 'F');
        doc.setFillColor(...NAVY);
        doc.rect(0, pageHeight - 12.5, pageWidth, 12.5, 'F');
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(255, 255, 255);
        doc.text('CONFIDENTIAL  ·  PREPARED FOR THE BOARD AND NAMFISA', centerX, pageHeight - 5, { align: 'center' });
      } catch (e) {
        // ignore cover page failures
      }
      resetInk();

      monthReports.forEach((mr) => {
        const reportMonth = mr?.period?.month;
        const reportYear = mr?.period?.year;
        const key = mmKey(reportYear, reportMonth);
        const inputs = getMmInputs(key);

        const derived = getMmDerived(key, mr);

        const income = {
          interestIncome: derived.income.interest_income,
          feesIncome: derived.income.fees_income,
          defaultInterest: derived.income.default_interest,
          badDebtsRecovered: derived.income.bad_debts_recovered,
          otherIncome: derived.income.other_income,
          operatingExpenses: derived.income.operating_expenses,
          impairmentCharge: derived.income.impairment_charge,
        };
        const netProfit = derived.income.net_profit;

        const provisions = {
          specific: derived.provisions.specific,
          general: derived.provisions.general,
        };
        const totalImpairment = derived.provisions.total_impairment;

        const netLoanBook = derived.loan_book.net;

        const cap = {
          paidUp: derived.capital.paid_up,
          retained: derived.capital.retained,
          requiredMin: derived.capital.required_minimum,
        };
        const totalEquity = derived.capital.total_equity;
        const surplusShortfall = derived.capital.surplus_shortfall;

        const periodLabel = `${monthOptions.find(o => Number(o.value) === Number(reportMonth))?.label || reportMonth} ${reportYear}`;

        const totalBookValue = Number(mr?.loanBook?.closing?.total_outstanding || 0);
        const activeLoans = Number(mr?.loanBook?.closing?.count || 0);
        const arrearsRatio = mr?.arrears?.arrearsRatioByAmount || 0;

        const unsecuredCount = derived.new_loans.unsecured.count;
        const unsecuredAmt = derived.new_loans.unsecured.amount;

        // Each month starts on its own page, after the cover.
        doc.addPage();
        let y = periodDivider(periodLabel, mr?.period?.startDate, mr?.period?.endDate, TOP_Y);
        const h = (n, title) => `${n}. ${title} - ${periodLabel}`;

        y = sectionHeading(h(1, 'Executive summary'), y);
        y = mmTable({
          startY: y,
          head: ['Item', 'Value'],
          body: [
            ['Total loan book value (closing)', toCurrency(totalBookValue)],
            ['Total number of active loans (closing)', activeLoans],
            ['Arrears ratio (by amount)', mmPercent(arrearsRatio)],
            ['Net profit / loss', toCurrency(netProfit)],
            ['Capital position (total equity)', toCurrency(totalEquity)],
          ],
          widths: W2,
          numeric: [1],
        });
        y = riskBlock(inputs.majorRiskIssues, y);

        y = sectionHeading(h(2, 'Loan portfolio summary'), y + 8);
        y = mmTable({
          startY: y,
          head: ['Item', 'Amount (N$)', 'Number of loans'],
          body: [
            ['Opening loan book', toCurrency(mr?.loanBook?.opening?.total_outstanding || 0), mr?.loanBook?.opening?.count || 0],
            ['New loans issued', toCurrency(mr?.loanBook?.newLoans?.total_principal || 0), mr?.loanBook?.newLoans?.count || 0],
            ['Loans settled', toCurrency(mr?.loanBook?.settled?.total_settled || 0), mr?.loanBook?.settled?.count || 0],
            ['Written off', toCurrency(mr?.loanBook?.writtenOff?.total || 0), mr?.loanBook?.writtenOff?.count || 0],
            ['Repayments & other movements', toCurrency(mr?.loanBook?.otherMovements?.total || 0), mr?.loanBook?.otherMovements?.count || 0],
            ['Closing loan book', toCurrency(mr?.loanBook?.closing?.total_outstanding || 0), mr?.loanBook?.closing?.count || 0],
            ['Unsecured loans', toCurrency(unsecuredAmt), unsecuredCount],
            ['Secured loans', toCurrency(mmNumber(inputs.securedLoansAmount)), mmNumber(inputs.securedLoansCount)],
          ],
          widths: W3,
          numeric: [1, 2],
          totals: [5],
        });

        y = sectionHeading(h(3, 'Arrears / aging analysis'), y + 8);
        y = mmTable({
          startY: y,
          head: ['Bucket', 'Number of loans', 'Outstanding (N$)'],
          body: [
            ['30–60 days', mr?.arrears?.buckets?.['30_60']?.count || 0, toCurrency(mr?.arrears?.buckets?.['30_60']?.outstanding || 0)],
            ['60–90 days', mr?.arrears?.buckets?.['60_90']?.count || 0, toCurrency(mr?.arrears?.buckets?.['60_90']?.outstanding || 0)],
            ['90–120 days', mr?.arrears?.buckets?.['90_120']?.count || 0, toCurrency(mr?.arrears?.buckets?.['90_120']?.outstanding || 0)],
            ['Over 120 days', mr?.arrears?.buckets?.['gt_120']?.count || 0, toCurrency(mr?.arrears?.buckets?.['gt_120']?.outstanding || 0)],
            ['Total in arrears', mr?.arrears?.totals?.count || 0, toCurrency(mr?.arrears?.totals?.outstanding || 0)],
            ['Arrears ratio', '', mmPercent(arrearsRatio)],
            ['NPL 90+ ratio (by amount)', '', mmPercent(mr?.arrears?.npl90?.ratioByAmount || 0)],
          ],
          widths: W3,
          numeric: [1, 2],
          totals: [4],
        });

        y = sectionHeading(h(4, 'Income statement summary'), y + 8);
        y = mmTable({
          startY: y,
          head: ['Item', 'Amount (N$)'],
          body: [
            ['Interest income', toCurrency(income.interestIncome)],
            ['Fees income', toCurrency(income.feesIncome)],
            ['Default interest', toCurrency(income.defaultInterest)],
            ['Bad debts recovered', toCurrency(income.badDebtsRecovered)],
            ['Other income', toCurrency(income.otherIncome)],
            ['Operating expenses', toCurrency(income.operatingExpenses)],
            ['Impairment charge', toCurrency(income.impairmentCharge)],
            ['Net profit / loss', toCurrency(netProfit)],
          ],
          widths: W2,
          numeric: [1],
          totals: [7],
        });

        y = sectionHeading(h(5, 'Balance sheet summary'), y + 8);
        y = mmTable({
          startY: y,
          head: ['Item', 'Amount (N$)'],
          body: [
            ['Cash and bank balances', toCurrency(mmNumber(inputs.cashAndBankBalances))],
            ['Loan book (net of provisions)', toCurrency(netLoanBook)],
            ['Other assets', toCurrency(mmNumber(inputs.otherAssets))],
            ['Total liabilities', toCurrency(mmNumber(inputs.totalLiabilities))],
            ['Shareholder equity', toCurrency(mmNumber(inputs.shareholderEquity))],
          ],
          widths: W2,
          numeric: [1],
        });

        y = sectionHeading(h(6, 'Capital adequacy position'), y + 8);
        y = mmTable({
          startY: y,
          head: ['Item', 'Amount (N$)'],
          body: [
            ['Paid-up share capital', toCurrency(cap.paidUp)],
            ['Retained earnings', toCurrency(cap.retained)],
            ['Total equity', toCurrency(totalEquity)],
            ['Required minimum capital', toCurrency(cap.requiredMin)],
            ['Surplus / shortfall', toCurrency(surplusShortfall)],
          ],
          widths: W2,
          numeric: [1],
          totals: [2, 4],
        });

        y = sectionHeading(h(7, 'Liquidity position'), y + 8);
        y = mmTable({
          startY: y,
          head: ['Item', 'Amount (N$)'],
          body: [
            ['Cash on hand', toCurrency(mmNumber(inputs.cashOnHand))],
            ['Monthly expenses', toCurrency(mmNumber(inputs.monthlyExpenses))],
            ['Short-term liabilities', toCurrency(mmNumber(inputs.shortTermLiabilities))],
          ],
          widths: W2,
          numeric: [1],
        });

        y = sectionHeading(h(8, 'Provision for bad debts'), y + 8);
        y = mmTable({
          startY: y,
          head: ['Item', 'Amount (N$)'],
          body: [
            ['Specific provisions (90+ days)', toCurrency(provisions.specific)],
            ['General provision', toCurrency(provisions.general)],
            ['Total impairment', toCurrency(totalImpairment)],
          ],
          widths: W2,
          numeric: [1],
          totals: [2],
        });

        y = sectionHeading(h(9, 'Consumer protection & compliance'), y + 8);
        y = mmTable({
          startY: y,
          head: ['Item', 'Number'],
          body: [
            ['Number of complaints received', mr?.compliance?.complaintsReceived ?? 0],
            ['Complaints resolved', mr?.compliance?.complaintsResolved ?? 0],
            ['Court cases', mr?.compliance?.courtCases ?? 0],
            ['Regulatory breaches', mr?.compliance?.regulatoryBreaches ?? 0],
            ['Any overcharging issues', mr?.compliance?.overchargingIssues ?? 0],
          ],
          widths: W2,
          numeric: [1],
        });

        y = sectionHeading(h(10, 'Management commentary'), y + 8, { keepWith: 16 });
        proseBlock(inputs.managementCommentary, y);
      });

      // Quarter-end / quarter-to-date summary, then the sign-off.
      if (monthlyManagement?.year && monthlyManagement?.quarter) {
        const months = Array.isArray(monthReports) ? monthReports : [];
        const last = months.length ? months[months.length - 1] : null;
        const qStart = monthlyManagement?.quarterStartDate || monthlyManagement?.qtd?.period?.startDate || '';
        const qEnd = monthlyManagement?.quarterEndDate || monthlyManagement?.qtd?.period?.endDate || '';

        const qtdInterestIncome = Number(monthlyManagement?.qtd?.income?.interest_income || 0);
        const qtdFeesIncome = Number(monthlyManagement?.qtd?.income?.fees_income || 0);
        const qtdBadDebtsRecovered = Number(monthlyManagement?.qtd?.income?.bad_debts_recovered || 0);
        const qtdImpairmentCharge = Number(monthlyManagement?.qtd?.income?.impairment_charge || 0);
        const qtdWrittenOff = Number(monthlyManagement?.qtd?.income?.written_off || 0);

        const qtdNewLoansCount = Number(monthlyManagement?.qtd?.newLoans?.count || 0);
        const qtdNewLoansAmt = Number(monthlyManagement?.qtd?.newLoans?.total_principal || 0);

        const qtdRepaymentsCount = Number(monthlyManagement?.qtd?.repayments?.txn_count || 0);
        const qtdRepaymentsAmt = Number(monthlyManagement?.qtd?.repayments?.total_paid || 0);

        const qtdDefaultInterestCount = Number(monthlyManagement?.qtd?.defaultInterest?.count || 0);
        const qtdDefaultInterestAmt = Number(monthlyManagement?.qtd?.defaultInterest?.total_default_interest || 0);

        const qeTotalBookValue = Number(last?.loanBook?.closing?.total_outstanding || 0);
        const qeActiveLoans = Number(last?.loanBook?.closing?.count || 0);
        const qeArrearsRatio = Number(last?.arrears?.arrearsRatioByAmount || 0);
        const qeNpl90Ratio = Number(last?.arrears?.npl90?.ratioByAmount || 0);

        doc.addPage();
        let y = periodDivider(`Quarter summary - ${quarterTag}`, qStart, qEnd, TOP_Y);
        const qh = (n, title) => `${n}. ${title} - ${quarterTag}`;

        // Company, licence and sign-off are on the cover and the last page.
        y = sectionHeading(qh(1, 'Quarter-end snapshot'), y);
        y = mmTable({
          startY: y,
          head: ['Item', 'Value'],
          body: [
            ['Closing loan book value', toCurrency(qeTotalBookValue)],
            ['Active loans (closing)', qeActiveLoans],
            ['Arrears ratio (by amount)', mmPercent(qeArrearsRatio)],
            ['NPL 90+ ratio (by amount)', mmPercent(qeNpl90Ratio)],
          ],
          widths: W2,
          numeric: [1],
        });

        y = sectionHeading(qh(2, 'Quarter-to-date totals'), y + 8);
        y = mmTable({
          startY: y,
          head: ['Item', 'Value'],
          body: [
            ['New loans issued (count)', qtdNewLoansCount],
            ['New loans issued (amount)', toCurrency(qtdNewLoansAmt)],
            ['Repayments (transactions)', qtdRepaymentsCount],
            ['Repayments (amount)', toCurrency(qtdRepaymentsAmt)],
            ['Default/penalty interest items', qtdDefaultInterestCount],
            ['Default/penalty interest (amount)', toCurrency(qtdDefaultInterestAmt)],
            ['Interest income (sum of monthly)', toCurrency(qtdInterestIncome)],
            ['Fees income (sum of monthly)', toCurrency(qtdFeesIncome)],
            ['Bad debts recovered (sum of monthly)', toCurrency(qtdBadDebtsRecovered)],
            ['Loans written off (sum of monthly)', toCurrency(qtdWrittenOff)],
            ['Impairment charge (sum of monthly)', toCurrency(qtdImpairmentCharge)],
          ],
          widths: W2,
          numeric: [1],
        });

        y = sectionHeading(qh(3, 'Management commentary'), y + 8, { keepWith: 16 });
        y = proseBlock(qInputs.quarterCommentary, y);

        y = sectionHeading(qh(4, 'Actions / remediation'), y + 8, { keepWith: 16 });
        proseBlock(qInputs.quarterActions, y);
      }

      // Sign-off: once, at the end of the report, on the last page.
      try {
        const signOffH = 84;
        let y = (doc.lastAutoTable?.finalY || TOP_Y) + 14;
        y = ensureSpace(y, signOffH);

        // Light rule, then the heading and the company.
        doc.setDrawColor(...RULE);
        doc.setLineWidth(0.4);
        doc.line(MARGIN_X, y, pageWidth - MARGIN_X, y);
        y += 9;
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(12);
        doc.setTextColor(...NAVY);
        doc.text('Sign-off', MARGIN_X, y);
        if (companyName) {
          doc.setFontSize(10);
          doc.setTextColor(...INK);
          doc.text(companyName, pageWidth - MARGIN_X, y, { align: 'right' });
        }
        y += 8;

        const signedOn = formatDateDMY(new Date());
        const colGap = 10;
        const colW = (CONTENT_W - colGap) / 2;
        const boxH = 56;
        const blocks = [
          { role: 'Prepared by', name: mmPreparedBy(qInputs), title: 'Principal Officer' },
          { role: 'Approved by', name: String(qInputs.approvedBy || '').trim(), title: 'Authorised Signatory' },
        ];
        blocks.forEach((b, i) => {
          const x = MARGIN_X + i * (colW + colGap);
          doc.setFillColor(250, 251, 254);
          doc.setDrawColor(...RULE);
          doc.setLineWidth(0.3);
          doc.roundedRect(x, y, colW, boxH, 2.5, 2.5, 'FD');
          doc.setFillColor(...NAVY);
          doc.rect(x, y + 3, 1.4, boxH - 6, 'F');

          const ix = x + 7;
          const iw = colW - 13;
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(7.5);
          doc.setTextColor(...MUTED);
          doc.text(b.role.toUpperCase(), ix, y + 8);

          doc.setFont('helvetica', 'bold');
          doc.setFontSize(11);
          doc.setTextColor(...INK);
          doc.text(doc.splitTextToSize(b.name || ' ', iw)[0] || '', ix, y + 15);
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(9);
          doc.setTextColor(...MUTED);
          doc.text(b.title, ix, y + 20.5);

          // Signature line
          doc.setDrawColor(...INK);
          doc.setLineWidth(0.3);
          doc.line(ix, y + 36, ix + iw, y + 36);
          doc.setFontSize(7.5);
          doc.text('Signature', ix, y + 40.5);

          // Date
          doc.setFontSize(9);
          doc.setTextColor(...INK);
          doc.text(`Date: ${signedOn}`, ix, y + 49);
        });
        y += boxH + 8;

        doc.setFont('helvetica', 'italic');
        doc.setFontSize(8.5);
        doc.setTextColor(...MUTED);
        doc.text(`Signed on behalf of ${companyName || 'the company'}.`, MARGIN_X, y);
        resetInk();
      } catch (e) {
        // ignore sign-off failures
      }

      // Running header and footer on every page except the cover.
      try {
        const pageCount = doc.getNumberOfPages();
        const headerRight = `Monthly Management Report${quarterTag ? ` · ${quarterTag}` : ''}`;
        for (let p = 2; p <= pageCount; p += 1) {
          doc.setPage(p);
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(8);
          doc.setTextColor(...NAVY);
          doc.text(companyName || '', MARGIN_X, 12);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(...MUTED);
          doc.text(headerRight, pageWidth - MARGIN_X, 12, { align: 'right' });
          doc.setDrawColor(...NAVY);
          doc.setLineWidth(0.5);
          doc.line(MARGIN_X, 15, pageWidth - MARGIN_X, 15);

          doc.setDrawColor(...RULE);
          doc.setLineWidth(0.3);
          doc.line(MARGIN_X, pageHeight - 13, pageWidth - MARGIN_X, pageHeight - 13);
          doc.setFontSize(7.5);
          doc.setTextColor(...MUTED);
          doc.text('Confidential', MARGIN_X, pageHeight - 8);
          doc.text(`Page ${p} of ${pageCount}`, pageWidth - MARGIN_X, pageHeight - 8, { align: 'right' });
        }
        resetInk();
      } catch (e) {
        // ignore header/footer failures
      }

      doc.save(`microlender_monthly_management_report_${fileSuffix}.pdf`);
    }
  };
  exportPdfRef.current = exportPdf;

  return (
    <div className="p-0">
      {mmCashPrompt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true" aria-labelledby="mm-cash-title">
          <form
            className="w-full max-w-md rounded-lg bg-white dark:bg-slate-800 shadow-xl"
            onSubmit={(e) => { e.preventDefault(); saveCashPrompt(); }}
          >
            <div className="border-b border-gray-200 dark:border-slate-700 px-5 py-4">
              <h3 id="mm-cash-title" className="text-base font-semibold text-gray-900 dark:text-slate-100">Cash and bank balances</h3>
              <p className="mt-1 text-sm text-gray-600 dark:text-slate-300">
                Enter the cash and bank balance at the end of each month of Q{mmCashPrompt.quarter} {mmCashPrompt.year}. It cannot be N$0.00; use a negative amount for an overdraft.
              </p>
            </div>
            <div className="space-y-3 px-5 py-4">
              {mmCashPrompt.months.map((m, i) => {
                const err = mmCashPrompt.errors?.[m.key];
                return (
                  <div key={m.key}>
                    <label htmlFor={`mm-cash-${m.key}`} className="text-sm font-medium text-gray-700 dark:text-slate-300">
                      {m.label} (N$)<span className="ml-0.5 text-red-600" aria-hidden="true">*</span>
                    </label>
                    <input
                      id={`mm-cash-${m.key}`}
                      type="text"
                      inputMode="decimal"
                      autoFocus={i === mmCashPrompt.months.findIndex((x) => !x.ok)}
                      value={mmCashPrompt.values[m.key] ?? ''}
                      onChange={(e) => {
                        const v = e.target.value;
                        setMmCashPrompt((p) => (p ? { ...p, values: { ...p.values, [m.key]: v }, errors: { ...p.errors, [m.key]: '' } } : p));
                      }}
                      placeholder="e.g. 12500.00"
                      aria-invalid={Boolean(err) || undefined}
                      className={`mt-1 w-full p-2 text-right ${err ? 'border-red-400 dark:border-red-500' : ''}`}
                    />
                    {err && <div className="mt-1 text-xs text-red-600 dark:text-red-400">{err}</div>}
                  </div>
                );
              })}
              {mmCashPrompt.error && (
                <div className="rounded-md border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/30 px-3 py-2 text-xs text-red-700 dark:text-red-300">{mmCashPrompt.error}</div>
              )}
            </div>
            <div className="flex justify-end gap-2 border-t border-gray-200 dark:border-slate-700 px-5 py-3">
              <button
                type="button"
                onClick={() => { mmCashThen.current = null; setMmCashPrompt(null); }}
                className="px-4 py-2 rounded-md text-sm border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-700"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={mmCashPrompt.saving}
                className="px-4 py-2 rounded-md text-sm bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
              >
                {mmCashPrompt.saving ? 'Saving…' : 'Save and continue'}
              </button>
            </div>
          </form>
        </div>
      )}
      {/* Shared report toolbar hidden — this view is locked to the Microlender
          Monthly Management Report (it has its own Year/Quarter + downloads). */}
      <div className="hidden">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-slate-300">Report</label>
            <select
              value={selectedReport}
              onChange={(e) => {
                setSelectedReport(e.target.value);
                setQuery({ ...filters });
              }}
              className="mt-1 w-full p-2"
            >
              {reportOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-slate-300">Month</label>
            <select
              value={filters.month}
              onChange={(e) => setFilters({ ...filters, month: e.target.value })}
              className="mt-1 w-full p-2"
            >
              {monthOptions.map(opt => <option key={opt.value || 'all'} value={opt.value}>{opt.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-slate-300">Year</label>
            <select
              value={filters.year}
              onChange={(e) => setFilters({ ...filters, year: e.target.value })}
              className="mt-1 w-full p-2"
            >
              <option value="">Select Year</option>
              {years.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-slate-300">Start Date</label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
              className="mt-1 w-full p-2"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-slate-300">End Date</label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
              className="mt-1 w-full p-2"
            />
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <button
            onClick={() => setQuery({ ...filters })}
            disabled={loading}
            className="w-full sm:w-auto px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 disabled:opacity-60"
          >
            {loading ? 'Loading...' : 'Apply Filters'}
          </button>
          <button
            onClick={() => { const reset = { month: '', year: '', startDate: '', endDate: '' }; setFilters(reset); setQuery(reset); }}
            className="w-full sm:w-auto px-4 py-2 bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-200 rounded-md text-sm hover:bg-gray-200 dark:hover:bg-slate-600"
          >
            Reset
          </button>
        </div>
        {(selectedReport === 'disbursements' && error) && <div className="text-sm text-red-600">{error}</div>}
        {(selectedReport === 'total_loans_disbursed' && totalLoansDisbursedError) && <div className="text-sm text-red-600">{totalLoansDisbursedError}</div>}
        {(selectedReport === 'total_loans_disbursed' && totalLoansDisbursedLoansError) && <div className="text-sm text-red-600">{totalLoansDisbursedLoansError}</div>}
        {(selectedReport === 'loans_per_collection_method' && loansPerCollectionMethodError) && <div className="text-sm text-red-600">{loansPerCollectionMethodError}</div>}
        {(selectedReport === 'loans_by_purpose' && loansByPurposeError) && <div className="text-sm text-red-600">{loansByPurposeError}</div>}
        {(selectedReport === 'disbursements_gender_ranges' && disbGenderRangesError) && <div className="text-sm text-red-600">{disbGenderRangesError}</div>}
        {(selectedReport === 'borrower_salaries_by_gender' && borrowerSalariesByGenderError) && <div className="text-sm text-red-600">{borrowerSalariesByGenderError}</div>}
        {(selectedReport === 'number_of_clients' && numberOfClientsError) && <div className="text-sm text-red-600">{numberOfClientsError}</div>}
        {(selectedReport === 'loans_outstanding_numbers' && loansOutstandingNumbersError) && <div className="text-sm text-red-600">{loansOutstandingNumbersError}</div>}
        {(selectedReport === 'overdue' && overdueError) && <div className="text-sm text-red-600">{overdueError}</div>}
        {(selectedReport === 'arrears_breakdown' && arrearsBreakdownError) && (
          <div className="text-sm text-red-600">{arrearsBreakdownError}</div>
        )}
        {(selectedReport === 'unpaid' && unpaidError) && <div className="text-sm text-red-600">{unpaidError}</div>}
        {(selectedReport === 'interest' && interestError) && <div className="text-sm text-red-600">{interestError}</div>}
        {(selectedReport === 'defaultInterest' && defaultInterestError) && <div className="text-sm text-red-600">{defaultInterestError}</div>}
        {(selectedReport === 'disbursements_duration_breakdown' && disbDurationBreakdownError) && <div className="text-sm text-red-600">{disbDurationBreakdownError}</div>}
        {(selectedReport === 'disbursements_duration_breakdown' && disbDurationBreakdownLoansError) && <div className="text-sm text-red-600">{disbDurationBreakdownLoansError}</div>}
        {(selectedReport === 'loans_per_collection_method' && loansPerCollectionMethodLoansError) && <div className="text-sm text-red-600">{loansPerCollectionMethodLoansError}</div>}
        {(selectedReport === 'loans_by_purpose' && loansByPurposeLoansError) && <div className="text-sm text-red-600">{loansByPurposeLoansError}</div>}
        {(selectedReport === 'borrower_salaries_by_gender' && borrowerSalariesByGenderBorrowersError) && <div className="text-sm text-red-600">{borrowerSalariesByGenderBorrowersError}</div>}
        {(selectedReport === 'repayment_methods' && repaymentsError) && (
          <div className="text-sm text-red-600">{repaymentsError}</div>
        )}
        {(selectedReport === 'microlender_monthly_management' && monthlyManagementError) && (
          <div className="text-sm text-red-600">{monthlyManagementError}</div>
        )}
        {(selectedReport === 'provision_bad_debts_movement' && provisionMovementError) && <div className="text-sm text-red-600">{provisionMovementError}</div>}
        {(selectedReport === 'interest_outstanding_quarter' && interestQuarterError) && <div className="text-sm text-red-600">{interestQuarterError}</div>}
      </div>

      {selectedReport === 'microlender_monthly_management' && (
        <div className={`${cardClass('blue')} p-4 pt-5 mb-4`}>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
            <div>
              <h2 className="text-lg font-semibold text-gray-800 dark:text-slate-100">Microlender Monthly Management Report</h2>
              <p className="text-sm text-gray-600 dark:text-slate-400">Uses system data where available; manual inputs are saved automatically and reloaded next time.</p>
              {monthlyManagement && mmSaveState !== 'idle' && (
                <p className={`text-xs mt-1 ${mmSaveState === 'error' ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-slate-400'}`}>
                  {mmSaveState === 'saving' && 'Saving manual inputs…'}
                  {mmSaveState === 'saved' && 'All manual inputs saved'}
                  {mmSaveState === 'error' && 'Could not save manual inputs — check your connection and try again.'}
                </p>
              )}
              {monthlyManagement && mmDerivedError && (
                <p className="text-xs mt-1 text-red-600 dark:text-red-400">{mmDerivedError}</p>
              )}
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-700 dark:text-slate-300">Year</label>
                <select
                  value={mmYear}
                  onChange={(e) => setMmYear(e.target.value)}
                  className="p-2"
                >
                  <option value="">Select Year</option>
                  {years.map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-700 dark:text-slate-300">Quarter</label>
                <select
                  value={mmQuarter}
                  onChange={(e) => setMmQuarter(e.target.value)}
                  className="p-2"
                >
                  <option value="">Select Quarter</option>
                  {[1, 2, 3, 4].map(q => <option key={q} value={q}>Q{q}</option>)}
                </select>
              </div>
              <button
                type="button"
                onClick={generateMonthlyManagement}
                disabled={monthlyManagementLoading}
                className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 disabled:opacity-60"
              >
                {monthlyManagementLoading ? 'Generating...' : 'Generate Report'}
              </button>
              <button
                type="button"
                onClick={exportMmPdf}
                disabled={!monthlyManagement || mmPdfWaiting}
                title={mmPdfWaiting ? 'Waiting for the commentary to finish' : undefined}
                className="px-4 py-2 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-md hover:bg-red-200 dark:hover:bg-red-900/50 text-sm disabled:opacity-60"
              >
                {mmPdfWaiting ? 'Finishing commentary…' : 'Download PDF'}
              </button>
              <button
                type="button"
                onClick={exportExcel}
                disabled={!monthlyManagement}
                className="px-4 py-2 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-md hover:bg-green-200 dark:hover:bg-green-900/50 text-sm disabled:opacity-60"
              >
                Download CSV
              </button>
            </div>
          </div>

          {monthlyManagementError && (
            <div className="mb-3 text-sm text-red-600">{monthlyManagementError}</div>
          )}

          {!monthlyManagement && (
            <div className="text-sm text-gray-700 dark:text-slate-300">
              Select a <span className="font-medium">Year</span> and <span className="font-medium">Quarter</span>, then click <span className="font-medium">Generate Report</span>.
            </div>
          )}

          {monthlyManagement && (
            (() => {
              const monthReports = Array.isArray(monthlyManagement?.months) ? monthlyManagement.months : [];
              const qk = mmQuarterKey(monthlyManagement?.year, monthlyManagement?.quarter);
              const qInputs = getMmQuarterInputs(qk);
              const last = monthReports.length ? monthReports[monthReports.length - 1] : null;

              const qtdInterestIncome = Number(monthlyManagement?.qtd?.income?.interest_income || 0);
              const qtdFeesIncome = Number(monthlyManagement?.qtd?.income?.fees_income || 0);
              const qtdBadDebtsRecovered = Number(monthlyManagement?.qtd?.income?.bad_debts_recovered || 0);
              const qtdImpairmentCharge = Number(monthlyManagement?.qtd?.income?.impairment_charge || 0);
              const qtdWrittenOff = Number(monthlyManagement?.qtd?.income?.written_off || 0);

              const qeTotalBookValue = Number(last?.loanBook?.closing?.total_outstanding || 0);
              const qeActiveLoans = Number(last?.loanBook?.closing?.count || 0);
              const qeArrearsRatio = Number(last?.arrears?.arrearsRatioByAmount || 0);
              const qeNpl90Ratio = Number(last?.arrears?.npl90?.ratioByAmount || 0);

              const qStart = monthlyManagement?.quarterStartDate || monthlyManagement?.qtd?.period?.startDate || '';
              const qEnd = monthlyManagement?.quarterEndDate || monthlyManagement?.qtd?.period?.endDate || '';
              const companyName = monthlyManagement?.company?.name || '';
              const quarterTagScreen = `Q${monthlyManagement?.quarter} ${monthlyManagement?.year}`;
              const aiBusy = Boolean(qk) && mmAiBusyKey === qk;
              const aiError = (qk && mmAiError.key === qk) ? mmAiError.message : '';

              const coverQuarter = mmQuarterTitle(monthlyManagement?.year, monthlyManagement?.quarter);
              const coverPreparedBy = mmPreparedBy(qInputs);
              const coverRows = [
                ['NAMFISA licence no.', monthlyManagement?.company?.namfisaRegNo || '—'],
                ['Reporting period', qStart || qEnd ? `${formatDate(qStart)} to ${formatDate(qEnd)}` : '—'],
                ['Prepared by', coverPreparedBy ? (coverPreparedBy === String(monthlyManagement?.company?.principalOfficerName || '').trim() ? `${coverPreparedBy} (Principal Officer)` : coverPreparedBy) : '—'],
                ['Approved by', String(qInputs.approvedBy || '').trim() || '—'],
                ['Date prepared', formatDate(new Date())],
              ];

              return (
                <div className="space-y-6">
                  {/* Cover page */}
                  <div className="relative overflow-hidden rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm">
                    <div className="h-2 bg-gradient-to-r from-blue-700 via-indigo-600 to-sky-500" />
                    <div className="px-6 py-8 sm:px-10 sm:py-10 flex flex-col items-center text-center">
                      {mmLogo?.src ? (
                        <img
                          src={mmLogo.src}
                          alt={companyName || 'Company logo'}
                          className="h-20 max-w-[220px] object-contain mb-5"
                          onError={() => setMmLogo(null)}
                        />
                      ) : null}
                      <div className="text-lg sm:text-xl font-semibold tracking-wide text-gray-800 dark:text-slate-100 uppercase">{companyName || '—'}</div>
                      {monthlyManagement?.company?.address && (
                        <div className="mt-1 text-xs text-gray-500 dark:text-slate-400">{monthlyManagement.company.address}</div>
                      )}
                      {(monthlyManagement?.company?.phoneNumber || monthlyManagement?.company?.email) && (
                        <div className="mt-0.5 text-xs text-gray-500 dark:text-slate-400">
                          {[monthlyManagement?.company?.phoneNumber, monthlyManagement?.company?.email].filter(Boolean).join('  ·  ')}
                        </div>
                      )}
                      <div className="mt-6 w-16 h-0.5 bg-blue-600 dark:bg-blue-400 rounded" />
                      <h2 className="mt-6 text-2xl sm:text-3xl font-bold text-blue-800 dark:text-blue-300">Monthly Management Report</h2>
                      {coverQuarter && (
                        <div className="mt-2 text-base sm:text-lg font-medium text-gray-700 dark:text-slate-200">{coverQuarter}</div>
                      )}
                      <dl className="mt-8 w-full max-w-lg grid grid-cols-1 sm:grid-cols-[auto,1fr] gap-x-6 gap-y-2 text-sm text-left rounded-lg bg-gray-50 dark:bg-slate-900/40 border border-gray-100 dark:border-slate-700 px-5 py-4">
                        {coverRows.map(([label, value]) => (
                          <React.Fragment key={label}>
                            <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400 sm:py-0.5">{label}</dt>
                            <dd className="text-gray-800 dark:text-slate-100 font-medium mb-2 sm:mb-0 sm:py-0.5">{value}</dd>
                          </React.Fragment>
                        ))}
                      </dl>
                      <div className="mt-6 text-[11px] uppercase tracking-widest text-gray-400 dark:text-slate-500">Prepared for the Board and NAMFISA · Confidential</div>
                    </div>
                    <div className="h-1 bg-gradient-to-r from-sky-500 via-indigo-600 to-blue-700" />
                  </div>

                  <div className="rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4 space-y-5">
                    <MmPeriodBanner
                      title={`Quarter summary — Q${monthlyManagement?.quarter} ${monthlyManagement?.year}`}
                      start={formatDate(qStart)}
                      end={formatDate(qEnd)}
                    />

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                      <div>
                        <MmSectionHeading n={1} title="Quarter-end snapshot" period={quarterTagScreen} />
                        <MmKvTable
                          rows={[
                            { label: <>Company name<SourceTag source="AUTO" /></>, value: companyName || '—' },
                            { label: <>Closing loan book value<SourceTag source="AUTO" /></>, value: toCurrency(qeTotalBookValue) },
                            { label: <>Active loans (closing)<SourceTag source="AUTO" /></>, value: qeActiveLoans },
                            { label: <>Arrears ratio (by amount)<SourceTag source="AUTO" /></>, value: mmPercent(qeArrearsRatio) },
                            { label: <>NPL 90+ ratio (by amount)<SourceTag source="AUTO" /></>, value: mmPercent(qeNpl90Ratio) },
                          ]}
                        />
                      </div>

                      <div>
                        <MmSectionHeading n={2} title="Quarter-to-date totals" period={quarterTagScreen} />
                        <MmKvTable
                          rows={[
                            { label: <>New loans issued (count)<SourceTag source="AUTO" /></>, value: monthlyManagement?.qtd?.newLoans?.count || 0 },
                            { label: <>New loans issued (amount)<SourceTag source="AUTO" /></>, value: toCurrency(monthlyManagement?.qtd?.newLoans?.total_principal || 0) },
                            { label: <>Repayments (transactions)<SourceTag source="AUTO" /></>, value: monthlyManagement?.qtd?.repayments?.txn_count || 0 },
                            { label: <>Repayments (amount)<SourceTag source="AUTO" /></>, value: toCurrency(monthlyManagement?.qtd?.repayments?.total_paid || 0) },
                            { label: <>Default/penalty interest items<SourceTag source="AUTO" /></>, value: monthlyManagement?.qtd?.defaultInterest?.count || 0 },
                            { label: <>Default/penalty interest (amount)<SourceTag source="AUTO" /></>, value: toCurrency(monthlyManagement?.qtd?.defaultInterest?.total_default_interest || 0) },
                            { label: <>Interest income (sum of months)<SourceTag source="AUTO" /></>, value: toCurrency(qtdInterestIncome) },
                            { label: <>Fees income (sum of months)<SourceTag source="AUTO" /></>, value: toCurrency(qtdFeesIncome) },
                            { label: <>Bad debts recovered (sum of months)<SourceTag source="AUTO" /></>, value: toCurrency(qtdBadDebtsRecovered) },
                            { label: <>Loans written off (sum of months)<SourceTag source="AUTO" /></>, value: toCurrency(qtdWrittenOff) },
                            { label: <>Impairment charge (sum of months)<SourceTag source="AUTO" /></>, value: toCurrency(qtdImpairmentCharge) },
                          ]}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                      <div>
                        <MmSectionHeading n={3} title="Report details" period={quarterTagScreen} />
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          <div className="sm:col-span-2">
                            <label className="text-xs font-medium text-gray-700 dark:text-slate-300">
                              NAMFISA licence number
                              <SourceTag source="AUTO" />
                              <HelpTooltip text={MM_TOOLTIPS.licenceNumber} />
                            </label>
                            <input
                              type="text"
                              value={monthlyManagement?.company?.namfisaRegNo || ''}
                              disabled
                              className="mt-1 w-full p-2"
                            />
                          </div>
                          <div>
                            <label className="text-xs font-medium text-gray-700 dark:text-slate-300">
                              Prepared by
                              <SourceTag source="MANUAL" />
                              <HelpTooltip text={MM_TOOLTIPS.preparedBy} />
                            </label>
                            <input
                              type="text"
                              value={qInputs.preparedBy}
                              onChange={(e) => setMmQuarterInput(qk, 'preparedBy', e.target.value)}
                              placeholder={monthlyManagement?.company?.principalOfficerName || 'Principal officer'}
                              className="mt-1 w-full p-2"
                            />
                          </div>
                          <div>
                            <label className="text-xs font-medium text-gray-700 dark:text-slate-300">
                              Approved by
                              <SourceTag source="MANUAL" />
                              <HelpTooltip text={MM_TOOLTIPS.approvedBy} />
                            </label>
                            <input
                              type="text"
                              value={qInputs.approvedBy}
                              onChange={(e) => setMmQuarterInput(qk, 'approvedBy', e.target.value)}
                              className="mt-1 w-full p-2"
                            />
                          </div>
                        </div>
                      </div>

                      <div>
                        <MmSectionHeading
                          n={4}
                          title="Management commentary"
                          period={quarterTagScreen}
                          right={(!aiBusy && String(qInputs.quarterCommentary || '').trim()) ? (
                            <button
                              type="button"
                              onClick={() => generateMmCommentary(qk, monthlyManagement?.year, monthlyManagement?.quarter)}
                              disabled={!qk}
                              title="Write the commentary, actions and each month's commentary and risks again from this quarter's figures"
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold text-blue-800 dark:text-blue-200 border border-blue-200 dark:border-blue-800 bg-white/70 dark:bg-slate-800 hover:bg-white dark:hover:bg-slate-700 disabled:opacity-60"
                            >
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.6M20 20v-5h-.6M5.1 9A7 7 0 0119 11M18.9 15A7 7 0 015 13" />
                              </svg>
                              Regenerate
                            </button>
                          ) : null}
                        />
                        {aiBusy ? (
                          <div className="flex items-center gap-3 rounded-md border border-blue-200 dark:border-blue-800 bg-blue-50/60 dark:bg-blue-950/30 px-4 py-6 text-sm text-blue-900 dark:text-blue-200" role="status" aria-live="polite">
                            <svg className="w-5 h-5 animate-spin shrink-0" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                            </svg>
                            <div>
                              <div className="font-medium">Writing the commentary…</div>
                              <div className="text-xs text-blue-900/70 dark:text-blue-200/70">This can take up to a minute.</div>
                            </div>
                          </div>
                        ) : (
                          <textarea
                            value={qInputs.quarterCommentary}
                            onChange={(e) => setMmQuarterInput(qk, 'quarterCommentary', e.target.value)}
                            aria-label="Quarter management commentary"
                            className="w-full p-2 leading-relaxed"
                            rows={String(qInputs.quarterCommentary || '').length > 300 ? 10 : 4}
                          />
                        )}
                        {aiError ? (
                          <div className="mt-2 flex flex-wrap items-center gap-2 rounded-md border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/30 px-3 py-2 text-xs text-red-700 dark:text-red-300">
                            <span className="flex-1 min-w-0">{aiError}</span>
                            <button
                              type="button"
                              onClick={() => generateMmCommentary(qk, monthlyManagement?.year, monthlyManagement?.quarter)}
                              className="px-2.5 py-1 rounded-md font-semibold bg-red-600 text-white hover:bg-red-700"
                            >
                              Try again
                            </button>
                          </div>
                        ) : (!aiBusy && (
                          <div className="mt-1 text-[11px] text-gray-500 dark:text-slate-400">Written automatically from this quarter's figures, with the actions below and each month's commentary and major risk issues. Read and edit before the report is approved.</div>
                        ))}

                        <div className="mt-4">
                          <label className="text-xs font-medium text-gray-700 dark:text-slate-300">
                            Actions / remediation
                            <HelpTooltip text={MM_TOOLTIPS.quarterActions} />
                          </label>
                          <textarea
                            value={qInputs.quarterActions}
                            onChange={(e) => setMmQuarterInput(qk, 'quarterActions', e.target.value)}
                            disabled={aiBusy}
                            placeholder={aiBusy ? 'Writing from the commentary…' : ''}
                            className="mt-1 w-full p-2 leading-relaxed disabled:opacity-70"
                            rows={String(qInputs.quarterActions || '').length > 200 ? 6 : 3}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {monthReports.map((mr) => {
                    const reportMonth = mr?.period?.month;
                    const reportYear = mr?.period?.year;
                    const title = `${monthOptions.find(o => Number(o.value) === Number(reportMonth))?.label || reportMonth} ${reportYear}`;
                    const key = `${String(reportYear).padStart(4, '0')}-${String(reportMonth).padStart(2, '0')}`;
                    const inputs = getMmInputs(key);

                    const derived = getMmDerived(key, mr);
                    const unsecuredCount = derived.new_loans.unsecured.count;
                    const unsecuredAmt = derived.new_loans.unsecured.amount;
                    const autoSpecificProvisions = derived.provisions.specific;
                    const netLoanBook = derived.loan_book.net;

                    return (
                      <div key={key} className="rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4 space-y-5">
                    <MmPeriodBanner title={title} start={formatDate(mr?.period?.startDate)} end={formatDate(mr?.period?.endDate)} />

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                      <div className="min-w-0">
                        <MmSectionHeading n={1} title="Executive summary" period={title} />
                        <MmKvTable
                          rows={[
                            { label: <>Total loan book value (closing)<SourceTag source="AUTO" /></>, value: toCurrency(mr?.loanBook?.closing?.total_outstanding || 0) },
                            { label: <>Total number of active loans (closing)<SourceTag source="AUTO" /></>, value: mr?.loanBook?.closing?.count || 0 },
                            { label: <>Arrears ratio (by amount)<SourceTag source="AUTO" /></>, value: mmPercent(mr?.arrears?.arrearsRatioByAmount || 0) },
                            { label: <>NPL 90+ ratio (by amount)<SourceTag source="AUTO" /></>, value: mmPercent(mr?.arrears?.npl90?.ratioByAmount || 0) },
                          ]}
                        />
                        <div className="mt-3">
                          <label className="text-xs font-medium text-gray-700 dark:text-slate-300">
                            Major risk issues
                            <HelpTooltip text={MM_TOOLTIPS.majorRiskIssues} />
                          </label>
                          <textarea
                            value={inputs.majorRiskIssues}
                            onChange={(e) => setMmInput(key, 'majorRiskIssues', e.target.value)}
                            disabled={aiBusy}
                            placeholder={aiBusy ? "Writing from the month's figures…" : ''}
                            className="mt-1 w-full p-2 leading-relaxed disabled:opacity-70"
                            rows={String(inputs.majorRiskIssues || '').length > 200 ? 6 : 3}
                          />
                        </div>
                      </div>

                      <div className="min-w-0">
                        <MmSectionHeading n={2} title="Loan portfolio summary" period={title} />
                        <TableScroll>
                          <table className="reports-table w-full text-sm border-collapse">
                            <thead>
                              <tr>
                                <th className={`${MM_TH} text-left`}>Item</th>
                                <th className={`${MM_TH} text-right`}>Amount (N$)</th>
                                <th className={`${MM_TH} text-right`}>Number of Loans</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr className={MM_TR}>
                                <td className={MM_TD}>
                                  <span className="inline-flex items-center">Opening loan book<SourceTag source="AUTO" /></span>
                                </td>
                                <td className={MM_TD_NUM}>{toCurrency(mr?.loanBook?.opening?.total_outstanding || 0)}</td>
                                <td className={MM_TD_NUM}>{mr?.loanBook?.opening?.count || 0}</td>
                              </tr>
                              <tr className={MM_TR}>
                                <td className={MM_TD}>
                                  <span className="inline-flex items-center">New loans issued<SourceTag source="AUTO" /></span>
                                </td>
                                <td className={MM_TD_NUM}>{toCurrency(mr?.loanBook?.newLoans?.total_principal || 0)}</td>
                                <td className={MM_TD_NUM}>{mr?.loanBook?.newLoans?.count || 0}</td>
                              </tr>
                              <tr className={MM_TR}>
                                <td className={MM_TD}>
                                  <span className="inline-flex items-center">Loans settled<SourceTag source="AUTO" /></span>
                                </td>
                                <td className={MM_TD_NUM}>{toCurrency(mr?.loanBook?.settled?.total_settled || 0)}</td>
                                <td className={MM_TD_NUM}>{mr?.loanBook?.settled?.count || 0}</td>
                              </tr>
                              <tr className={MM_TR}>
                                <td className={MM_TD}>
                                  <span className="inline-flex items-center">Written off<SourceTag source="AUTO" /><HelpTooltip text={MM_TOOLTIPS.writtenOff} /></span>
                                </td>
                                <td className={MM_TD_NUM}>{toCurrency(mr?.loanBook?.writtenOff?.total || 0)}</td>
                                <td className={MM_TD_NUM}>{mr?.loanBook?.writtenOff?.count || 0}</td>
                              </tr>
                              <tr className={MM_TR}>
                                <td className={MM_TD}>
                                  <span className="inline-flex items-center">Repayments &amp; other movements<SourceTag source="AUTO" /><HelpTooltip text={MM_TOOLTIPS.otherMovements} /></span>
                                </td>
                                <td className={MM_TD_NUM}>{toCurrency(mr?.loanBook?.otherMovements?.total || 0)}</td>
                                <td className={MM_TD_NUM}>{mr?.loanBook?.otherMovements?.count || 0}</td>
                              </tr>
                              <tr className={MM_TR_TOTAL}>
                                <td className={MM_TD}>
                                  <span className="inline-flex items-center">Closing loan book<SourceTag source="AUTO" /></span>
                                </td>
                                <td className={MM_TD_NUM}>{toCurrency(mr?.loanBook?.closing?.total_outstanding || 0)}</td>
                                <td className={MM_TD_NUM}>{mr?.loanBook?.closing?.count || 0}</td>
                              </tr>
                              <tr className={MM_TR}>
                                <td className={MM_TD}>
                                  <span className="inline-flex items-center">Unsecured loans<SourceTag source="AUTO+MANUAL" /></span>
                                </td>
                                <td className={MM_TD_NUM}>{toCurrency(unsecuredAmt)}</td>
                                <td className={MM_TD_NUM}>{unsecuredCount}</td>
                              </tr>
                              <tr className={MM_TR}>
                                <td className={MM_TD}>
                                  <span className="inline-flex items-center">
                                    Secured loans
                                    <SourceTag source="MANUAL" />
                                    <HelpTooltip text={MM_TOOLTIPS.securedLoans} />
                                  </span>
                                </td>
                                <td className={MM_TD_NUM}>
                                  <input
                                    type="text"
                                    value={inputs.securedLoansAmount}
                                    onChange={(e) => setMmInput(key, 'securedLoansAmount', e.target.value)}
                                    className="w-32 text-right"
                                  />
                                </td>
                                <td className={MM_TD_NUM}>
                                  <input
                                    type="text"
                                    value={inputs.securedLoansCount}
                                    onChange={(e) => setMmInput(key, 'securedLoansCount', e.target.value)}
                                    className="w-20 text-right"
                                  />
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </TableScroll>
                      </div>
                    </div>

                    <div className="min-w-0">
                      <MmSectionHeading n={3} title="Arrears / aging analysis" period={title} />
                      <TableScroll>
                        <table className="reports-table w-full text-sm border-collapse">
                          <thead>
                            <tr>
                              <th className={`${MM_TH} text-left`}>Bucket</th>
                              <th className={`${MM_TH} text-right`}>Number of Loans</th>
                              <th className={`${MM_TH} text-right`}>Outstanding (N$)</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr className={MM_TR}>
                                <td className={MM_TD}><span className="inline-flex items-center">30–60 days<SourceTag source="AUTO" /></span></td>
                              <td className={MM_TD_NUM}>{mr?.arrears?.buckets?.['30_60']?.count || 0}</td>
                              <td className={MM_TD_NUM}>{toCurrency(mr?.arrears?.buckets?.['30_60']?.outstanding || 0)}</td>
                            </tr>
                            <tr className={MM_TR}>
                                <td className={MM_TD}><span className="inline-flex items-center">60–90 days<SourceTag source="AUTO" /></span></td>
                              <td className={MM_TD_NUM}>{mr?.arrears?.buckets?.['60_90']?.count || 0}</td>
                              <td className={MM_TD_NUM}>{toCurrency(mr?.arrears?.buckets?.['60_90']?.outstanding || 0)}</td>
                            </tr>
                            <tr className={MM_TR}>
                                <td className={MM_TD}><span className="inline-flex items-center">90–120 days<SourceTag source="AUTO" /></span></td>
                              <td className={MM_TD_NUM}>{mr?.arrears?.buckets?.['90_120']?.count || 0}</td>
                              <td className={MM_TD_NUM}>{toCurrency(mr?.arrears?.buckets?.['90_120']?.outstanding || 0)}</td>
                            </tr>
                            <tr className={MM_TR}>
                                <td className={MM_TD}><span className="inline-flex items-center">Over 120 days<SourceTag source="AUTO" /></span></td>
                              <td className={MM_TD_NUM}>{mr?.arrears?.buckets?.['gt_120']?.count || 0}</td>
                              <td className={MM_TD_NUM}>{toCurrency(mr?.arrears?.buckets?.['gt_120']?.outstanding || 0)}</td>
                            </tr>
                            <tr className={MM_TR_TOTAL}>
                                <td className={MM_TD}><span className="inline-flex items-center">Total in arrears<SourceTag source="AUTO" /></span></td>
                              <td className={MM_TD_NUM}>{mr?.arrears?.totals?.count || 0}</td>
                              <td className={MM_TD_NUM}>{toCurrency(mr?.arrears?.totals?.outstanding || 0)}</td>
                            </tr>
                            <tr className={MM_TR}>
                                <td className={MM_TD}><span className="inline-flex items-center">Arrears ratio<SourceTag source="AUTO" /></span></td>
                              <td className={MM_TD_NUM} colSpan={2}>{mmPercent(mr?.arrears?.arrearsRatioByAmount || 0)}</td>
                            </tr>
                          </tbody>
                        </table>
                      </TableScroll>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                      <div className="min-w-0">
                        <MmSectionHeading n={4} title="Income statement summary" period={title} />
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          <div className="sm:col-span-2">
                            <label className="text-xs font-medium text-gray-700 dark:text-slate-300">
                              Interest income (N$)
                              <SourceTag source="AUTO" />
                              <HelpTooltip text={MM_TOOLTIPS.interestIncome} />
                            </label>
                            <div className="mt-1 w-full border border-gray-200 dark:border-slate-600 rounded-md p-2 text-sm bg-gray-50 dark:bg-slate-700 dark:text-slate-100 text-right">
                              {toCurrency(mr?.incomeStatement?.interestIncome || 0)}
                            </div>
                          </div>
                          <div>
                            <label className="text-xs font-medium text-gray-700 dark:text-slate-300">
                              Fees income (N$)
                              <SourceTag source="AUTO" />
                              <HelpTooltip text={MM_TOOLTIPS.feesIncome} />
                            </label>
                            <div className="mt-1 w-full border border-gray-200 dark:border-slate-600 rounded-md p-2 text-sm bg-gray-50 dark:bg-slate-700 dark:text-slate-100 text-right">
                              {toCurrency(mr?.incomeStatement?.feesIncome || 0)}
                            </div>
                          </div>
                          {[
                            { key: 'defaultInterest', label: 'Default interest', value: derived.income.default_interest },
                            { key: 'badDebtsRecovered', label: 'Bad debts recovered', value: derived.income.bad_debts_recovered },
                            { key: 'impairmentCharge', label: 'Impairment charge', value: derived.income.impairment_charge },
                          ].map((f) => (
                            <div key={f.key}>
                              <label className="text-xs font-medium text-gray-700 dark:text-slate-300">
                                {f.label} (N$)
                                <SourceTag source="AUTO" />
                                <HelpTooltip text={MM_TOOLTIPS[f.key]} />
                              </label>
                              <div className="mt-1 w-full border border-gray-200 dark:border-slate-600 rounded-md p-2 text-sm bg-gray-50 dark:bg-slate-700 dark:text-slate-100 text-right">
                                {toCurrency(f.value || 0)}
                              </div>
                            </div>
                          ))}
                          {[
                            { key: 'otherIncome', label: 'Other income' },
                            { key: 'operatingExpenses', label: 'Operating expenses' },
                          ].map((f) => (
                            <div key={f.key}>
                              <label className="text-xs font-medium text-gray-700 dark:text-slate-300">
                                {f.label} (N$)
                                <SourceTag source="MANUAL" />
                                <HelpTooltip text={MM_TOOLTIPS[f.key]} />
                              </label>
                              <input
                                type="text"
                                value={inputs[f.key]}
                                onChange={(e) => setMmInput(key, f.key, e.target.value)}
                                className="mt-1 w-full p-2"
                              />
                            </div>
                          ))}
                          {[
                            { label: 'Total income', value: derived.income.total_income },
                            { label: 'Total expenses', value: derived.income.total_expenses },
                            { label: 'Net profit / loss', value: derived.income.net_profit, wide: true },
                          ].map((f) => (
                            <div key={f.label} className={f.wide ? 'sm:col-span-2' : undefined}>
                              <label className="text-xs font-medium text-gray-700 dark:text-slate-300">
                                {f.label} (N$)
                                <SourceTag source="AUTO+MANUAL" />
                              </label>
                              <div className={`mt-1 w-full border border-gray-200 dark:border-slate-600 rounded-md p-2 text-sm bg-gray-50 dark:bg-slate-700 dark:text-slate-100 text-right${f.wide ? 'font-semibold' : ''}`}>
                                {toCurrency(f.value)}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="min-w-0">
                        <MmSectionHeading n={5} title="Balance sheet summary" period={title} />
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {[
                            { key: 'cashAndBankBalances', label: 'Cash and bank balances' },
                            { key: 'otherAssets', label: 'Other assets' },
                            { key: 'totalLiabilities', label: 'Total liabilities' },
                            { key: 'shareholderEquity', label: 'Shareholder equity' },
                          ].map((f) => {
                            const required = f.key === 'cashAndBankBalances';
                            const missing = required && mmNumber(inputs[f.key]) === 0;
                            return (
                            <div key={f.key}>
                              <label className="text-xs font-medium text-gray-700 dark:text-slate-300">
                                {f.label} (N$)
                                {required && <span className="ml-0.5 text-red-600" aria-hidden="true">*</span>}
                                {!required && <SourceTag source="MANUAL" />}
                                <HelpTooltip text={MM_TOOLTIPS[f.key]} />
                              </label>
                              <input
                                type="text"
                                inputMode={required ? 'decimal' : undefined}
                                value={inputs[f.key]}
                                onChange={(e) => setMmInput(key, f.key, e.target.value)}
                                aria-invalid={missing || undefined}
                                className={`mt-1 w-full p-2 ${missing ? 'border-red-400 dark:border-red-500 bg-red-50/60' : ''}`}
                              />
                              {missing && (
                                <div className="mt-1 text-[11px] text-red-600 dark:text-red-400">Required, and cannot be N$0.00. Use a negative amount for an overdraft.</div>
                              )}
                            </div>
                            );
                          })}

                          <div className="sm:col-span-2">
                            <label className="text-xs font-medium text-gray-700 dark:text-slate-300">
                              Loan book (net of provisions) (N$)
                              <SourceTag source="AUTO+MANUAL" />
                              <HelpTooltip text={MM_TOOLTIPS.loanBookNetOfProvisions} />
                            </label>
                            <div className="mt-1 w-full border border-gray-200 dark:border-slate-600 rounded-md p-2 text-sm bg-gray-50 dark:bg-slate-700 dark:text-slate-100 text-right">
                              {toCurrency(netLoanBook)}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                      <div className="min-w-0">
                        <MmSectionHeading n={6} title="Capital adequacy position" period={title} />
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {[
                            { key: 'paidUpShareCapital', label: 'Paid-up share capital' },
                            { key: 'retainedEarnings', label: 'Retained earnings' },
                            { key: 'requiredMinimumCapital', label: 'Required minimum capital' },
                          ].map((f) => (
                            <div key={f.key}>
                              <label className="text-xs font-medium text-gray-700 dark:text-slate-300">
                                {f.label} (N$)
                                <SourceTag source="MANUAL" />
                                <HelpTooltip text={MM_TOOLTIPS[f.key]} />
                              </label>
                              <input
                                type="text"
                                value={inputs[f.key]}
                                onChange={(e) => setMmInput(key, f.key, e.target.value)}
                                className="mt-1 w-full p-2"
                              />
                            </div>
                          ))}
                          {[
                            { label: 'Total equity', value: derived.capital.total_equity },
                            { label: 'Surplus / shortfall', value: derived.capital.surplus_shortfall },
                          ].map((f) => (
                            <div key={f.label}>
                              <label className="text-xs font-medium text-gray-700 dark:text-slate-300">
                                {f.label} (N$)
                                <SourceTag source="AUTO+MANUAL" />
                              </label>
                              <div className={`mt-1 w-full border border-gray-200 dark:border-slate-600 rounded-md p-2 text-sm bg-gray-50 dark:bg-slate-700 text-right ${Number(f.value) < 0 ? 'text-red-600 dark:text-red-400' : ''}`}>
                                {toCurrency(f.value)}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="min-w-0">
                        <MmSectionHeading n={7} title="Liquidity position" period={title} />
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {[
                            { key: 'cashOnHand', label: 'Cash on hand' },
                            { key: 'monthlyExpenses', label: 'Monthly expenses' },
                            { key: 'shortTermLiabilities', label: 'Short-term liabilities' },
                          ].map((f) => (
                            <div key={f.key}>
                              <label className="text-xs font-medium text-gray-700 dark:text-slate-300">
                                {f.label} (N$)
                                <SourceTag source="MANUAL" />
                                <HelpTooltip text={MM_TOOLTIPS[f.key]} />
                              </label>
                              <input
                                type="text"
                                value={inputs[f.key]}
                                onChange={(e) => setMmInput(key, f.key, e.target.value)}
                                className="mt-1 w-full p-2"
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                      <div className="min-w-0">
                        <MmSectionHeading n={8} title="Provision for bad debts" period={title} />
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          <div>
                            <label className="text-xs font-medium text-gray-700 dark:text-slate-300">
                              Specific provisions (90+ days) (N$)
                              <SourceTag source="AUTO" />
                              <HelpTooltip text={MM_TOOLTIPS.specificProvisions} />
                            </label>
                            <div className="mt-1 w-full border border-gray-200 dark:border-slate-600 rounded-md p-2 text-sm bg-gray-50 dark:bg-slate-700 dark:text-slate-100 text-right">
                              {toCurrency(autoSpecificProvisions)}
                            </div>
                          </div>

                          <div>
                            <label className="text-xs font-medium text-gray-700 dark:text-slate-300">
                              General provision (N$)
                              <SourceTag source="MANUAL" />
                              <HelpTooltip text={MM_TOOLTIPS.generalProvision} />
                            </label>
                            <input
                              type="text"
                              value={inputs.generalProvision}
                              onChange={(e) => setMmInput(key, 'generalProvision', e.target.value)}
                              className="mt-1 w-full p-2"
                            />
                          </div>

                          <div className="sm:col-span-2">
                            <label className="text-xs font-medium text-gray-700 dark:text-slate-300">
                              Total impairment (N$)
                              <SourceTag source="AUTO+MANUAL" />
                            </label>
                            <div className="mt-1 w-full border border-gray-200 dark:border-slate-600 rounded-md p-2 text-sm bg-gray-50 dark:bg-slate-700 dark:text-slate-100 text-right font-semibold">
                              {toCurrency(derived.provisions.total_impairment)}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="min-w-0">
                        <MmSectionHeading
                          n={9}
                          title="Consumer protection & compliance"
                          period={title}
                          right={<a href="/compliance-cases" className="text-xs font-medium text-blue-700 dark:text-blue-300 hover:underline">Manage cases →</a>}
                        />
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {[
                            { key: 'complaintsReceived', label: 'Complaints received' },
                            { key: 'complaintsResolved', label: 'Complaints resolved' },
                            { key: 'courtCases', label: 'Court cases' },
                            { key: 'regulatoryBreaches', label: 'Regulatory breaches' },
                            { key: 'overchargingIssues', label: 'Overcharging issues' },
                          ].map((f) => (
                            <div key={f.key}>
                              <label className="text-xs font-medium text-gray-700 dark:text-slate-300">
                                {f.label}
                                <SourceTag source="AUTO" />
                                <HelpTooltip text={MM_TOOLTIPS[f.key]} />
                              </label>
                              <div className="mt-1 w-full border border-gray-200 dark:border-slate-600 rounded-md p-2 text-sm bg-gray-50 dark:bg-slate-700 dark:text-slate-100 text-right">
                                {mr?.compliance?.[f.key] ?? 0}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="min-w-0">
                      <MmSectionHeading
                        n={10}
                        title="Management commentary"
                        period={title}
                        right={<span className="inline-flex items-center"><HelpTooltip text={MM_TOOLTIPS.managementCommentary} /></span>}
                      />
                      <textarea
                        value={inputs.managementCommentary}
                        onChange={(e) => setMmInput(key, 'managementCommentary', e.target.value)}
                        disabled={aiBusy}
                        placeholder={aiBusy ? "Writing from the month's figures…" : ''}
                        className="mt-1 w-full p-2 leading-relaxed disabled:opacity-70"
                        rows={String(inputs.managementCommentary || '').length > 300 ? 8 : 4}
                      />
                    </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()
          )}
        </div>
      )}

      {selectedReport === 'disbursements' && (
      <>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
        <div className="bg-purple-50 border border-purple-100 rounded-lg p-4">
          <p className="text-xs uppercase text-purple-600 font-semibold">Months Covered</p>
          <p className="text-2xl font-bold text-purple-800">{data.byMonth?.length || 0}</p>
          <p className="text-xs text-purple-700 mt-1 line-clamp-2">{data.byMonth?.map(m => m.month).join(', ') || '—'}</p>
        </div>
        <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
          <p className="text-xs uppercase text-blue-600 font-semibold">Total Loans</p>
          <p className="text-2xl font-bold text-blue-800">{data.totals.count}</p>
        </div>
        <div className="bg-green-50 border border-green-100 rounded-lg p-4">
          <p className="text-xs uppercase text-green-600 font-semibold">Total Amount</p>
          <p className="text-2xl font-bold text-green-800">{toCurrency(data.totals.total_amount)}</p>
        </div>
        <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-4">
          <p className="text-xs uppercase text-emerald-600 font-semibold">Loans Paid Off</p>
          <p className="text-2xl font-bold text-emerald-800">{data.paidOff.count}</p>
          <p className="text-sm text-emerald-700">Amount {toCurrency(data.paidOff.total_amount)}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 mb-4">
        <div className={`${cardClass('indigo')} p-4 pt-5`}>
          <div className="flex justify-between items-center mb-2">
            <h2 className="text-lg font-semibold text-gray-800 dark:text-slate-100">By Gender</h2>
          </div>
          <div className="w-full">
            <TableScroll>
              <table className="reports-table text-sm w-full">
              <thead>
                <tr className="bg-gray-50 dark:bg-slate-700 text-gray-700 dark:text-slate-200">
                  <th className="text-left px-3 py-2">Gender</th>
                  <th className="text-left px-3 py-2">Count</th>
                  <th className="text-left px-3 py-2">Total Amount</th>
                </tr>
              </thead>
              <tbody>
                {disbGenderPage.items.map((row) => (
                  <tr key={row.gender} className="border-b last:border-0">
                    <td className="px-3 py-2 capitalize">{row.gender}</td>
                    <td className="px-3 py-2">{row.count}</td>
                    <td className="px-3 py-2">{toCurrency(row.total_amount)}</td>
                  </tr>
                ))}
                {data.byGender.length === 0 && (
                  <tr><td className="px-3 py-2 text-gray-500 dark:text-slate-400" colSpan={3}>No data</td></tr>
                )}
              </tbody>
              </table>
            </TableScroll>
            <Pager hideSingle className="mt-3"
              page={disbGenderPage.page}
              pages={disbGenderPage.totalPages}
              total={disbGenderPage.totalItems}
              pageSize={disbGenderPage.pageSize}
              onPage={(p) => setPages((prev) => ({ ...prev, disbGender: clamp(p, 1, disbGenderPage.totalPages) }))}
              onPageSize={(n) => { setPageSize(n); setPages((prev) => ({ ...prev, disbGender: 1 })); }}
            />
          </div>
        </div>

        <div className={`${cardClass('violet')} p-4 pt-5`}>
          <div className="flex justify-between items-center mb-2">
            <h2 className="text-lg font-semibold text-gray-800 dark:text-slate-100">By Loan Reason</h2>
          </div>
          <div className="w-full">
            <TableScroll>
              <table className="reports-table text-sm w-full">
              <thead>
                <tr className="bg-gray-50 dark:bg-slate-700 text-gray-700 dark:text-slate-200">
                  <th className="text-left px-3 py-2">Reason</th>
                  <th className="text-left px-3 py-2">Count</th>
                  <th className="text-left px-3 py-2">Total Amount</th>
                </tr>
              </thead>
              <tbody>
                {disbReasonPage.items.map((row) => (
                  <tr key={row.reason} className="border-b last:border-0">
                    <td className="px-3 py-2">{row.reason}</td>
                    <td className="px-3 py-2">{row.count}</td>
                    <td className="px-3 py-2">{toCurrency(row.total_amount)}</td>
                  </tr>
                ))}
                {data.byReason.length === 0 && (
                  <tr><td className="px-3 py-2 text-gray-500 dark:text-slate-400" colSpan={3}>No data</td></tr>
                )}
              </tbody>
              </table>
            </TableScroll>
            <Pager hideSingle className="mt-3"
              page={disbReasonPage.page}
              pages={disbReasonPage.totalPages}
              total={disbReasonPage.totalItems}
              pageSize={disbReasonPage.pageSize}
              onPage={(p) => setPages((prev) => ({ ...prev, disbReason: clamp(p, 1, disbReasonPage.totalPages) }))}
              onPageSize={(n) => { setPageSize(n); setPages((prev) => ({ ...prev, disbReason: 1 })); }}
            />
          </div>
        </div>
      </div>

      <div className={`${cardClass('blue')} p-4 pt-5`}>
        <div className="flex justify-between items-center mb-2">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-slate-100">By Month</h2>
        </div>
        <div className="w-full">
          <TableScroll>
            <table className="reports-table text-sm w-full">
            <thead>
              <tr className="bg-gray-50 dark:bg-slate-700 text-gray-700 dark:text-slate-200">
                <th className="text-left px-3 py-2">Month</th>
                <th className="text-left px-3 py-2">Count</th>
                <th className="text-left px-3 py-2">Total Amount</th>
              </tr>
            </thead>
            <tbody>
              {disbMonthPage.items.map((row) => (
                <tr key={row.month} className="border-b last:border-0">
                  <td className="px-3 py-2">{row.month}</td>
                  <td className="px-3 py-2">{row.count}</td>
                  <td className="px-3 py-2">{toCurrency(row.total_amount)}</td>
                </tr>
              ))}
              {data.byMonth.length === 0 && (
                <tr><td className="px-3 py-2 text-gray-500 dark:text-slate-400" colSpan={3}>No data</td></tr>
              )}
            </tbody>
            </table>
          </TableScroll>
          <Pager hideSingle className="mt-3"
            page={disbMonthPage.page}
            pages={disbMonthPage.totalPages}
            total={disbMonthPage.totalItems}
            pageSize={disbMonthPage.pageSize}
            onPage={(p) => setPages((prev) => ({ ...prev, disbMonth: clamp(p, 1, disbMonthPage.totalPages) }))}
            onPageSize={(n) => { setPageSize(n); setPages((prev) => ({ ...prev, disbMonth: 1 })); }}
          />
        </div>
      </div>
      </>
      )}

      {selectedReport === 'total_loans_disbursed' && (
      <div className={`${cardClass('emerald')} p-4 pt-5 mt-4`}>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-3">
          <div>
            <h2 className="text-lg font-semibold text-gray-800 dark:text-slate-100">Total number of loans disbursed during the period</h2>
            <p className="text-sm text-gray-500 dark:text-slate-400">
              Period: {dmy(totalLoansDisbursed.filters?.startDate, '—')} to {dmy(totalLoansDisbursed.filters?.endDate, '—')}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-1 gap-3">
          <div className="bg-emerald-50 border border-emerald-100 rounded-md p-3">
            <p className="text-xs uppercase text-emerald-700">Loans Disbursed</p>
            <p className="text-2xl font-semibold text-emerald-900">{totalLoansDisbursed.totals?.count ?? 0}</p>
          </div>
        </div>

        <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-md p-3 mt-4 border-t-4 border-emerald-700">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-2">
            <h3 className="text-md font-semibold text-gray-800 dark:text-slate-100">Summary</h3>
          </div>
          <TableScroll>
            <table className="reports-table text-sm w-full">
              <thead>
                <tr className="bg-gray-50 dark:bg-slate-700 text-gray-700 dark:text-slate-200">
                  <th className="text-left px-3 py-2">Metric</th>
                  <th className="text-left px-3 py-2">Value</th>
                </tr>
              </thead>
              <tbody>
                {totalLoansDisbursedSummaryPage.items.map((r) => (
                  <tr key={r.metric} className="border-b last:border-0">
                    <td className={r.bold ? 'px-3 py-2 font-semibold' : 'px-3 py-2'}>{r.metric}</td>
                    <td className={r.bold ? 'px-3 py-2 font-semibold' : 'px-3 py-2'}>{r.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScroll>
          <Pager hideSingle className="mt-3"
            page={totalLoansDisbursedSummaryPage.page}
            pages={totalLoansDisbursedSummaryPage.totalPages}
            total={totalLoansDisbursedSummaryPage.totalItems}
            pageSize={totalLoansDisbursedSummaryPage.pageSize}
            onPage={(p) => setPages((prev) => ({ ...prev, totalLoansDisbursedSummary: clamp(p, 1, totalLoansDisbursedSummaryPage.totalPages) }))}
            onPageSize={(n) => { setPageSize(n); setPages((prev) => ({ ...prev, totalLoansDisbursedSummary: 1 })); }}
          />
        </div>

        <div className="mt-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-2">
            <h3 className="text-md font-semibold text-gray-800 dark:text-slate-100">Loans (detail)</h3>
            <div className="text-xs text-gray-600 dark:text-slate-400">Showing {totalLoansDisbursedLoansPage.totalItems} loans</div>
          </div>

          <div className="w-full">
            <TableScroll>
              <table className="reports-table text-sm w-full">
              <thead>
                <tr className="bg-gray-50 dark:bg-slate-700 text-gray-700 dark:text-slate-200">
                  <th className="text-left px-3 py-2">Loan Code</th>
                  <th className="text-left px-3 py-2">Client</th>
                  <th className="text-left px-3 py-2">Principal</th>
                  <th className="text-left px-3 py-2">Duration (months)</th>
                  <th className="text-left px-3 py-2">Due Date</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-left px-3 py-2">Created</th>
                  <th className="text-left px-3 py-2">Days Overdue</th>
                  <th className="text-left px-3 py-2">Gender</th>
                </tr>
              </thead>
              <tbody>
                {totalLoansDisbursedLoansPage.items.map((r) => (
                  <tr key={r.loan_id} className="border-b last:border-0">
                    <td className="px-3 py-2">{loanRef(r) || r.loan_id}</td>
                    <td className="px-3 py-2">{r.client_name}</td>
                    <td className="px-3 py-2">{toCurrency(r.principal_amount)}</td>
                    <td className="px-3 py-2">{r.duration_months ?? '—'}</td>
                    <td className="px-3 py-2">{r.due_date ? formatDate(r.due_date) : '—'}</td>
                    <td className="px-3 py-2">{r.loan_status}</td>
                    <td className="px-3 py-2">{r.created_at ? formatDate(r.created_at) : '—'}</td>
                    <td className="px-3 py-2">{r.days_overdue ?? '—'}</td>
                    <td className="px-3 py-2 capitalize">{r.gender}</td>
                  </tr>
                ))}
                {totalLoansDisbursedLoansPage.totalItems === 0 && (
                  <tr><td className="px-3 py-2 text-gray-500 dark:text-slate-400" colSpan={9}>No loans</td></tr>
                )}
              </tbody>
              </table>
            </TableScroll>
            <Pager hideSingle className="mt-3"
              page={totalLoansDisbursedLoansPage.page}
              pages={totalLoansDisbursedLoansPage.totalPages}
              total={totalLoansDisbursedLoansPage.totalItems}
              pageSize={totalLoansDisbursedLoansPage.pageSize}
              onPage={(p) => setPages((prev) => ({ ...prev, totalLoansDisbursedLoans: clamp(p, 1, totalLoansDisbursedLoansPage.totalPages) }))}
              onPageSize={(n) => { setPageSize(n); setPages((prev) => ({ ...prev, totalLoansDisbursedLoans: 1 })); }}
            />
          </div>
        </div>
      </div>
      )}

      {selectedReport === 'loans_per_collection_method' && (
      <div className={`${cardClass('emerald')} p-4 pt-5 mt-4`}>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-3">
          <div>
            <h2 className="text-lg font-semibold text-gray-800 dark:text-slate-100">Number of Loans per collection method</h2>
            <p className="text-sm text-gray-500 dark:text-slate-400">
              Period: {dmy(loansPerCollectionMethod.filters?.startDate, '—')} to {dmy(loansPerCollectionMethod.filters?.endDate, '—')}
            </p>
          </div>
        </div>

        <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-md p-3 mb-4 border-t-4 border-emerald-700">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-2">
            <h3 className="text-md font-semibold text-gray-800 dark:text-slate-100">Summary</h3>
          </div>
          <TableScroll>
            <table className="reports-table text-sm w-full">
              <thead>
                <tr className="bg-gray-50 dark:bg-slate-700 text-gray-700 dark:text-slate-200">
                  <th className="text-left px-3 py-2">Metric</th>
                  <th className="text-left px-3 py-2">Value</th>
                </tr>
              </thead>
              <tbody>
                {loansPerCollectionMethodSummaryPage.items.map((r) => (
                  <tr key={r.metric} className="border-b last:border-0">
                    <td className={r.bold ? 'px-3 py-2 font-semibold' : 'px-3 py-2'}>{r.metric}</td>
                    <td className={r.bold ? 'px-3 py-2 font-semibold' : 'px-3 py-2'}>{r.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScroll>
          <Pager hideSingle className="mt-3"
            page={loansPerCollectionMethodSummaryPage.page}
            pages={loansPerCollectionMethodSummaryPage.totalPages}
            total={loansPerCollectionMethodSummaryPage.totalItems}
            pageSize={loansPerCollectionMethodSummaryPage.pageSize}
            onPage={(p) => setPages((prev) => ({ ...prev, loansPerCollectionMethodSummary: clamp(p, 1, loansPerCollectionMethodSummaryPage.totalPages) }))}
            onPageSize={(n) => { setPageSize(n); setPages((prev) => ({ ...prev, loansPerCollectionMethodSummary: 1 })); }}
          />
        </div>

        <TableScroll>
          <table className="reports-table text-sm w-full">
            <thead>
              <tr className="bg-gray-50 dark:bg-slate-700 text-gray-700 dark:text-slate-200">
                <th className="text-left px-3 py-2">Collection Method</th>
                <th className="text-left px-3 py-2">Number of Loans</th>
              </tr>
            </thead>
            <tbody>
              {loansPerCollectionMethodRowsPage.items.map((r) => (
                <tr key={r.collection_method} className="border-b last:border-0">
                  <td className="px-3 py-2">{r.collection_method}</td>
                  <td className="px-3 py-2">{r.loan_count}</td>
                </tr>
              ))}
              {loansPerCollectionMethodRowsPage.totalItems === 0 && (
                <tr><td className="px-3 py-2 text-gray-500 dark:text-slate-400" colSpan={2}>No data</td></tr>
              )}
            </tbody>
            {(Array.isArray(loansPerCollectionMethod.rows) ? loansPerCollectionMethod.rows : []).length > 0 && (
              <tfoot>
                <tr className="bg-gray-50 dark:bg-slate-700 text-gray-700 dark:text-slate-200">
                  <td className="px-3 py-2 font-semibold">Total</td>
                  <td className="px-3 py-2 font-semibold">
                    {loansPerCollectionMethod.totals?.count ?? 0}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </TableScroll>
        <Pager hideSingle className="mt-3"
          page={loansPerCollectionMethodRowsPage.page}
          pages={loansPerCollectionMethodRowsPage.totalPages}
          total={loansPerCollectionMethodRowsPage.totalItems}
          pageSize={loansPerCollectionMethodRowsPage.pageSize}
          onPage={(p) => setPages((prev) => ({ ...prev, loansPerCollectionMethodRows: clamp(p, 1, loansPerCollectionMethodRowsPage.totalPages) }))}
          onPageSize={(n) => { setPageSize(n); setPages((prev) => ({ ...prev, loansPerCollectionMethodRows: 1 })); }}
        />

        <div className="mt-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-2">
            <h3 className="text-md font-semibold text-gray-800 dark:text-slate-100">Loans (detail)</h3>
            <div className="text-xs text-gray-600 dark:text-slate-400">Showing {loansPerCollectionMethodLoansPage.totalItems} loans</div>
          </div>

          <div className="w-full">
            <TableScroll>
              <table className="reports-table text-sm w-full">
              <thead>
                <tr className="bg-gray-50 dark:bg-slate-700 text-gray-700 dark:text-slate-200">
                  <th className="text-left px-3 py-2">Loan Code</th>
                  <th className="text-left px-3 py-2">Client</th>
                  <th className="text-left px-3 py-2">Principal</th>
                  <th className="text-left px-3 py-2">Due Date</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-left px-3 py-2">Collection Method</th>
                  <th className="text-left px-3 py-2">Created</th>
                  <th className="text-left px-3 py-2">Days Overdue</th>
                  <th className="text-left px-3 py-2">Gender</th>
                </tr>
              </thead>
              <tbody>
                {loansPerCollectionMethodLoansPage.items.map((r) => (
                  <tr key={r.loan_id} className="border-b last:border-0">
                    <td className="px-3 py-2">{loanRef(r) || r.loan_id}</td>
                    <td className="px-3 py-2">{r.client_name}</td>
                    <td className="px-3 py-2">{toCurrency(r.principal_amount)}</td>
                    <td className="px-3 py-2">{r.due_date ? formatDate(r.due_date) : '—'}</td>
                    <td className="px-3 py-2">{r.loan_status}</td>
                    <td className="px-3 py-2">{r.collection_method}</td>
                    <td className="px-3 py-2">{r.created_at ? formatDate(r.created_at) : '—'}</td>
                    <td className="px-3 py-2">{r.days_overdue ?? '—'}</td>
                    <td className="px-3 py-2 capitalize">{r.gender}</td>
                  </tr>
                ))}
                {loansPerCollectionMethodLoansPage.totalItems === 0 && (
                  <tr><td className="px-3 py-2 text-gray-500 dark:text-slate-400" colSpan={9}>No loans</td></tr>
                )}
              </tbody>
              </table>
            </TableScroll>
            <Pager hideSingle className="mt-3"
              page={loansPerCollectionMethodLoansPage.page}
              pages={loansPerCollectionMethodLoansPage.totalPages}
              total={loansPerCollectionMethodLoansPage.totalItems}
              pageSize={loansPerCollectionMethodLoansPage.pageSize}
              onPage={(p) => setPages((prev) => ({ ...prev, loansPerCollectionMethodLoans: clamp(p, 1, loansPerCollectionMethodLoansPage.totalPages) }))}
              onPageSize={(n) => { setPageSize(n); setPages((prev) => ({ ...prev, loansPerCollectionMethodLoans: 1 })); }}
            />
          </div>
        </div>
      </div>
      )}

      {selectedReport === 'loans_by_purpose' && (
      <div className={`${cardClass('emerald')} p-4 pt-5 mt-4`}>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-3">
          <div>
            <h2 className="text-lg font-semibold text-gray-800 dark:text-slate-100">Number of loans by purpose</h2>
            <p className="text-sm text-gray-500 dark:text-slate-400">
              Period: {dmy(loansByPurpose.filters?.startDate, '—')} to {dmy(loansByPurpose.filters?.endDate, '—')}
            </p>
          </div>
        </div>

        <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-md p-3 mb-4 border-t-4 border-emerald-700">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-2">
            <h3 className="text-md font-semibold text-gray-800 dark:text-slate-100">Summary</h3>
          </div>
          <TableScroll>
            <table className="reports-table text-sm w-full">
              <thead>
                <tr className="bg-gray-50 dark:bg-slate-700 text-gray-700 dark:text-slate-200">
                  <th className="text-left px-3 py-2">Metric</th>
                  <th className="text-left px-3 py-2">Value</th>
                </tr>
              </thead>
              <tbody>
                {loansByPurposeSummaryPage.items.map((r) => (
                  <tr key={r.metric} className="border-b last:border-0">
                    <td className={r.bold ? 'px-3 py-2 font-semibold' : 'px-3 py-2'}>{r.metric}</td>
                    <td className={r.bold ? 'px-3 py-2 font-semibold' : 'px-3 py-2'}>{r.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScroll>
          <Pager hideSingle className="mt-3"
            page={loansByPurposeSummaryPage.page}
            pages={loansByPurposeSummaryPage.totalPages}
            total={loansByPurposeSummaryPage.totalItems}
            pageSize={loansByPurposeSummaryPage.pageSize}
            onPage={(p) => setPages((prev) => ({ ...prev, loansByPurposeSummary: clamp(p, 1, loansByPurposeSummaryPage.totalPages) }))}
            onPageSize={(n) => { setPageSize(n); setPages((prev) => ({ ...prev, loansByPurposeSummary: 1 })); }}
          />
        </div>

        <TableScroll>
          <table className="reports-table text-sm w-full">
            <thead>
              <tr className="bg-gray-50 dark:bg-slate-700 text-gray-700 dark:text-slate-200">
                <th className="text-left px-3 py-2">Purpose</th>
                <th className="text-left px-3 py-2">Number of Loans</th>
              </tr>
            </thead>
            <tbody>
              {loansByPurposeRowsPage.items.map((r) => (
                <tr key={r.purpose} className="border-b last:border-0">
                  <td className="px-3 py-2">{r.purpose}</td>
                  <td className="px-3 py-2">{r.loan_count}</td>
                </tr>
              ))}
              {loansByPurposeRowsPage.totalItems === 0 && (
                <tr><td className="px-3 py-2 text-gray-500 dark:text-slate-400" colSpan={2}>No data</td></tr>
              )}
            </tbody>
            {(Array.isArray(loansByPurpose.rows) ? loansByPurpose.rows : []).length > 0 && (
              <tfoot>
                <tr className="bg-gray-50 dark:bg-slate-700 text-gray-700 dark:text-slate-200">
                  <td className="px-3 py-2 font-semibold">Total</td>
                  <td className="px-3 py-2 font-semibold">
                    {loansByPurposeTotalLoans}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </TableScroll>
        <Pager hideSingle className="mt-3"
          page={loansByPurposeRowsPage.page}
          pages={loansByPurposeRowsPage.totalPages}
          total={loansByPurposeRowsPage.totalItems}
          pageSize={loansByPurposeRowsPage.pageSize}
          onPage={(p) => setPages((prev) => ({ ...prev, loansByPurposeRows: clamp(p, 1, loansByPurposeRowsPage.totalPages) }))}
          onPageSize={(n) => { setPageSize(n); setPages((prev) => ({ ...prev, loansByPurposeRows: 1 })); }}
        />

        <div className="mt-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-2">
            <h3 className="text-md font-semibold text-gray-800 dark:text-slate-100">Loans (detail)</h3>
            <div className="text-xs text-gray-600 dark:text-slate-400">Showing {loansByPurposeLoansPage.totalItems} loans</div>
          </div>

          <div className="w-full">
            <TableScroll>
              <table className="reports-table text-sm w-full">
              <thead>
                <tr className="bg-gray-50 dark:bg-slate-700 text-gray-700 dark:text-slate-200">
                  <th className="text-left px-3 py-2">Loan Code</th>
                  <th className="text-left px-3 py-2">Client</th>
                  <th className="text-left px-3 py-2">Principal</th>
                  <th className="text-left px-3 py-2">Due Date</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-left px-3 py-2">Purpose</th>
                  <th className="text-left px-3 py-2">Created</th>
                  <th className="text-left px-3 py-2">Days Overdue</th>
                  <th className="text-left px-3 py-2">Gender</th>
                </tr>
              </thead>
              <tbody>
                {loansByPurposeLoansPage.items.map((r) => (
                  <tr key={r.loan_id} className="border-b last:border-0">
                    <td className="px-3 py-2">{loanRef(r) || r.loan_id}</td>
                    <td className="px-3 py-2">{r.client_name}</td>
                    <td className="px-3 py-2">{toCurrency(r.principal_amount)}</td>
                    <td className="px-3 py-2">{r.due_date ? formatDate(r.due_date) : '—'}</td>
                    <td className="px-3 py-2">{r.loan_status}</td>
                    <td className="px-3 py-2">{r.purpose}</td>
                    <td className="px-3 py-2">{r.created_at ? formatDate(r.created_at) : '—'}</td>
                    <td className="px-3 py-2">{r.days_overdue ?? '—'}</td>
                    <td className="px-3 py-2 capitalize">{r.gender}</td>
                  </tr>
                ))}
                {loansByPurposeLoansPage.totalItems === 0 && (
                  <tr><td className="px-3 py-2 text-gray-500 dark:text-slate-400" colSpan={9}>No loans</td></tr>
                )}
              </tbody>
              </table>
            </TableScroll>
            <Pager hideSingle className="mt-3"
              page={loansByPurposeLoansPage.page}
              pages={loansByPurposeLoansPage.totalPages}
              total={loansByPurposeLoansPage.totalItems}
              pageSize={loansByPurposeLoansPage.pageSize}
              onPage={(p) => setPages((prev) => ({ ...prev, loansByPurposeLoans: clamp(p, 1, loansByPurposeLoansPage.totalPages) }))}
              onPageSize={(n) => { setPageSize(n); setPages((prev) => ({ ...prev, loansByPurposeLoans: 1 })); }}
            />
          </div>
        </div>
      </div>
      )}

      {selectedReport === 'disbursements_gender_ranges' && (
      <div className={`${cardClass('cyan')} p-4 pt-5 mt-4`}>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-3">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-slate-100">Disbursed loans by gender (amount ranges)</h2>
          <div className="text-sm text-gray-600 dark:text-slate-400">
            Total Loans: <span className="font-semibold">{disbGenderRangesPivot.grand.totalCount}</span> · Total Amount: <span className="font-semibold">{toCurrency(disbGenderRangesPivot.grand.totalAmount)}</span>
          </div>
        </div>

        <TableScroll>
          <table className="reports-table text-sm w-full">
            <thead>
              <tr className="bg-gray-50 dark:bg-slate-700 text-gray-700 dark:text-slate-200">
                <th className="text-left px-3 py-2">Gender</th>
                {disbGenderRangesPivot.columns.map((col) => (
                  <th key={col} className="text-left px-3 py-2">{col}</th>
                ))}
                <th className="text-left px-3 py-2">Total</th>
              </tr>
            </thead>
            <tbody>
              {disbGenderRangesPivotPage.items.map((row) => (
                <tr key={row.gender} className="border-b last:border-0">
                  <td className="px-3 py-2 capitalize">{row.gender}</td>
                  {disbGenderRangesPivot.columns.map((col) => {
                    const cell = row.cells[col] || { count: 0, total_amount: 0 };
                    return (
                      <td key={col} className="px-3 py-2">
                        <div className="font-medium">{cell.count}</div>
                        <div className="text-xs text-gray-600 dark:text-slate-400">{toCurrency(cell.total_amount)}</div>
                      </td>
                    );
                  })}
                  <td className="px-3 py-2">
                    <div className="font-semibold">{row.totalCount}</div>
                    <div className="text-xs text-gray-700">{toCurrency(row.totalAmount)}</div>
                  </td>
                </tr>
              ))}

              {disbGenderRangesPivotPage.totalItems === 0 && (
                <tr><td className="px-3 py-2 text-gray-500 dark:text-slate-400" colSpan={disbGenderRangesPivot.columns.length + 2}>No data</td></tr>
              )}
            </tbody>
            {disbGenderRangesPivot.tableRows.length > 0 && (
              <tfoot>
                <tr className="bg-gray-50 dark:bg-slate-700 text-gray-700 dark:text-slate-200">
                  <td className="px-3 py-2 font-semibold">Total</td>
                  {disbGenderRangesPivot.columns.map((col) => (
                    <td key={col} className="px-3 py-2">
                      <div className="font-semibold">{disbGenderRangesPivot.columnTotals[col]?.count || 0}</div>
                      <div className="text-xs text-gray-700">{toCurrency(disbGenderRangesPivot.columnTotals[col]?.total_amount || 0)}</div>
                    </td>
                  ))}
                  <td className="px-3 py-2">
                    <div className="font-semibold">{disbGenderRangesPivot.grand.totalCount}</div>
                    <div className="text-xs text-gray-700">{toCurrency(disbGenderRangesPivot.grand.totalAmount)}</div>
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </TableScroll>
        <Pager hideSingle className="mt-3"
          page={disbGenderRangesPivotPage.page}
          pages={disbGenderRangesPivotPage.totalPages}
          total={disbGenderRangesPivotPage.totalItems}
          pageSize={disbGenderRangesPivotPage.pageSize}
          onPage={(p) => setPages((prev) => ({ ...prev, disbGenderRangesPivot: clamp(p, 1, disbGenderRangesPivotPage.totalPages) }))}
          onPageSize={(n) => { setPageSize(n); setPages((prev) => ({ ...prev, disbGenderRangesPivot: 1 })); }}
        />

        <div className="mt-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-2">
            <h3 className="text-md font-semibold text-gray-800 dark:text-slate-100">Loans (detail)</h3>
            <div className="text-xs text-gray-600 dark:text-slate-400">Showing {disbGenderRangesLoansPage.totalItems} loans</div>
          </div>

          <div className="w-full">
            <TableScroll>
              <table className="reports-table text-sm w-full">
              <thead>
                <tr className="bg-gray-50 dark:bg-slate-700 text-gray-700 dark:text-slate-200">
                  <th className="text-left px-3 py-2">Loan Code</th>
                  <th className="text-left px-3 py-2">Client</th>
                  <th className="text-left px-3 py-2">Principal</th>
                  <th className="text-left px-3 py-2">Due Date</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-left px-3 py-2">Amount Range</th>
                  <th className="text-left px-3 py-2">Created</th>
                  <th className="text-left px-3 py-2">Days Overdue</th>
                  <th className="text-left px-3 py-2">Gender</th>
                </tr>
              </thead>
              <tbody>
                {disbGenderRangesLoansPage.items.map((r) => (
                  <tr key={r.loan_id} className="border-b last:border-0">
                    <td className="px-3 py-2">{loanRef(r) || r.loan_id}</td>
                    <td className="px-3 py-2">{r.client_name}</td>
                    <td className="px-3 py-2">{toCurrency(r.principal_amount)}</td>
                    <td className="px-3 py-2">{r.due_date ? formatDate(r.due_date) : '—'}</td>
                    <td className="px-3 py-2">{r.loan_status}</td>
                    <td className="px-3 py-2">{r.amount_range}</td>
                    <td className="px-3 py-2">{r.created_at ? formatDate(r.created_at) : '—'}</td>
                    <td className="px-3 py-2">{r.days_overdue ?? '—'}</td>
                    <td className="px-3 py-2 capitalize">{r.gender}</td>
                  </tr>
                ))}
                {disbGenderRangesLoansPage.totalItems === 0 && (
                  <tr><td className="px-3 py-2 text-gray-500 dark:text-slate-400" colSpan={9}>No loans</td></tr>
                )}
              </tbody>
              </table>
            </TableScroll>
            <Pager hideSingle className="mt-3"
              page={disbGenderRangesLoansPage.page}
              pages={disbGenderRangesLoansPage.totalPages}
              total={disbGenderRangesLoansPage.totalItems}
              pageSize={disbGenderRangesLoansPage.pageSize}
              onPage={(p) => setPages((prev) => ({ ...prev, disbGenderRangesLoans: clamp(p, 1, disbGenderRangesLoansPage.totalPages) }))}
              onPageSize={(n) => { setPageSize(n); setPages((prev) => ({ ...prev, disbGenderRangesLoans: 1 })); }}
            />
          </div>
        </div>
      </div>
      )}

      {selectedReport === 'borrower_salaries_by_gender' && (
      <div className={`${cardClass('teal')} p-4 pt-5 mt-4`}>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-3">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-slate-100">Borrower salaries by gender</h2>
          <div className="text-sm text-gray-600 dark:text-slate-400">
            Total Borrowers: <span className="font-semibold">{borrowerSalariesByGenderPivot.grand.totalCount}</span> · Total Salary: <span className="font-semibold">{toCurrency(borrowerSalariesByGenderPivot.grand.totalSalary)}</span>
          </div>
        </div>

        <TableScroll>
          <table className="reports-table text-sm w-full">
            <thead>
              <tr className="bg-gray-50 dark:bg-slate-700 text-gray-700 dark:text-slate-200">
                <th className="text-left px-3 py-2">Gender</th>
                {borrowerSalariesByGenderPivot.columns.map((col) => (
                  <th key={col} className="text-left px-3 py-2">{col}</th>
                ))}
                <th className="text-left px-3 py-2">Total</th>
              </tr>
            </thead>
            <tbody>
              {borrowerSalariesByGenderPivotPage.items.map((row) => (
                <tr key={row.gender} className="border-b last:border-0">
                  <td className="px-3 py-2 capitalize">{row.gender}</td>
                  {borrowerSalariesByGenderPivot.columns.map((col) => {
                    const cell = row.cells[col] || { count: 0, total_salary: 0 };
                    return (
                      <td key={col} className="px-3 py-2">
                        <div className="font-medium">{cell.count}</div>
                        <div className="text-xs text-gray-600 dark:text-slate-400">{toCurrency(cell.total_salary)}</div>
                      </td>
                    );
                  })}
                  <td className="px-3 py-2">
                    <div className="font-semibold">{row.totalCount}</div>
                    <div className="text-xs text-gray-700">{toCurrency(row.totalSalary)}</div>
                  </td>
                </tr>
              ))}

              {borrowerSalariesByGenderPivotPage.totalItems === 0 && (
                <tr><td className="px-3 py-2 text-gray-500 dark:text-slate-400" colSpan={borrowerSalariesByGenderPivot.columns.length + 2}>No data</td></tr>
              )}
            </tbody>
            {borrowerSalariesByGenderPivot.tableRows.length > 0 && (
              <tfoot>
                <tr className="bg-gray-50 dark:bg-slate-700 text-gray-700 dark:text-slate-200">
                  <td className="px-3 py-2 font-semibold">Total</td>
                  {borrowerSalariesByGenderPivot.columns.map((col) => (
                    <td key={col} className="px-3 py-2">
                      <div className="font-semibold">{borrowerSalariesByGenderPivot.columnTotals[col]?.count || 0}</div>
                      <div className="text-xs text-gray-700">{toCurrency(borrowerSalariesByGenderPivot.columnTotals[col]?.total_salary || 0)}</div>
                    </td>
                  ))}
                  <td className="px-3 py-2">
                    <div className="font-semibold">{borrowerSalariesByGenderPivot.grand.totalCount}</div>
                    <div className="text-xs text-gray-700">{toCurrency(borrowerSalariesByGenderPivot.grand.totalSalary)}</div>
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </TableScroll>
        <Pager hideSingle className="mt-3"
          page={borrowerSalariesByGenderPivotPage.page}
          pages={borrowerSalariesByGenderPivotPage.totalPages}
          total={borrowerSalariesByGenderPivotPage.totalItems}
          pageSize={borrowerSalariesByGenderPivotPage.pageSize}
          onPage={(p) => setPages((prev) => ({ ...prev, borrowerSalariesByGenderPivot: clamp(p, 1, borrowerSalariesByGenderPivotPage.totalPages) }))}
          onPageSize={(n) => { setPageSize(n); setPages((prev) => ({ ...prev, borrowerSalariesByGenderPivot: 1 })); }}
        />

        <div className="mt-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-2">
            <h3 className="text-md font-semibold text-gray-800 dark:text-slate-100">Borrowers (detail)</h3>
            <div className="text-xs text-gray-600 dark:text-slate-400">Showing {borrowerSalariesByGenderBorrowersPage.totalItems} borrowers</div>
          </div>

          <div className="w-full">
            <TableScroll>
              <table className="reports-table text-sm w-full">
              <thead>
                <tr className="bg-gray-50 dark:bg-slate-700 text-gray-700 dark:text-slate-200">
                  <th className="text-left px-3 py-2">Client Code</th>
                  <th className="text-left px-3 py-2">Client</th>
                  <th className="text-left px-3 py-2">Gender</th>
                  <th className="text-left px-3 py-2">Salary</th>
                  <th className="text-left px-3 py-2">Salary Range</th>
                </tr>
              </thead>
              <tbody>
                {borrowerSalariesByGenderBorrowersPage.items.map((r) => (
                  <tr key={r.client_id} className="border-b last:border-0">
                    <td className="px-3 py-2">{r.client_code || '—'}</td>
                    <td className="px-3 py-2">{r.client_name}</td>
                    <td className="px-3 py-2 capitalize">{r.gender}</td>
                    <td className="px-3 py-2">{toCurrency(r.salary)}</td>
                    <td className="px-3 py-2">{r.salary_range}</td>
                  </tr>
                ))}
                {borrowerSalariesByGenderBorrowersPage.totalItems === 0 && (
                  <tr><td className="px-3 py-2 text-gray-500 dark:text-slate-400" colSpan={5}>No borrowers</td></tr>
                )}
              </tbody>
              </table>
            </TableScroll>
            <Pager hideSingle className="mt-3"
              page={borrowerSalariesByGenderBorrowersPage.page}
              pages={borrowerSalariesByGenderBorrowersPage.totalPages}
              total={borrowerSalariesByGenderBorrowersPage.totalItems}
              pageSize={borrowerSalariesByGenderBorrowersPage.pageSize}
              onPage={(p) => setPages((prev) => ({ ...prev, borrowerSalariesByGenderBorrowers: clamp(p, 1, borrowerSalariesByGenderBorrowersPage.totalPages) }))}
              onPageSize={(n) => { setPageSize(n); setPages((prev) => ({ ...prev, borrowerSalariesByGenderBorrowers: 1 })); }}
            />
          </div>
        </div>
      </div>
      )}

      {selectedReport === 'number_of_clients' && (
      <div className={`${cardClass('slate')} p-4 pt-5 mt-4`}>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-3">
          <div>
            <h2 className="text-lg font-semibold text-gray-800 dark:text-slate-100">Total number of clients in book at last day of reporting period (Active Clients)</h2>
            <p className="text-sm text-gray-500 dark:text-slate-400">As of: {dmy(numberOfClients.filters?.endDate, '—')}</p>
          </div>
          <div className="text-sm text-gray-600 dark:text-slate-400">
            Active Clients: <span className="font-semibold">{numberOfClients.totals.active_clients}</span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
          <div className="bg-slate-50 border border-slate-100 rounded-md p-3">
            <p className="text-xs uppercase text-slate-600">Active Clients</p>
            <p className="text-lg font-semibold text-slate-800">{numberOfClients.totals.active_clients}</p>
          </div>
          <div className="bg-slate-50 border border-slate-100 rounded-md p-3">
            <p className="text-xs uppercase text-slate-600">Outstanding Loans</p>
            <p className="text-lg font-semibold text-slate-800">{numberOfClients.totals.loan_count}</p>
          </div>
          <div className="bg-slate-50 border border-slate-100 rounded-md p-3">
            <p className="text-xs uppercase text-slate-600">Total Outstanding</p>
            <p className="text-lg font-semibold text-slate-800">{toCurrency(numberOfClients.totals.total_outstanding)}</p>
          </div>
        </div>

        <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-md p-3 border-t-4 border-slate-700">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-2">
            <h3 className="text-md font-semibold text-gray-800 dark:text-slate-100">Loans (in book as of period end)</h3>
            <div className="text-xs text-gray-600 dark:text-slate-400">Showing {numberOfClientsLoansPage.totalItems} loans</div>
          </div>
          <TableScroll>
            <table className="reports-table text-sm w-full">
            <thead>
              <tr className="bg-gray-50 dark:bg-slate-700 text-gray-700 dark:text-slate-200">
                <th className="text-left px-3 py-2">Loan Code</th>
                <th className="text-left px-3 py-2">Client</th>
                <th className="text-left px-3 py-2">Due Date</th>
                <th className="text-left px-3 py-2">Outstanding</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-left px-3 py-2">Created</th>
                <th className="text-left px-3 py-2">Days Overdue</th>
                <th className="text-left px-3 py-2">Total Due</th>
                <th className="text-left px-3 py-2">Paid</th>
              </tr>
            </thead>
            <tbody>
              {numberOfClientsLoansPage.items.map((r) => (
                <tr key={r.loan_id} className="border-b last:border-0">
                  <td className="px-3 py-2">{loanRef(r) || r.loan_id}</td>
                  <td className="px-3 py-2">{r.client_name}</td>
                  <td className="px-3 py-2">{r.due_date ? formatDate(r.due_date) : '—'}</td>
                  <td className="px-3 py-2">{toCurrency(r.outstanding)}</td>
                  <td className="px-3 py-2">{r.loan_status}</td>
                  <td className="px-3 py-2">{r.created_at ? formatDate(r.created_at) : '—'}</td>
                  <td className="px-3 py-2">{r.days_overdue ?? '—'}</td>
                  <td className="px-3 py-2">{toCurrency(r.total_due)}</td>
                  <td className="px-3 py-2">{toCurrency(r.total_paid)}</td>
                </tr>
              ))}
              {numberOfClientsLoansPage.totalItems === 0 && (
                <tr><td className="px-3 py-2 text-gray-500 dark:text-slate-400" colSpan={9}>No loans</td></tr>
              )}
            </tbody>
            </table>
          </TableScroll>
          <Pager hideSingle className="mt-3"
            page={numberOfClientsLoansPage.page}
            pages={numberOfClientsLoansPage.totalPages}
            total={numberOfClientsLoansPage.totalItems}
            pageSize={numberOfClientsLoansPage.pageSize}
            onPage={(p) => setPages((prev) => ({ ...prev, numberOfClientsLoans: clamp(p, 1, numberOfClientsLoansPage.totalPages) }))}
            onPageSize={(n) => { setPageSize(n); setPages((prev) => ({ ...prev, numberOfClientsLoans: 1 })); }}
          />
        </div>
      </div>
      )}

      {selectedReport === 'loans_outstanding_numbers' && (
      <div className={`${cardClass('indigo')} p-4 pt-5 mt-4`}>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-3">
          <div>
            <h2 className="text-lg font-semibold text-gray-800 dark:text-slate-100">Number of loans outstanding</h2>
            <p className="text-sm text-gray-500 dark:text-slate-400">As of: {dmy(loansOutstandingNumbers.filters?.endDate, '—')}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
          <div className="bg-indigo-50 border border-indigo-100 rounded-md p-3">
            <p className="text-xs uppercase text-indigo-700">Current loans</p>
            <p className="text-lg font-semibold text-indigo-800">{loansOutstandingNumbers.current.count}</p>
            <p className="text-sm text-indigo-700">Outstanding: {toCurrency(loansOutstandingNumbers.current.total_outstanding)}</p>
          </div>
          <div className="bg-rose-50 border border-rose-100 rounded-md p-3">
            <p className="text-xs uppercase text-rose-700">Loans in arrears</p>
            <p className="text-lg font-semibold text-rose-800">{loansOutstandingNumbers.arrears.count}</p>
            <p className="text-sm text-rose-700">Outstanding: {toCurrency(loansOutstandingNumbers.arrears.total_outstanding)}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4">
          <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-md p-3 border-t-4 border-indigo-700">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-2">
              <h3 className="text-md font-semibold text-gray-800 dark:text-slate-100">Current loans (detail)</h3>
              <div className="text-xs text-gray-600 dark:text-slate-400">Showing {loansOutstandingCurrentPage.totalItems} loans</div>
            </div>
            <TableScroll>
              <table className="reports-table text-sm w-full">
              <thead>
                <tr className="bg-gray-50 dark:bg-slate-700 text-gray-700 dark:text-slate-200">
                  <th className="text-left px-3 py-2">Loan Code</th>
                  <th className="text-left px-3 py-2">Client</th>
                  <th className="text-left px-3 py-2">Due Date</th>
                  <th className="text-left px-3 py-2">Outstanding</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-left px-3 py-2">Created</th>
                  <th className="text-left px-3 py-2">Days Overdue</th>
                  <th className="text-left px-3 py-2">Paid</th>
                </tr>
              </thead>
              <tbody>
                {loansOutstandingCurrentPage.items.map((r) => (
                  <tr key={r.loan_id} className="border-b last:border-0">
                    <td className="px-3 py-2">{loanRef(r) || r.loan_id}</td>
                    <td className="px-3 py-2">{r.client_name}</td>
                    <td className="px-3 py-2">{r.due_date ? formatDate(r.due_date) : '—'}</td>
                    <td className="px-3 py-2">{toCurrency(r.outstanding)}</td>
                    <td className="px-3 py-2">{r.loan_status}</td>
                    <td className="px-3 py-2">{r.created_at ? formatDate(r.created_at) : '—'}</td>
                    <td className="px-3 py-2">{r.days_overdue ?? '—'}</td>
                    <td className="px-3 py-2">{toCurrency(r.total_paid)}</td>
                  </tr>
                ))}
                {loansOutstandingCurrentPage.totalItems === 0 && (
                  <tr><td className="px-3 py-2 text-gray-500 dark:text-slate-400" colSpan={8}>No loans</td></tr>
                )}
              </tbody>
              </table>
            </TableScroll>
            <Pager hideSingle className="mt-3"
              page={loansOutstandingCurrentPage.page}
              pages={loansOutstandingCurrentPage.totalPages}
              total={loansOutstandingCurrentPage.totalItems}
              pageSize={loansOutstandingCurrentPage.pageSize}
              onPage={(p) => setPages((prev) => ({ ...prev, loansOutstandingCurrent: clamp(p, 1, loansOutstandingCurrentPage.totalPages) }))}
              onPageSize={(n) => { setPageSize(n); setPages((prev) => ({ ...prev, loansOutstandingCurrent: 1 })); }}
            />
          </div>

          <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-md p-3 border-t-4 border-rose-700">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-2">
              <h3 className="text-md font-semibold text-gray-800 dark:text-slate-100">Loans in arrears (detail)</h3>
              <div className="text-xs text-gray-600 dark:text-slate-400">Showing {loansOutstandingArrearsPage.totalItems} loans</div>
            </div>
            <TableScroll>
              <table className="reports-table text-sm w-full">
              <thead>
                <tr className="bg-gray-50 dark:bg-slate-700 text-gray-700 dark:text-slate-200">
                  <th className="text-left px-3 py-2">Loan Code</th>
                  <th className="text-left px-3 py-2">Client</th>
                  <th className="text-left px-3 py-2">Due Date</th>
                  <th className="text-left px-3 py-2">Outstanding</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-left px-3 py-2">Created</th>
                  <th className="text-left px-3 py-2">Days Overdue</th>
                  <th className="text-left px-3 py-2">Paid</th>
                </tr>
              </thead>
              <tbody>
                {loansOutstandingArrearsPage.items.map((r) => (
                  <tr key={r.loan_id} className="border-b last:border-0">
                    <td className="px-3 py-2">{loanRef(r) || r.loan_id}</td>
                    <td className="px-3 py-2">{r.client_name}</td>
                    <td className="px-3 py-2">{r.due_date ? formatDate(r.due_date) : '—'}</td>
                    <td className="px-3 py-2">{toCurrency(r.outstanding)}</td>
                    <td className="px-3 py-2">{r.loan_status}</td>
                    <td className="px-3 py-2">{r.created_at ? formatDate(r.created_at) : '—'}</td>
                    <td className="px-3 py-2">{r.days_overdue ?? '—'}</td>
                    <td className="px-3 py-2">{toCurrency(r.total_paid)}</td>
                  </tr>
                ))}
                {loansOutstandingArrearsPage.totalItems === 0 && (
                  <tr><td className="px-3 py-2 text-gray-500 dark:text-slate-400" colSpan={8}>No loans</td></tr>
                )}
              </tbody>
              </table>
            </TableScroll>
            <Pager hideSingle className="mt-3"
              page={loansOutstandingArrearsPage.page}
              pages={loansOutstandingArrearsPage.totalPages}
              total={loansOutstandingArrearsPage.totalItems}
              pageSize={loansOutstandingArrearsPage.pageSize}
              onPage={(p) => setPages((prev) => ({ ...prev, loansOutstandingArrears: clamp(p, 1, loansOutstandingArrearsPage.totalPages) }))}
              onPageSize={(n) => { setPageSize(n); setPages((prev) => ({ ...prev, loansOutstandingArrears: 1 })); }}
            />
          </div>
        </div>
      </div>
      )}

      {selectedReport === 'disbursements_duration_breakdown' && (
      <>
        <div className="grid grid-cols-1 gap-4 mb-4">
          <div className={`${cardClass('blue')} p-4 pt-5`}>
            <div className="flex justify-between items-center mb-2">
              <h2 className="text-lg font-semibold text-gray-800 dark:text-slate-100">Loan disbursement breakdown (1–5 months)</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
              {(Array.isArray(disbDurationBreakdown.rows) ? disbDurationBreakdown.rows : []).map((r) => (
                <div key={r.duration_months} className="bg-blue-50 border border-blue-100 rounded-lg p-3">
                  <p className="text-xs uppercase text-blue-600 font-semibold">{r.duration_months} month{Number(r.duration_months) === 1 ? '' : 's'}</p>
                  <p className="text-sm text-gray-700 dark:text-slate-300 mt-1">Loans: <span className="font-semibold">{r.loan_count}</span></p>
                  <p className="text-sm text-gray-700 dark:text-slate-300">Amount: <span className="font-semibold">{toCurrency(r.total_amount)}</span></p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {[1, 2, 3, 4, 5, 6].map((durationMonths) => {
          const page = disbDurationBreakdownLoansByDuration?.[durationMonths] || paginateArray([], 1, pageSize);

          return (
            <div key={durationMonths} className="grid grid-cols-1 gap-4 mb-4">
              <div className={`${cardClass('indigo')} p-4 pt-5`}>
                <div className="flex justify-between items-center mb-2">
                  <h2 className="text-lg font-semibold text-gray-800 dark:text-slate-100">Loans disbursed ({durationMonths === 6 ? '6 months or longer' : `${durationMonths} month${durationMonths === 1 ? '' : 's'}`})</h2>
                </div>
                <div className="w-full">
                  <TableScroll>
                    <table className="reports-table text-sm w-full">
                      <thead>
                        <tr className="bg-gray-50 dark:bg-slate-700 text-gray-700 dark:text-slate-200">
                          <th className="text-left px-3 py-2">Loan Code</th>
                          <th className="text-left px-3 py-2">Client</th>
                          <th className="text-left px-3 py-2">Principal</th>
                          <th className="text-left px-3 py-2">Disbursed</th>
                          <th className="text-left px-3 py-2">Status</th>
                          <th className="text-left px-3 py-2">Duration</th>
                          <th className="text-left px-3 py-2">Gender</th>
                        </tr>
                      </thead>
                      <tbody>
                        {page.items.map((r) => (
                          <tr key={`${r.loan_id}-${r.duration_months}`} className="border-b last:border-0">
                            <td className="px-3 py-2">{loanRef(r) || r.loan_id}</td>
                            <td className="px-3 py-2">{r.client_name || '—'}</td>
                            <td className="px-3 py-2">{toCurrency(r.principal_amount)}</td>
                            <td className="px-3 py-2">{r.disbursement_date ? formatDate(r.disbursement_date) : '—'}</td>
                            <td className="px-3 py-2">{r.loan_status}</td>
                            <td className="px-3 py-2">{r.duration_months}m</td>
                            <td className="px-3 py-2 capitalize">{r.gender || 'unknown'}</td>
                          </tr>
                        ))}
                        {page.totalItems === 0 && (
                          <tr><td className="px-3 py-2 text-gray-500 dark:text-slate-400" colSpan={7}>No loans</td></tr>
                        )}
                      </tbody>
                    </table>
                  </TableScroll>
                  <Pager hideSingle className="mt-3"
                    page={page.page}
                    pages={page.totalPages}
                    total={page.totalItems}
                    pageSize={page.pageSize}
                    onPage={(p) => setPages((prev) => ({
                      ...prev,
                      [`disbDurationBreakdownLoans${durationMonths}`]: clamp(p, 1, page.totalPages),
                    }))}
                    onPageSize={(n) => {
                      setPageSize(n);
                      setPages((prev) => ({ ...prev, [`disbDurationBreakdownLoans${durationMonths}`]: 1 }));
                    }}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </>
      )}

      {selectedReport === 'overdue' && (
      <div className={`${cardClass('amber')} p-4 pt-5 mt-4`}>
        <div className="flex justify-between items-center mb-2">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-slate-100">Overdue Loans (with penalty interest)</h2>
          <span className="text-sm text-gray-500 dark:text-slate-400">Total Amount Due (incl. penalties): {toCurrency((Number(overdue.totals.total_amount_due) || 0))}</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
          <div className="bg-amber-50 border border-amber-100 rounded-md p-3">
            <p className="text-xs uppercase text-amber-700">Loans</p>
            <p className="text-lg font-semibold text-amber-800">{overdue.totals.count}</p>
          </div>
          <div className="bg-amber-50 border border-amber-100 rounded-md p-3">
            <p className="text-xs uppercase text-amber-700">Penalty Interest</p>
            <p className="text-lg font-semibold text-amber-800">{toCurrency(overdue.totals.total_penalty_interest)}</p>
          </div>
          <div className="bg-amber-50 border border-amber-100 rounded-md p-3">
            <p className="text-xs uppercase text-amber-700">Total Amount Due</p>
            <p className="text-lg font-semibold text-amber-800">{toCurrency(overdue.totals.total_amount_due)}</p>
          </div>
        </div>

        <div className="w-full">
          <TableScroll>
            <table className="reports-table text-sm w-full">
              <thead>
                <tr className="bg-gray-50 dark:bg-slate-700 text-gray-700 dark:text-slate-200">
                  <th className="text-left px-3 py-2">Loan Code</th>
                  <th className="text-left px-3 py-2">Client</th>
                  <th className="text-left px-3 py-2">Gender</th>
                  <th className="text-left px-3 py-2">Created</th>
                  <th className="text-left px-3 py-2">Due Date</th>
                  <th className="text-left px-3 py-2">Days Overdue</th>
                  <th className="text-left px-3 py-2">Amount Due</th>
                  <th className="text-left px-3 py-2">Penalty Interest</th>
                  <th className="text-left px-3 py-2">Total Due</th>
                  <th className="text-left px-3 py-2">Loan Status</th>
                </tr>
              </thead>
              <tbody>
                {overduePage.items.map((row) => (
                  <tr key={row.loan_id} className="border-b last:border-0">
                    <td className="px-3 py-2">{loanRef(row) || row.loan_id}</td>
                    <td className="px-3 py-2">{row.client_name}</td>
                    <td className="px-3 py-2 capitalize">{row.gender}</td>
                    <td className="px-3 py-2">{row.created_at ? formatDate(row.created_at) : '-'}</td>
                    <td className="px-3 py-2">{row.due_date ? formatDate(row.due_date) : '-'}</td>
                    <td className="px-3 py-2">{row.days_overdue}</td>
                    <td className="px-3 py-2">{toCurrency(row.amount_due)}</td>
                    <td className="px-3 py-2">{toCurrency(row.overdue_interest)}</td>
                    <td className="px-3 py-2">{toCurrency(row.total_due ?? ((Number(row.amount_due) || 0) + (Number(row.overdue_interest) || 0)))}</td>
                    <td className="px-3 py-2">{row.loan_status}</td>
                  </tr>
                ))}
                {overdue.loans.length === 0 && (
                  <tr><td className="px-3 py-2 text-gray-500 dark:text-slate-400" colSpan={10}>No overdue loans with penalty interest.</td></tr>
                )}
              </tbody>
            </table>
          </TableScroll>
          <Pager hideSingle className="mt-3"
            page={overduePage.page}
            pages={overduePage.totalPages}
            total={overduePage.totalItems}
            pageSize={overduePage.pageSize}
            onPage={(p) => setPages((prev) => ({ ...prev, overdue: clamp(p, 1, overduePage.totalPages) }))}
            onPageSize={(n) => { setPageSize(n); setPages((prev) => ({ ...prev, overdue: 1 })); }}
          />
        </div>
      </div>
      )}

      {selectedReport === 'arrears_breakdown' && (
      <div className={`${cardClass('red')} p-4 pt-5 mt-4`}>
        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-1 mb-3">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-slate-100">Loans in arrears (all buckets)</h2>
          {arrearsBreakdown?.asOfDate && (
            <span className="text-sm text-gray-500 dark:text-slate-400">As of: {formatDate(arrearsBreakdown.asOfDate)}</span>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
          {['30_60', '60_90', '90_120', 'gt_120'].map((bucketKey) => {
            const label = arrearsBucketMeta?.[bucketKey]?.label || `Arrears ${bucketKey}`;
            const bucket = arrearsBreakdown?.buckets?.[bucketKey] || { totals: { count: 0, total_outstanding: 0 }, loans: [] };
            return (
              <div key={bucketKey} className="bg-red-50 border border-red-100 rounded-md p-3">
                <p className="text-xs uppercase text-red-700 line-clamp-2">{label}</p>
                <div className="mt-2 flex items-end justify-between">
                  <div>
                    <p className="text-xs text-red-700">Loans</p>
                    <p className="text-lg font-semibold text-red-800">{bucket.totals?.count || 0}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-red-700">Outstanding</p>
                    <p className="text-sm font-semibold text-red-800">{toCurrency(bucket.totals?.total_outstanding || 0)}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="grid grid-cols-1 gap-4">
          {['30_60', '60_90', '90_120', 'gt_120'].map((bucketKey) => {
            const label = arrearsBucketMeta?.[bucketKey]?.label || `Arrears ${bucketKey}`;
            const pageKey = arrearsBucketMeta?.[bucketKey]?.pageKey;
            const bucket = arrearsBreakdown?.buckets?.[bucketKey] || { totals: { count: 0, total_outstanding: 0 }, loans: [] };
            const bucketPage = arrearsBreakdownPages?.[bucketKey] || paginateArray([], 1, pageSize);

            return (
              <div key={bucketKey} className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-md p-3 border-t-4 border-red-600">
                <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-1 mb-2">
                  <h3 className="text-base font-semibold text-gray-800 dark:text-slate-100">{label}</h3>
                  <span className="text-sm text-gray-500 dark:text-slate-400">Total Outstanding: {toCurrency(bucket.totals?.total_outstanding || 0)}</span>
                </div>

                <div className="w-full">
                  <TableScroll>
                    <table className="reports-table text-sm w-full">
                    <thead>
                      <tr className="bg-gray-50 dark:bg-slate-700 text-gray-700 dark:text-slate-200">
                        <th className="text-left px-3 py-2">Loan Code</th>
                        <th className="text-left px-3 py-2">Client</th>
                        <th className="text-left px-3 py-2">Due Date</th>
                        <th className="text-left px-3 py-2">Outstanding</th>
                        <th className="text-left px-3 py-2">Status</th>
                        <th className="text-left px-3 py-2">Created</th>
                        <th className="text-left px-3 py-2">Days Overdue</th>
                        <th className="text-left px-3 py-2">Total Due</th>
                        <th className="text-left px-3 py-2">Paid</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bucketPage.items.map((row) => (
                        <tr key={row.loan_id} className="border-b last:border-0">
                          <td className="px-3 py-2">{loanRef(row) || row.loan_id}</td>
                          <td className="px-3 py-2">{row.client_name}</td>
                          <td className="px-3 py-2">{row.due_date ? formatDate(row.due_date) : '-'}</td>
                          <td className="px-3 py-2">{toCurrency(row.outstanding)}</td>
                          <td className="px-3 py-2">{row.loan_status}</td>
                          <td className="px-3 py-2">{row.created_at ? formatDate(row.created_at) : '-'}</td>
                          <td className="px-3 py-2">{row.days_overdue ?? '-'}</td>
                          <td className="px-3 py-2">{toCurrency(row.total_due)}</td>
                          <td className="px-3 py-2">{toCurrency(row.total_paid)}</td>
                        </tr>
                      ))}
                      {(bucket.loans || []).length === 0 && (
                        <tr><td className="px-3 py-2 text-gray-500 dark:text-slate-400" colSpan={9}>No loans found in this arrears bucket.</td></tr>
                      )}
                    </tbody>
                    </table>
                  </TableScroll>

                  <Pager hideSingle className="mt-3"
                    page={bucketPage.page}
                    pages={bucketPage.totalPages}
                    total={bucketPage.totalItems}
                    pageSize={bucketPage.pageSize}
                    onPage={(p) => setPages((prev) => ({
                      ...prev,
                      [pageKey]: clamp(p, 1, bucketPage.totalPages),
                    }))}
                    onPageSize={(n) => {
                      setPageSize(n);
                      setPages((prev) => ({ ...prev, [pageKey]: 1 }));
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
      )}

      {selectedReport === 'defaultInterest' && (
      <div className={`${cardClass('slate')} p-4 pt-5 mt-4`}>
        <div className="flex justify-between items-center mb-2">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-slate-100">Default Interest</h2>
          <span className="text-sm text-gray-500 dark:text-slate-400">Total Default Interest: {toCurrency(defaultInterest.totals.total_default_interest)}</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
          <div className="bg-slate-50 border border-slate-100 rounded-md p-3">
            <p className="text-xs uppercase text-slate-600">Items</p>
            <p className="text-lg font-semibold text-slate-900">{defaultInterest.totals.count}</p>
          </div>
          <div className="bg-slate-50 border border-slate-100 rounded-md p-3">
            <p className="text-xs uppercase text-slate-600">Default Interest</p>
            <p className="text-lg font-semibold text-slate-900">{toCurrency(defaultInterest.totals.total_default_interest)}</p>
          </div>
          <div className="bg-slate-50 border border-slate-100 rounded-md p-3">
            <p className="text-xs uppercase text-slate-600">Total Amount Due</p>
            <p className="text-lg font-semibold text-slate-900">{toCurrency(defaultInterest.totals.total_amount_due)}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4">
          <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-md p-3 border-t-4 border-slate-600">
            <TableScroll>
              <table className="reports-table text-sm w-full">
              <thead>
                <tr className="bg-gray-50 dark:bg-slate-700 text-gray-700 dark:text-slate-200">
                  <th className="text-left px-3 py-2">Month</th>
                  <th className="text-left px-3 py-2">Count</th>
                  <th className="text-left px-3 py-2">Total Default Interest</th>
                </tr>
              </thead>
              <tbody>
                {defaultInterestByMonthPage.items.map(row => (
                  <tr key={row.month} className="border-b last:border-0">
                    <td className="px-3 py-2">{row.month}</td>
                    <td className="px-3 py-2">{row.count}</td>
                    <td className="px-3 py-2">{toCurrency(row.total_default_interest)}</td>
                  </tr>
                ))}
                {defaultInterest.byMonth.length === 0 && (
                  <tr><td className="px-3 py-2 text-gray-500 dark:text-slate-400" colSpan={3}>No data</td></tr>
                )}
              </tbody>
              </table>
            </TableScroll>
            <Pager hideSingle className="mt-3"
              page={defaultInterestByMonthPage.page}
              pages={defaultInterestByMonthPage.totalPages}
              total={defaultInterestByMonthPage.totalItems}
              pageSize={defaultInterestByMonthPage.pageSize}
              onPage={(p) => setPages((prev) => ({ ...prev, defaultInterestByMonth: clamp(p, 1, defaultInterestByMonthPage.totalPages) }))}
              onPageSize={(n) => { setPageSize(n); setPages((prev) => ({ ...prev, defaultInterestByMonth: 1 })); }}
            />
          </div>

          <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-md p-3 border-t-4 border-slate-700">
            <TableScroll>
              <table className="reports-table text-sm w-full">
              <thead>
                <tr className="bg-gray-50 dark:bg-slate-700 text-gray-700 dark:text-slate-200">
                  <th className="text-left px-3 py-2">Loan Code</th>
                  <th className="text-left px-3 py-2">Client</th>
                  <th className="text-left px-3 py-2">Due Date</th>
                  <th className="text-left px-3 py-2">Default Interest</th>
                  <th className="text-left px-3 py-2">Total Due</th>
                  <th className="text-left px-3 py-2">Created</th>
                  <th className="text-left px-3 py-2">Days Overdue</th>
                  <th className="text-left px-3 py-2">Amount Due</th>
                  <th className="text-left px-3 py-2">Loan Status</th>
                </tr>
              </thead>
              <tbody>
                {defaultInterestItemsPage.items.map((row, idx) => (
                  <tr key={`${row.loan_id}-${row.due_date}-${idx}`} className="border-b last:border-0">
                    <td className="px-3 py-2">{loanRef(row) || row.loan_id}</td>
                    <td className="px-3 py-2">{row.client_name}</td>
                    <td className="px-3 py-2">{row.due_date ? formatDate(row.due_date) : '-'}</td>
                    <td className="px-3 py-2">{toCurrency(row.default_interest)}</td>
                    <td className="px-3 py-2">{toCurrency(row.total_due)}</td>
                    <td className="px-3 py-2">{row.created_at ? formatDate(row.created_at) : '-'}</td>
                    <td className="px-3 py-2">{row.days_overdue}</td>
                    <td className="px-3 py-2">{toCurrency(row.amount_due)}</td>
                    <td className="px-3 py-2">{row.loan_status}</td>
                  </tr>
                ))}
                {defaultInterest.items.length === 0 && (
                  <tr><td className="px-3 py-2 text-gray-500 dark:text-slate-400" colSpan={10}>No default interest found.</td></tr>
                )}
              </tbody>
              </table>
            </TableScroll>
            <Pager hideSingle className="mt-3"
              page={defaultInterestItemsPage.page}
              pages={defaultInterestItemsPage.totalPages}
              total={defaultInterestItemsPage.totalItems}
              pageSize={defaultInterestItemsPage.pageSize}
              onPage={(p) => setPages((prev) => ({ ...prev, defaultInterestItems: clamp(p, 1, defaultInterestItemsPage.totalPages) }))}
              onPageSize={(n) => { setPageSize(n); setPages((prev) => ({ ...prev, defaultInterestItems: 1 })); }}
            />
          </div>
        </div>
      </div>
      )}

      {selectedReport === 'unpaid' && (
      <div className={`${cardClass('orange')} p-4 pt-5 mt-4`}>
        <div className="flex justify-between items-center mb-2">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-slate-100">Unpaid Loans</h2>
          <span className="text-sm text-gray-500 dark:text-slate-400">Total Outstanding: {toCurrency(unpaid.totals.total_outstanding)}</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
          <div className="bg-orange-50 border border-orange-100 rounded-md p-3">
            <p className="text-xs uppercase text-orange-700">Loans</p>
            <p className="text-lg font-semibold text-orange-800">{unpaid.totals.count}</p>
          </div>
          <div className="bg-orange-50 border border-orange-100 rounded-md p-3">
            <p className="text-xs uppercase text-orange-700">Total Outstanding</p>
            <p className="text-lg font-semibold text-orange-800">{toCurrency(unpaid.totals.total_outstanding)}</p>
          </div>
        </div>
        <div className="w-full">
          <TableScroll>
            <table className="reports-table text-sm w-full">
            <thead>
              <tr className="bg-gray-50 dark:bg-slate-700 text-gray-700 dark:text-slate-200">
                <th className="text-left px-3 py-2">Loan Code</th>
                <th className="text-left px-3 py-2">Client</th>
                <th className="text-left px-3 py-2">Due Date</th>
                <th className="text-left px-3 py-2">Outstanding</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-left px-3 py-2">Created</th>
                <th className="text-left px-3 py-2">Days Overdue</th>
                <th className="text-left px-3 py-2">Total Due</th>
                <th className="text-left px-3 py-2">Paid</th>
              </tr>
            </thead>
            <tbody>
              {unpaidPage.items.map((row) => (
                <tr key={row.loan_id} className="border-b last:border-0">
                  <td className="px-3 py-2">{loanRef(row) || row.loan_id}</td>
                  <td className="px-3 py-2">{row.client_name}</td>
                  <td className="px-3 py-2">{row.due_date ? formatDate(row.due_date) : '-'}</td>
                  <td className="px-3 py-2">{toCurrency(row.outstanding)}</td>
                  <td className="px-3 py-2">{row.loan_status}</td>
                  <td className="px-3 py-2">{row.created_at ? formatDate(row.created_at) : '-'}</td>
                  <td className="px-3 py-2">{row.days_overdue ?? '-'}</td>
                  <td className="px-3 py-2">{toCurrency(row.total_due)}</td>
                  <td className="px-3 py-2">{toCurrency(row.total_paid)}</td>
                </tr>
              ))}
              {unpaid.loans.length === 0 && (
                <tr><td className="px-3 py-2 text-gray-500 dark:text-slate-400" colSpan={9}>No unpaid loans found.</td></tr>
              )}
            </tbody>
            </table>
          </TableScroll>
          <Pager hideSingle className="mt-3"
            page={unpaidPage.page}
            pages={unpaidPage.totalPages}
            total={unpaidPage.totalItems}
            pageSize={unpaidPage.pageSize}
            onPage={(p) => setPages((prev) => ({ ...prev, unpaid: clamp(p, 1, unpaidPage.totalPages) }))}
            onPageSize={(n) => { setPageSize(n); setPages((prev) => ({ ...prev, unpaid: 1 })); }}
          />
        </div>
      </div>
      )}

      {selectedReport === 'repayment_methods' && (
      <div className={`${cardClass('teal')} p-4 pt-5 mt-4`}>
        <div className="flex justify-between items-center mb-2">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-slate-100">Repayment Methods</h2>
          <span className="text-sm text-gray-500 dark:text-slate-400">
            Total Paid: {toCurrency(
              (Number(repaymentMethods?.debit?.totals?.total_paid) || 0)
              + (Number(repaymentMethods?.cash?.totals?.total_paid) || 0)
              + (Number(repaymentMethods?.other?.totals?.total_paid) || 0)
            )}
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
          <div className="bg-gray-50 border border-gray-100 rounded-md p-3">
            <p className="text-xs uppercase text-gray-500 dark:text-slate-400">Debit Order</p>
            <p className="text-sm text-gray-700 dark:text-slate-300 mt-1">Payments: <span className="font-semibold">{repaymentMethods.debit.totals.count}</span></p>
            <p className="text-sm text-gray-700 dark:text-slate-300">Total Paid: <span className="font-semibold">{toCurrency(repaymentMethods.debit.totals.total_paid)}</span></p>
          </div>
          <div className="bg-gray-50 border border-gray-100 rounded-md p-3">
            <p className="text-xs uppercase text-gray-500 dark:text-slate-400">Cash</p>
            <p className="text-sm text-gray-700 dark:text-slate-300 mt-1">Payments: <span className="font-semibold">{repaymentMethods.cash.totals.count}</span></p>
            <p className="text-sm text-gray-700 dark:text-slate-300">Total Paid: <span className="font-semibold">{toCurrency(repaymentMethods.cash.totals.total_paid)}</span></p>
          </div>
          <div className="bg-gray-50 border border-gray-100 rounded-md p-3">
            <p className="text-xs uppercase text-gray-500 dark:text-slate-400">Other</p>
            <p className="text-sm text-gray-700 dark:text-slate-300 mt-1">Payments: <span className="font-semibold">{repaymentMethods.other.totals.count}</span></p>
            <p className="text-sm text-gray-700 dark:text-slate-300">Total Paid: <span className="font-semibold">{toCurrency(repaymentMethods.other.totals.total_paid)}</span></p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4">
          {[
            { key: 'debit', title: 'Debit Order', page: repaymentsDebitItemsPage, pageKey: 'repaymentsDebitItems' },
            { key: 'cash', title: 'Cash', page: repaymentsCashItemsPage, pageKey: 'repaymentsCashItems' },
            { key: 'other', title: 'Other', page: repaymentsOtherItemsPage, pageKey: 'repaymentsOtherItems' },
          ].map(({ key, title, page, pageKey }) => (
            <div key={key} className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-md p-3 border-t-4 border-teal-700">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-2">
                <h3 className="text-md font-semibold text-gray-800 dark:text-slate-100">{title} (repayments)</h3>
                <div className="text-xs text-gray-600 dark:text-slate-400">Showing {page.totalItems} payments</div>
              </div>

              <div className="w-full">
                <TableScroll>
                  <table className="reports-table text-sm w-full">
                <thead>
                  <tr className="bg-gray-50 dark:bg-slate-700 text-gray-700 dark:text-slate-200">
                    <th className="text-left px-3 py-2">Loan Code</th>
                    <th className="text-left px-3 py-2">Client</th>
                    <th className="text-left px-3 py-2">Payment Date</th>
                    <th className="text-left px-3 py-2">Amount</th>
                    <th className="text-left px-3 py-2">Method</th>
                    <th className="text-left px-3 py-2">Repayment ID</th>
                    <th className="text-left px-3 py-2">Loan Created</th>
                    <th className="text-left px-3 py-2">Due Date</th>
                    <th className="text-left px-3 py-2">Days Overdue</th>
                    <th className="text-left px-3 py-2">Ref</th>
                  </tr>
                </thead>
                <tbody>
                  {page.items.map((row) => (
                    <tr key={`${key}-${row.repayment_id}`} className="border-b last:border-0">
                      <td className="px-3 py-2"><LoanLink loanId={row.loan_id}>{loanRef(row) || row.loan_id}</LoanLink></td>
                      <td className="px-3 py-2">{row.client_name}</td>
                      <td className="px-3 py-2">{row.payment_date ? formatDate(row.payment_date) : '-'}</td>
                      <td className="px-3 py-2"><RepaymentLink repaymentId={row.repayment_id}>{toCurrency(row.amount_paid)}</RepaymentLink></td>
                      <td className="px-3 py-2">{row.payment_method}</td>
                      <td className="px-3 py-2"><RepaymentLink repaymentId={row.repayment_id}>{row.repayment_id}</RepaymentLink></td>
                      <td className="px-3 py-2">{row.created_at ? formatDate(row.created_at) : '-'}</td>
                      <td className="px-3 py-2">{row.due_date ? formatDate(row.due_date) : '-'}</td>
                      <td className="px-3 py-2">{row.days_overdue ?? '-'}</td>
                      <td className="px-3 py-2">{row.payment_reference || '-'}</td>
                    </tr>
                  ))}
                  {page.totalItems === 0 && (
                    <tr><td className="px-3 py-2 text-gray-500 dark:text-slate-400" colSpan={10}>No repayments found.</td></tr>
                  )}
                </tbody>
                </table>
                </TableScroll>
              </div>

              <Pager hideSingle className="mt-3"
                page={page.page}
                pages={page.totalPages}
                total={page.totalItems}
                pageSize={page.pageSize}
                onPage={(p) => setPages((prev) => ({ ...prev, [pageKey]: clamp(p, 1, page.totalPages) }))}
                onPageSize={(n) => { setPageSize(n); setPages((prev) => ({ ...prev, [pageKey]: 1 })); }}
              />
            </div>
          ))}
        </div>
      </div>
      )}

      {selectedReport === 'provision_bad_debts_movement' && (
      <div className={`${cardClass('blue')} p-4 pt-5 mt-4`}>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-2">
          <div>
            <h2 className="text-lg font-semibold text-gray-800 dark:text-slate-100">Movement in Provision for Bad Debts</h2>
            <p className="text-sm text-gray-500 dark:text-slate-400">
              Period: {dmy(provisionMovement.filters?.startDate, '—')} to {dmy(provisionMovement.filters?.endDate, '—')}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600 dark:text-slate-400">Provision %</label>
            <input
              type="number"
              min="0"
              step="0.1"
              value={provisionRatePct}
              onChange={(e) => setProvisionRatePct(e.target.value)}
              className="w-24 p-2"
            />
            <button
              onClick={() => fetchProvisionBadDebtsMovement(query)}
              disabled={loading}
              className="px-3 py-2 bg-blue-50 text-blue-700 rounded-md hover:bg-blue-100 text-sm disabled:opacity-60 dark:bg-slate-700 dark:text-blue-300 dark:hover:bg-slate-600"
            >
              Recalculate
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
          <div className="bg-blue-50 border border-blue-100 rounded-md p-3">
            <p className="text-xs uppercase text-blue-700 font-semibold">Provision (beginning of period)</p>
            <p className="text-lg font-semibold text-blue-800">{toCurrency(provisionMovement.movement.opening_provision)}</p>
          </div>
          <div className="bg-blue-50 border border-blue-100 rounded-md p-3">
            <p className="text-xs uppercase text-blue-700 font-semibold">Loans written-off (during period)</p>
            <p className="text-lg font-semibold text-blue-800">{toCurrency(provisionMovement.movement.loans_written_off)}</p>
            <p className="text-xs text-blue-700">Count: {provisionMovement.movement.written_off_count}</p>
          </div>
          <div className="bg-blue-50 border border-blue-100 rounded-md p-3">
            <p className="text-xs uppercase text-blue-700 font-semibold">Provision (during period)</p>
            <p className="text-lg font-semibold text-blue-800">{toCurrency(provisionMovement.movement.provision_during_period)}</p>
          </div>
        </div>

        <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-md p-3 border-t-4 border-blue-700">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-md font-semibold text-gray-800 dark:text-slate-100">Written-off loans during period</h3>
          </div>

          <div className="w-full">
            <TableScroll>
              <table className="reports-table text-sm w-full">
            <thead>
              <tr className="bg-gray-50 dark:bg-slate-700 text-gray-700 dark:text-slate-200">
                <th className="text-left px-3 py-2">Loan Code</th>
                <th className="text-left px-3 py-2">Client</th>
                <th className="text-left px-3 py-2">Write-off Date</th>
                <th className="text-left px-3 py-2">Principal Written-off</th>
                <th className="text-left px-3 py-2">Due Date</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-left px-3 py-2">Created</th>
              </tr>
            </thead>
            <tbody>
              {provisionMovementWrittenOffPage.items.map((row) => (
                <tr key={`${row.loan_id}-${row.writeoff_date || ''}`} className="border-b last:border-0">
                  <td className="px-3 py-2">{loanRef(row) || row.loan_id}</td>
                  <td className="px-3 py-2">{row.client_name}</td>
                  <td className="px-3 py-2">{row.writeoff_date ? formatDate(row.writeoff_date) : '-'}</td>
                  <td className="px-3 py-2">{toCurrency(row.principal_amount)}</td>
                  <td className="px-3 py-2">{row.due_date ? formatDate(row.due_date) : '-'}</td>
                  <td className="px-3 py-2">{row.loan_status}</td>
                  <td className="px-3 py-2">{row.created_at ? formatDate(row.created_at) : '-'}</td>
                </tr>
              ))}
              {(provisionMovement.writtenOffLoans || []).length === 0 && (
                <tr><td className="px-3 py-2 text-gray-500 dark:text-slate-400" colSpan={7}>No written-off loans found in the period.</td></tr>
              )}
            </tbody>
            </table>
            </TableScroll>
          </div>

          <Pager hideSingle className="mt-3"
            page={provisionMovementWrittenOffPage.page}
            pages={provisionMovementWrittenOffPage.totalPages}
            total={provisionMovementWrittenOffPage.totalItems}
            pageSize={provisionMovementWrittenOffPage.pageSize}
            onPage={(p) => setPages((prev) => ({ ...prev, provisionMovementWrittenOff: clamp(p, 1, provisionMovementWrittenOffPage.totalPages) }))}
            onPageSize={(n) => { setPageSize(n); setPages((prev) => ({ ...prev, provisionMovementWrittenOff: 1 })); }}
          />
        </div>
      </div>
      )}

      {selectedReport === 'interest_outstanding_quarter' && (
      <div className={`${cardClass('emerald')} p-4 pt-5 mt-4`}>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-2">
          <div>
            <h2 className="text-lg font-semibold text-gray-800 dark:text-slate-100">Interest Charged on loans outstanding (end of quarter)</h2>
            <p className="text-sm text-gray-500 dark:text-slate-400">End Date: {dmy(interestQuarter.filters?.endDate, '—')}</p>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600 dark:text-slate-400">Year</label>
            <select
              value={quarterYear}
              onChange={(e) => setQuarterYear(e.target.value)}
              className="p-2"
            >
              <option value="">Select Year</option>
              {years.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            <label className="text-sm text-gray-600 dark:text-slate-400">Quarter</label>
            <select
              value={quarter}
              onChange={(e) => setQuarter(e.target.value)}
              className="p-2"
            >
              <option value="">Select Quarter</option>
              {[1, 2, 3, 4].map(q => <option key={q} value={q}>Q{q}</option>)}
            </select>
            <button
              onClick={fetchInterestOutstandingQuarter}
              disabled={loading}
              className="px-3 py-2 bg-blue-50 text-blue-700 rounded-md hover:bg-blue-100 text-sm disabled:opacity-60 dark:bg-slate-700 dark:text-blue-300 dark:hover:bg-slate-600"
            >
              Refresh
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
          <div className="bg-gray-50 border border-gray-100 rounded-md p-3">
            <p className="text-xs uppercase text-gray-500 dark:text-slate-400">Loans</p>
            <p className="text-lg font-semibold text-gray-800 dark:text-slate-100">{interestQuarter.totals.count}</p>
          </div>
          <div className="bg-gray-50 border border-gray-100 rounded-md p-3">
            <p className="text-xs uppercase text-gray-500 dark:text-slate-400">Outstanding (End)</p>
            <p className="text-lg font-semibold text-gray-800 dark:text-slate-100">{toCurrency(interestQuarter.totals.total_outstanding_at_end)}</p>
          </div>
          <div className="bg-gray-50 border border-gray-100 rounded-md p-3">
            <p className="text-xs uppercase text-gray-500 dark:text-slate-400">Interest Charged</p>
            <p className="text-lg font-semibold text-gray-800 dark:text-slate-100">{toCurrency(interestQuarter.totals.total_interest_charged)}</p>
          </div>
        </div>

        <div className="w-full">
          <TableScroll>
            <table className="reports-table text-sm w-full">
            <thead>
              <tr className="bg-gray-50 dark:bg-slate-700 text-gray-700 dark:text-slate-200">
                <th className="text-left px-3 py-2">Loan Code</th>
                <th className="text-left px-3 py-2">Client</th>
                <th className="text-left px-3 py-2">Outstanding (End)</th>
                <th className="text-left px-3 py-2">Interest Charged</th>
                <th className="text-left px-3 py-2">Due Date</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-left px-3 py-2">Created</th>
                <th className="text-left px-3 py-2">Days Overdue</th>
              </tr>
            </thead>
            <tbody>
              {interestQuarterPage.items.map(row => (
                <tr key={row.loan_id} className="border-b last:border-0">
                  <td className="px-3 py-2">{loanRef(row) || row.loan_id}</td>
                  <td className="px-3 py-2">{row.client_name}</td>
                  <td className="px-3 py-2">{toCurrency(row.outstanding_at_end)}</td>
                  <td className="px-3 py-2">{toCurrency(row.interest_charged)}</td>
                  <td className="px-3 py-2">{row.due_date ? formatDate(row.due_date) : '-'}</td>
                  <td className="px-3 py-2">{row.loan_status}</td>
                  <td className="px-3 py-2">{row.created_at ? formatDate(row.created_at) : '-'}</td>
                  <td className="px-3 py-2">{row.days_overdue ?? '-'}</td>
                </tr>
              ))}
              {interestQuarter.loans.length === 0 && (
                <tr><td className="px-3 py-2 text-gray-500 dark:text-slate-400" colSpan={8}>No outstanding loans found at quarter end.</td></tr>
              )}
            </tbody>
            </table>
          </TableScroll>
          <Pager hideSingle className="mt-3"
            page={interestQuarterPage.page}
            pages={interestQuarterPage.totalPages}
            total={interestQuarterPage.totalItems}
            pageSize={interestQuarterPage.pageSize}
            onPage={(p) => setPages((prev) => ({ ...prev, interestQuarter: clamp(p, 1, interestQuarterPage.totalPages) }))}
            onPageSize={(n) => { setPageSize(n); setPages((prev) => ({ ...prev, interestQuarter: 1 })); }}
          />
        </div>
      </div>
      )}

      {selectedReport === 'interest' && (
      <div className={`${cardClass('indigo')} p-4 pt-5 mt-4`}>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-2">
          <div>
            <h2 className="text-lg font-semibold text-gray-800 dark:text-slate-100">Interest on Loans and Advances - Micro lenders</h2>
            <p className="text-sm text-gray-500 dark:text-slate-400">Total Interest: {toCurrency(interest.totals.total_interest)}</p>
            <p className="text-sm text-gray-500 dark:text-slate-400">Period: {dmy(interest.filters?.startDate, '—')} to {dmy(interest.filters?.endDate, '—')}</p>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600 dark:text-slate-400">Year</label>
            <select
              value={quarterYear}
              onChange={(e) => setQuarterYear(e.target.value)}
              className="p-2"
            >
              <option value="">Select Year</option>
              {years.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            <label className="text-sm text-gray-600 dark:text-slate-400">Quarter</label>
            <select
              value={quarter}
              onChange={(e) => setQuarter(e.target.value)}
              className="p-2"
            >
              <option value="">Select Quarter</option>
              {[1, 2, 3, 4].map(q => <option key={q} value={q}>Q{q}</option>)}
            </select>
            <button
              onClick={fetchInterestQuarterly}
              disabled={loading}
              className="px-3 py-2 bg-blue-50 text-blue-700 rounded-md hover:bg-blue-100 text-sm disabled:opacity-60 dark:bg-slate-700 dark:text-blue-300 dark:hover:bg-slate-600"
            >
              Refresh
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3">
          <div className="bg-gray-50 rounded-md p-3">
            <p className="text-xs uppercase text-gray-500 dark:text-slate-400">Loans</p>
            <p className="text-lg font-semibold text-gray-800 dark:text-slate-100">{interest.totals.count}</p>
          </div>
          <div className="bg-gray-50 rounded-md p-3">
            <p className="text-xs uppercase text-gray-500 dark:text-slate-400">Total Principal</p>
            <p className="text-lg font-semibold text-gray-800 dark:text-slate-100">{toCurrency(interest.totals.total_principal)}</p>
          </div>
          <div className="bg-gray-50 rounded-md p-3">
            <p className="text-xs uppercase text-gray-500 dark:text-slate-400">Total Interest</p>
            <p className="text-lg font-semibold text-gray-800 dark:text-slate-100">{toCurrency(interest.totals.total_interest)}</p>
          </div>
          <div className="bg-gray-50 rounded-md p-3">
            <p className="text-xs uppercase text-gray-500 dark:text-slate-400">Total Repayable</p>
            <p className="text-lg font-semibold text-gray-800 dark:text-slate-100">{toCurrency(interest.totals.total_repayable)}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4">
          <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-md p-3 border-t-4 border-indigo-600">
            <TableScroll>
              <table className="reports-table text-sm w-full">
              <thead>
                <tr className="bg-gray-50 dark:bg-slate-700 text-gray-700 dark:text-slate-200">
                  <th className="text-left px-3 py-2">Month</th>
                  <th className="text-left px-3 py-2">Count</th>
                  <th className="text-left px-3 py-2">Total Interest</th>
                </tr>
              </thead>
              <tbody>
                {interestByMonthPage.items.map(row => (
                  <tr key={row.month} className="border-b last:border-0">
                    <td className="px-3 py-2">{row.month}</td>
                    <td className="px-3 py-2">{row.count}</td>
                    <td className="px-3 py-2">{toCurrency(row.total_interest)}</td>
                  </tr>
                ))}
                {interest.byMonth.length === 0 && (
                  <tr><td className="px-3 py-2 text-gray-500 dark:text-slate-400" colSpan={3}>No data</td></tr>
                )}
              </tbody>
              </table>
            </TableScroll>
            <Pager hideSingle className="mt-3"
              page={interestByMonthPage.page}
              pages={interestByMonthPage.totalPages}
              total={interestByMonthPage.totalItems}
              pageSize={interestByMonthPage.pageSize}
              onPage={(p) => setPages((prev) => ({ ...prev, interestByMonth: clamp(p, 1, interestByMonthPage.totalPages) }))}
              onPageSize={(n) => { setPageSize(n); setPages((prev) => ({ ...prev, interestByMonth: 1 })); }}
            />
          </div>

          <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-md p-3 border-t-4 border-indigo-700">
            <TableScroll>
              <table className="reports-table text-sm w-full">
              <thead>
                <tr className="bg-gray-50 dark:bg-slate-700 text-gray-700 dark:text-slate-200">
                  <th className="text-left px-3 py-2">Loan Code</th>
                  <th className="text-left px-3 py-2">Client</th>
                  <th className="text-left px-3 py-2">Principal</th>
                  <th className="text-left px-3 py-2">Interest</th>
                  <th className="text-left px-3 py-2">Total Repayable</th>
                  <th className="text-left px-3 py-2">Interest Rate</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-left px-3 py-2">Created</th>
                  <th className="text-left px-3 py-2">Due Date</th>
                  <th className="text-left px-3 py-2">Days Overdue</th>
                </tr>
              </thead>
              <tbody>
                {interestLoansPage.items.map(row => (
                  <tr key={row.loan_id} className="border-b last:border-0">
                    <td className="px-3 py-2">{loanRef(row) || row.loan_id}</td>
                    <td className="px-3 py-2">{row.client_name}</td>
                    <td className="px-3 py-2">{toCurrency(row.principal_amount)}</td>
                    <td className="px-3 py-2">{toCurrency(row.interest_amount)}</td>
                    <td className="px-3 py-2">{toCurrency(row.total_repayable)}</td>
                    <td className="px-3 py-2">{row.interest_rate}%</td>
                    <td className="px-3 py-2">{row.loan_status}</td>
                    <td className="px-3 py-2">{row.created_at ? formatDate(row.created_at) : '-'}</td>
                    <td className="px-3 py-2">{row.due_date ? formatDate(row.due_date) : '-'}</td>
                    <td className="px-3 py-2">{row.days_overdue ?? '-'}</td>
                  </tr>
                ))}
                {interest.loans.length === 0 && (
                  <tr><td className="px-3 py-2 text-gray-500 dark:text-slate-400" colSpan={10}>No interest data found.</td></tr>
                )}
              </tbody>
              </table>
            </TableScroll>
            <Pager hideSingle className="mt-3"
              page={interestLoansPage.page}
              pages={interestLoansPage.totalPages}
              total={interestLoansPage.totalItems}
              pageSize={interestLoansPage.pageSize}
              onPage={(p) => setPages((prev) => ({ ...prev, interestLoans: clamp(p, 1, interestLoansPage.totalPages) }))}
              onPageSize={(n) => { setPageSize(n); setPages((prev) => ({ ...prev, interestLoans: 1 })); }}
            />
          </div>
        </div>
      </div>
      )}
    </div>
  );
};

export default MicrolenderMonthlyManagement;
